import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import ProposalHeader from './ProposalHeader';
import Alert from '../../../../components/Alert/Alert';
import Loading from '../../../../components/Loading/Loading';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import api from '../../../../api';
import { createProposal, updateProposal, getActiveCurrencyRate } from '../../api/ProposalApi';
import { AppSelect, FormFieldSelect } from '../../../../components/common/select';
import CurrencyInput from 'react-currency-input-field';
import Modal from '../../../../components/common/Modal'


// PDF-related imports removed - functionality moved to DetailProposal
import { ChevronDown } from 'lucide-react';
import { resolveChildEaCodeSectorIds } from './eaCodeInheritance';
import {
  resolveHoAddressPrefill,
  shouldSyncHoAddressToCompany,
  buildCompanyAddressUpdatePayload,
  resolvePrimarySiteName,
} from './auditMethodologySiteUtils';
import { resolveConvertedUnitPrice } from './proposalCurrencyUtils';

const EditProposal = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const readOnly = location.pathname.startsWith('/proposals/view/');

  // State
  const [loading, setLoading] = useState(true);
  const [productTypes, setProductTypes] = useState([]);
  const [leadProducts, setLeadProducts] = useState([]);
  const [proposalData, setProposalData] = useState({
    leadId: id, // tambahkan ini, gunakan id dari useParams
    proposalDate: new Date().toISOString().split('T')[0],
    validUntil: '',
    sendToClient: false,
    services: '',
    companyName: '',
    proposalCode: '',
    serviceTemplateId: '',
    proposalTemplateId: '',
    serviceType: '',
    scope: '', // ✅ SCOPE: Scope is stored in lead_services, not in proposal. This is just for UI state. Backend reads from lead_services when generating templates.
    accreditationIds: [], // ✅ ACCREDITATION: Array of accreditation IDs
    bankId: '', // ✅ BANK: Bank ID
    projectName: '', // ✅ PROJECT NAME: Project name from lead
    signatureName: '', // ✅ SIGNATURE NAME: Signature name field
    signaturePosition: '', // ✅ SIGNATURE POSITION: Signature position field
    trainingDeliveryMethod: '', // ✅ TRAINING DELIVERY: Training delivery method field
    // ✅ ISCC-SPECIFIC FIELDS
    turnover: '',
    registrationNumber: '', // ✅ SUSTAINABILITY: Registration number
    facilitiesAndAddress: '',
    materialInputIds: [],
    materialOutputIds: [],
    ghgCalculation: '',

    // ✅ CARBON PROJECT & GHG FIELDS
    // Shared fields
    objective: '',
    sector: '',
    materiality: '',
    numberOfSites: '',
    numberOfFacilities: '',
    siteAddress: '',
    facilityAddress: '',

    // Audit Methodology (MoM 03 June 2026)
    auditMethodology: '',
    multiSite: false,
    // auditFlaggingType is derived from auditMethodology on backend
    integratedStandardIds: [],
    combinedStandardIds: [],
    auditConfigurationCompleted: false,

    // Carbon Project specific
    methodology: '',
    typeOfEmissionsRemovals: '',
    ghgMonitoringPeriod: '',
    ghgEmissionReductions: '',

    // GHG specific
    organizationActivities: '',
    reportingBoundaries: '',
    claim: '',
    levelOfAssurance: '',
    verificationLocation: '',
  });
  
  // Add state for proposal templates based on selected service
  const [proposalTemplateOptions, setProposalTemplateOptions] = useState([]);

  // ✅ TEMPLATES: fetch proposal templates for a given serviceId
  const fetchProposalTemplatesByService = async (serviceId) => {
    try {
      if (!serviceId) {
        setProposalTemplateOptions([]);
        return;
      }

      const response = await api.get(`/crm/proposal-templates/service/${serviceId}`);

      const templates = (response.data || []).map(template => ({
        id: template.id,
        name: template.templateName,
        serviceId: template.serviceId,
        serviceName: template.serviceName,
        templateContent: template.templateContent,
        documentFileName: template.documentFileName,
        documentFilePath: template.documentFilePath,
        documentFileSize: template.documentFileSize,
        documentMimeType: template.documentMimeType,
        isActive: template.isActive,
      }));

      setProposalTemplateOptions(templates);
    } catch (error) {
      console.error('Error fetching proposal templates:', error);
      setProposalTemplateOptions([]);
    }
  };

  const [alert, setAlert] = useState(null);
  const [proposalServices, setProposalServices] = useState([]);
  // Remove global options state
  const [standardOptionsBySection, setStandardOptionsBySection] = useState({});
  const [categoryOptionsBySection, setCategoryOptionsBySection] = useState({});
  const [itemOptionsBySection, setItemOptionsBySection] = useState({});
  const [versionOptionsByStandard, setVersionOptionsByStandard] = useState({}); // ✅ TAMBAHAN: Store version options by standard ID
  const [eaCodeOptions, setEaCodeOptions] = useState([]); // ✅ EA CODE: sector options carrying an EA Code, scoped per standard
  
  // TAMBAHAN: Flag untuk mencegah multiple fetch
  const [dataFetched, setDataFetched] = useState(false);
  // ✅ Tracks standard IDs whose versions have been requested (success or failure) to avoid re-fetch loops
  const requestedVersionStandardIdsRef = useRef(new Set());

  const [unitOptions, setUnitOptions] = useState([]); // Ganti dari array statis ke state
  const [investmentBreakdown, setInvestmentBreakdown] = useState(null);
  const [proposalCurrencyCode, setProposalCurrencyCode] = useState('IDR');
  const [proposalRateDate, setProposalRateDate] = useState(null); // YYYY-MM-DD dari created_at proposal
  const [currencies, setCurrencies] = useState([]);
  const [currencyRates, setCurrencyRates] = useState([]);
  const [hasExistingProposal, setHasExistingProposal] = useState(false);
  const currencyChangeRequestRef = useRef(new Map());
  
  const [leadData, setLeadData] = useState(null); // Default currency
  const [accreditationOptions, setAccreditationOptions] = useState([]); // ✅ TAMBAHAN: Accreditation options
  const [bankOptions, setBankOptions] = useState([]); // ✅ BANK: Bank options
  const [isccScopeOptions, setIsccScopeOptions] = useState([]); // ✅ ISCC SCOPE: ISCC scope options
  const [savingSites, setSavingSites] = useState(false);
  const [auditSites, setAuditSites] = useState([]);
  const [companyDetail, setCompanyDetail] = useState(null); // ✅ SITES: company address for single-site prefill
  const [companyContacts, setCompanyContacts] = useState([]); // full contacts list — required so a HO Address save never wipes them
  const [hoAddressSaveState, setHoAddressSaveState] = useState('idle'); // idle | saving | saved | error

  // ✅ ISCC SCOPE: Whether the selected proposal template is ISCC/Sustainability.
  // This is the single source of truth for ISCC-mode — must be name-based, never
  // inferred from the shape of stored data (a non-ISCC proposal can still have
  // array-shaped legacy scope data, which must NOT force ISCC mode).
  const isTemplateIsccOrSustainability = useMemo(() => {
    const proposalTemplateId = proposalData.proposalTemplateId;
    const proposalTemplate = proposalTemplateOptions.find(opt => String(opt.id) === String(proposalTemplateId));
    const templateName = (proposalTemplate?.name || '').toLowerCase();
    if (templateName) {
      return templateName.includes('iscc') || templateName.includes('sustainability');
    }
    // Template options haven't loaded yet / no template selected — fall back to
    // inferring from data shape only in this narrow case.
    const scopeValue = proposalData.scope;
    return !proposalTemplateId && (
      (typeof scopeValue === 'string' && scopeValue.startsWith('[') && scopeValue.endsWith(']')) ||
      Array.isArray(scopeValue)
    );
  }, [proposalData.proposalTemplateId, proposalData.scope, proposalTemplateOptions]);

  // ✅ ISCC SCOPE: Convert scope value to display format for UI
  const displayScope = useMemo(() => {
    const scopeValue = proposalData.scope;
    if (!scopeValue) return [];

    if (isTemplateIsccOrSustainability) {
      // For ISCC/Sustainability templates, ensure scope is an array
      if (Array.isArray(scopeValue)) {
        return scopeValue;
      } else if (typeof scopeValue === 'string' && scopeValue.startsWith('[') && scopeValue.endsWith(']')) {
        try {
          const parsed = JSON.parse(scopeValue);
          return Array.isArray(parsed) ? parsed : [scopeValue];
        } catch (e) {
          return [scopeValue];
        }
      } else if (typeof scopeValue === 'string' && scopeValue.includes(',')) {
        return scopeValue.split(',').map(s => s.trim()).filter(s => s);
      } else {
        return [scopeValue];
      }
    } else {
      // For non-ISCC/Sustainability templates, scope should be a string
      return scopeValue;
    }
  }, [proposalData.scope, isTemplateIsccOrSustainability]);

  // ✅ ISCC SCOPE: Fetch ISCC scope options
  const fetchIsccScopeOptions = async () => {
    try {

      // Try to get ALL ISCC scope data first (non-paginated)
      let response;
      try {
        response = await api.get('/md/iscc-scope/all');
      } catch (allEndpointError) {

        // If /all endpoint fails, try the main paginated endpoint with large page size
        try {
          response = await api.get('/md/iscc-scope?page=0&size=1000&search=');
        } catch (endpointError) {

          // Try alternative endpoints
          const alternativeEndpoints = [
            '/api/md/iscc-scope/all',
            '/crm/iscc-scope/all',
            '/master/iscc-scope/all',
            '/md/iscc-scope/all',
            '/api/md/iscc-scope?page=0&size=1000&search=',
            '/crm/iscc-scope?page=0&size=1000&search=',
            '/master/iscc-scope?page=0&size=1000&search=',
            '/md/scope'
          ];

          for (const endpoint of alternativeEndpoints) {
            try {
              response = await api.get(endpoint);
              break;
            } catch (altError) {
              // Continue to next endpoint
            }
          }

          if (!response) {
            throw new Error('All endpoints failed');
          }
        }
      }

      // Handle different response structures
      let scopeData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          scopeData = response.data;
        } else if (response.data.content && Array.isArray(response.data.content)) {
          scopeData = response.data.content;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          scopeData = response.data.data;
        } else if (response.data.items && Array.isArray(response.data.items)) {
          scopeData = response.data.items;
        } else if (response.data.records && Array.isArray(response.data.records)) {
          scopeData = response.data.records;
        } else {
          scopeData = [];
        }
      }

      if (scopeData.length > 0) {
        const formattedOptions = scopeData.map(scope => ({
          value: scope.id || scope.value || scope.code,
          label: scope.code && scope.name ? `${scope.code} - ${scope.name}` : (scope.label || scope.name || 'Unknown Scope')
        }));
        setIsccScopeOptions(formattedOptions);
      } else {
        setIsccScopeOptions([
          { value: 'fallback-1', label: 'ISCC Scope 1 - Fallback Option' },
          { value: 'fallback-2', label: 'ISCC Scope 2 - Fallback Option' },
          { value: 'fallback-3', label: 'ISCC Scope 3 - Fallback Option' }
        ]);
      }
    } catch (error) {
      setIsccScopeOptions([
        { value: 'fallback-1', label: 'ISCC Scope 1 - Fallback Option' },
        { value: 'fallback-2', label: 'ISCC Scope 2 - Fallback Option' },
        { value: 'fallback-3', label: 'ISCC Scope 3 - Fallback Option' }
      ]);
    }
  };

  // ✅ ISCC SCOPE: Check if selected service is ISCC or Sustainability
  const isIsccOrSustainabilityService = (serviceId) => {
    if (!serviceId) return false;
    // Resolve the id to its service name before checking (serviceId is a numeric id, not a name)
    const service = serviceOptions.find(opt => String(opt.id) === String(serviceId));
    const name = (service?.name || '').toLowerCase();
    return name.includes('iscc') || name.includes('sustainability');
  };

  // ✅ MULTI-CURRENCY: Helper function untuk mendapatkan currency symbol
  const getCurrencySymbol = (currencyCode) => {
    const currency = currencies.find(
      item => String(item.currencyCode || '').toUpperCase() === String(currencyCode || '').toUpperCase()
    );
    return currency?.symbol || currencyCode || '';
  };

  // ✅ CURRENCY: options are loaded from active master-currency data
  const currencyOptions = currencies.map(currency => ({
    value: currency.currencyCode,
    label: currency.currencyCode,
    symbol: currency.symbol,
  }));
  const getCommercialTermAmountPrefix = (currencyCode) => {
    const symbol = getCurrencySymbol(currencyCode);
    return symbol ? `${symbol} ` : '';
  };

  // ✅ Parse saved CURRENCY point text to get currency code and raw amount (digits)
  const parseCommercialTermCurrencyFromText = (savedPointText) => {
    if (!savedPointText || typeof savedPointText !== 'string') return { currencyCode: 'IDR', amountDigits: null };
    const m = savedPointText.match(/net amount\s+(Rp\.?|\$|€|¥|S\$)?\s*([\d,.]+)\s+to/i);
    let currencyCode = 'IDR';
    let amountDigits = null;
    if (m) {
      const sym = (m[1] || '').trim();
      if (/Rp\.?/i.test(sym)) currencyCode = 'IDR';
      else if (sym === '$') currencyCode = 'USD';
      else if (sym === '€') currencyCode = 'EUR';
      else if (sym === '¥') currencyCode = 'JPY';
      else if (sym === 'S$') currencyCode = 'SGD';
      amountDigits = (m[2] || '').replace(/[^\d]/g, '');
    }
    return { currencyCode, amountDigits };
  };

  // ✅ MULTI-CURRENCY: Helper function untuk mendapatkan currency prefix
  const getCurrencyPrefix = (currencyCode) => {
    const prefix = getCurrencySymbol(currencyCode) + ' ';
    return prefix;
  };

  const normalizeOptionName = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

  // Activity Administration is a recurring activity; Activity is planned for
  // one selected year. Return null for all other product categories.
  const getForcedProductCategoryYearType = (sectionIdx, standardIdx, productCategoryIdx, productCategoryId, serviceItemId) => {
    const categoryOption = (categoryOptionsBySection[sectionIdx] || [])
      .find(option => String(option.id) === String(productCategoryId));
    const itemOptionsKey = productCategoryIdx === null
      ? `${sectionIdx}_${standardIdx}`
      : `sub_${sectionIdx}_${standardIdx}_${productCategoryIdx}`;
    const itemOption = (itemOptionsBySection[itemOptionsKey] || [])
      .find(option => String(option.id) === String(serviceItemId));
    const categoryName = normalizeOptionName(categoryOption?.name || categoryOption?.label);
    const itemName = normalizeOptionName(itemOption?.name);
    const activityType = normalizeOptionName(itemOption?.activityType || itemOption?.activity_type);

    if (
      categoryName.includes('activity administration') ||
      categoryName.includes('administration') ||
      itemName.includes('administration') ||
      activityType === 'administration'
    ) return 0;
    if (categoryName === 'activity' || itemName === 'activity' || activityType === 'activity') return 1;
    return null;
  };

  // Resolve exchange rate from cached list.
  // 1. Try exact date match (startDate <= proposalRateDate <= endDate)
  // 2. Fallback: latest rate by startDate — mirrors backend fallback logic
  const resolveExchangeRate = (code) => {
    if (!code || code.toUpperCase() === 'IDR') return 1;
    const ref = new Date(proposalRateDate || new Date());
    ref.setHours(0, 0, 0, 0);
    const list = Array.isArray(currencyRates) ? currencyRates : [];
    const forCode = list.filter(r =>
      (r.currency_code || r.currencyCode || '').toUpperCase() === code.toUpperCase() &&
      (r.startdate || r.start_date || r.startDate)
    );

    // 1. Exact date match
    const exact = forCode.find(r => {
      const start = new Date(r.startdate || r.start_date || r.startDate);
      start.setHours(0, 0, 0, 0);
      const endRaw = r.enddate || r.end_date || r.endDate;
      const end = endRaw ? new Date(endRaw) : null;
      if (end) end.setHours(23, 59, 59, 999);
      return ref >= start && (!end || ref <= end);
    });
    if (exact) return Number(exact.exchange_rate || exact.exchangeRate || 1);

    // 2. Fallback: most recent rate (largest startDate)
    if (forCode.length > 0) {
      const latest = forCode.reduce((best, r) => {
        const rDate = new Date(r.startdate || r.start_date || r.startDate);
        const bDate = new Date(best.startdate || best.start_date || best.startDate);
        return rDate > bDate ? r : best;
      });
      return Number(latest.exchange_rate || latest.exchangeRate || 1);
    }

    return 1;
  };

  // Fetch exchange rate from API then store it into the row state (functional update avoids stale closure)
  const fetchAndStoreExchangeRate = async (sectionIdx, standardIdx, currencyCode, isSubRow = false, productCategoryIdx = null) => {
    if (!currencyCode || currencyCode === 'IDR') return;
    const rate = await getActiveCurrencyRate(currencyCode, proposalRateDate);
    setProposalServices(prev => prev.map((section, sIdx) => {
      if (sIdx !== sectionIdx) return section;
      return {
        ...section,
        standards: (section.standards || []).map((std, stdIdx) => {
          if (stdIdx !== standardIdx) return std;
          if (isSubRow && productCategoryIdx !== null) {
            return {
              ...std,
              productCategories: (std.productCategories || []).map((pc, pcIdx) => {
                if (pcIdx !== productCategoryIdx) return pc;
                return { ...pc, exchangeRate: rate };
              })
            };
          }
          return { ...std, exchangeRate: rate };
        })
      };
    }));
  };

  const getRowExchangeRate = async (currencyCode) => {
    if (!currencyCode || currencyCode.toUpperCase() === 'IDR') return 1;
    const activeRate = await getActiveCurrencyRate(currencyCode, proposalRateDate);
    return activeRate !== 1 ? activeRate : resolveExchangeRate(currencyCode);
  };

  // Convert a row's current unit price through IDR using the rate for the
  // proposal creation date (or today for a new proposal).
  const handleRowCurrencyChange = async (sectionIdx, standardIdx, newCurrencyCode, isSubRow = false, productCategoryIdx = null) => {
    const section = proposalServices[sectionIdx];
    const standard = section?.standards?.[standardIdx];
    const row = isSubRow ? standard?.productCategories?.[productCategoryIdx] : standard;
    if (!row) return;

    const rowKey = `${sectionIdx}:${standardIdx}:${isSubRow ? productCategoryIdx : 'standard'}`;
    const requestId = (currencyChangeRequestRef.current.get(rowKey) || 0) + 1;
    currencyChangeRequestRef.current.set(rowKey, requestId);

    const oldCurrencyCode = row.currencyCode || 'IDR';
    const currentUnitPrice = Number(row.unitPrice || 0);
    const oldRate = await getRowExchangeRate(oldCurrencyCode);
    const newRate = await getRowExchangeRate(newCurrencyCode);
    const convertedPrice = resolveConvertedUnitPrice({
      currentUnitPrice,
      savedBasePrice: row.unitPriceBaseCurrency,
      oldRate,
      newRate,
      newCurrencyCode,
    });

    // A later selection must win if an earlier rate request resolves afterward.
    if (currencyChangeRequestRef.current.get(rowKey) !== requestId) return;

    setProposalCurrencyCode(newCurrencyCode);
    setProposalServices(prev => prev.map((sectionValue, sIdx) => {
      if (sIdx !== sectionIdx) return sectionValue;
      return {
        ...sectionValue,
        standards: (sectionValue.standards || []).map((standardValue, stdIdx) => {
          if (stdIdx !== standardIdx) return standardValue;
          if (isSubRow && productCategoryIdx !== null) {
            return {
              ...standardValue,
              productCategories: (standardValue.productCategories || []).map((pc, pcIdx) =>
                pcIdx === productCategoryIdx
                  ? {
                      ...pc,
                      currencyCode: newCurrencyCode,
                      exchangeRate: convertedPrice.exchangeRate,
                      unitPrice: convertedPrice.unitPrice,
                      unitPriceDisplayCurrency: convertedPrice.unitPriceDisplayCurrency,
                      unitPriceBaseCurrency: convertedPrice.unitPriceBaseCurrency,
                    }
                  : pc
              )
            };
          }
          return {
            ...standardValue,
            currencyCode: newCurrencyCode,
            exchangeRate: convertedPrice.exchangeRate,
            unitPrice: convertedPrice.unitPrice,
            unitPriceDisplayCurrency: convertedPrice.unitPriceDisplayCurrency,
            unitPriceBaseCurrency: convertedPrice.unitPriceBaseCurrency,
          };
        })
      };
    }));
  };

  // ✅ MULTI-CURRENCY: Helper function untuk convert currency di frontend
  const convertCurrency = async (amount, fromCurrencyCode, toCurrencyCode) => {
    try {
      if (fromCurrencyCode === toCurrencyCode) {
        return amount;
      }

      // Ambil exchange rates dari API
      const fromCurrencyRes = await api.get(`/crm/currencies/by-code/${fromCurrencyCode}`);
      const toCurrencyRes = await api.get(`/crm/currencies/by-code/${toCurrencyCode}`);
      
      const fromRate = fromCurrencyRes.data.exchangeRate;
      const toRate = toCurrencyRes.data.exchangeRate;
      
      // Logika konversi yang sama dengan backend
      let result;
      if (toCurrencyRes.data.isBaseCurrency) {
        // Konversi ke base currency: multiply dengan exchange rate
        result = amount * fromRate;
      } else if (fromCurrencyRes.data.isBaseCurrency) {
        // Konversi dari base currency: divide dengan exchange rate
        result = amount / toRate;
      } else {
        // Konversi antar non-base currencies: convert to base first, then to target
        const amountInBase = amount * fromRate;
        result = amountInBase / toRate;
      }
      
      // Round ke 2 decimal places
      const finalResult = Math.round(result * 100) / 100;
      
      return finalResult;
    } catch (error) {
      console.error('Error converting currency:', error);
      return amount; // Fallback ke nilai asli jika error
    }
  };

  const [maxPriceByRow, setMaxPriceByRow] = useState({});

  // ✅ AUDIT SITES: Helper functions for multi-site configuration (MoM 03 June 2026)
  const getAuditStandardOptions = useCallback(() => {
    return proposalServices.flatMap((section, sectionIndex) =>
      (section.standards || [])
        .filter(standard => standard?.standard || standard?.standardName)
        .map((standard, standardIndex) => {
          const sectionStdOptions = standardOptionsBySection[sectionIndex] || [];
          const foundOption = sectionStdOptions.find(opt => String(opt.id) === String(standard.standard));
          const name = standard.standardName || foundOption?.name || foundOption?.label || `Standard ${standard.standard || standardIndex + 1}`;
          return {
            id: Number(standard.id) || Number(standard.standard) || `${sectionIndex}-${standardIndex}`,
            label: name,
            serviceName: section.serviceName || 'Service',
          };
        })
    );
  }, [proposalServices, standardOptionsBySection]);

  const updateAuditStandardSelection = (targetField, standardId, checked) => {
    setProposalData(prev => {
      const current = Array.isArray(prev[targetField]) ? prev[targetField] : [];
      const normalizedId = Number(standardId);
      const next = checked
        ? Array.from(new Set([...current, normalizedId]))
        : current.filter(selectedId => Number(selectedId) !== normalizedId);
      return { ...prev, [targetField]: next };
    });
  };
  // Handler for multi-site toggle with numberOfSites derivation
  const handleMultiSiteToggle = (checked) => {
    setProposalData(prev => {
      if (checked) {
        // If turning on multi-site and numberOfSites is empty or less than 2, set to 2
        const currentSites = parseInt(prev.numberOfSites) || 0;
        return { ...prev, multiSite: true, numberOfSites: currentSites < 2 ? '2' : prev.numberOfSites };
      } else {
        // If turning off multi-site, set numberOfSites to 1
        return { ...prev, multiSite: false, numberOfSites: '1' };
      }
    });
  };


  const loadProposalSites = async (proposalId) => {
    if (!proposalId) return;
    try {
      const response = await api.get(`/crm/proposals/${proposalId}/sites`);
      const list = Array.isArray(response.data) ? response.data : [];
      // Only adopt persisted sites when present; otherwise leave the single-site
      // prefill effect (company address) to populate the first card.
      if (list.length > 0) {
        setAuditSites(list);
      }
    } catch (err) {
      console.warn('Failed to load proposal sites', err);
    }
  };

  const addAuditSite = () => {
    setAuditSites(prev => ([
      ...prev,
      {
        siteName: '',
        siteCode: '',
        address: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        auditMandays: 0,
        isPrimary: prev.length === 0,
        notes: '',
      },
    ]));
  };

  const updateAuditSite = (index, field, value) => {
    setAuditSites(prev => prev.map((site, siteIndex) => (
      siteIndex === index ? { ...site, [field]: value } : site
    )));
  };

  const removeAuditSite = (index) => {
    setAuditSites(prev => {
      const next = prev.filter((_, siteIndex) => siteIndex !== index);
      // Never leave zero sites, and always keep one primary
      if (next.length === 0) return prev.length ? [{ ...prev[0], isPrimary: true }] : prev;
      if (!next.some(s => s.isPrimary)) next[0] = { ...next[0], isPrimary: true };
      return next;
    });
  };

  // ✅ SITES: build a blank site row (Audit Platform consumes this shape). Site
  // Location never auto-fills — this is the record seeded to Audit Platform for
  // scheduling and certificate issuance/draft, so the user names/addresses it
  // deliberately rather than inheriting the company's registered address.
  const buildEmptySite = (primary = false) => ({
    siteName: resolvePrimarySiteName(), siteCode: '', address: '', city: '', state: '', country: '', postalCode: '',
    contactName: '', contactEmail: '', contactPhone: '', auditMandays: 0, isPrimary: primary, notes: '',
  });

  // ✅ SITES: mark exactly one site as primary
  const setPrimarySite = (index) => {
    setAuditSites(prev => prev.map((site, i) => ({ ...site, isPrimary: i === index })));
  };

  // ✅ SITES: handle the Single Site / Multi-sites dropdown
  const handleSitesModeChange = (mode) => {
    if (mode === 'multi') {
      setProposalData(prev => ({
        ...prev,
        multiSite: true,
        numberOfSites: (parseInt(prev.numberOfSites) || 0) < 2 ? '2' : prev.numberOfSites,
      }));
      setAuditSites(prev => (prev.length >= 1
        ? prev
        : [buildEmptySite(true)]));
    } else {
      setProposalData(prev => ({ ...prev, multiSite: false, numberOfSites: '1' }));
      // Collapse to a single primary site (keep the first one if present)
      setAuditSites(prev => {
        if (prev.length >= 1) return [{ ...prev[0], isPrimary: true }];
        return [buildEmptySite(true)];
      });
    }
  };

  const saveProposalSites = async (proposalId) => {
    if (!proposalId) return;
    const normalizedSites = auditSites
      .map((site, index) => ({
        ...site,
        siteName: String(site.siteName || '').trim(),
        sequence: index,
        auditMandays: Number(site.auditMandays) || 0,
        isPrimary: Boolean(site.isPrimary),
      }))
      .filter(site => site.siteName);
    try {
      setSavingSites(true);
      const response = await api.put(`/crm/proposals/${proposalId}/sites`, normalizedSites);
      setAuditSites(Array.isArray(response.data) ? response.data : normalizedSites);
    } finally {
      setSavingSites(false);
    }
  };

  // ✅ HO ADDRESS: prefill the proposal's HO Address from the client Company's own
  // address, but only while the field is still blank — never clobbers a value the
  // user already typed/saved on this proposal.
  useEffect(() => {
    if (!companyDetail) return;
    setProposalData(prev => {
      const next = resolveHoAddressPrefill(prev.siteAddress, companyDetail.address);
      return next === prev.siteAddress ? prev : { ...prev, siteAddress: next };
    });
    // eslint-disable-next-line
  }, [companyDetail]);

  // ✅ HO ADDRESS: on blur, if the user actually changed the value, write it back
  // to the Company record. PUT /md/companies/{id} is a full-overwrite endpoint —
  // buildCompanyAddressUpdatePayload echoes back every existing company field and
  // the full contacts list so this partial-field edit never wipes unrelated data.
  const syncHoAddressToCompany = async (nextAddress) => {
    const companyId = leadData?.companyId;
    if (!shouldSyncHoAddressToCompany(companyId, companyDetail?.address, nextAddress)) return;
    try {
      setHoAddressSaveState('saving');
      const payload = buildCompanyAddressUpdatePayload(companyDetail, companyContacts, nextAddress);
      await api.put(`/md/companies/${companyId}`, payload);
      setCompanyDetail(prev => (prev ? { ...prev, address: nextAddress } : prev));
      setHoAddressSaveState('saved');
    } catch (err) {
      console.error('Failed to sync HO Address to Company', err);
      setHoAddressSaveState('error');
    }
  };

  // ✅ SITES: load the company/client address once the lead's company is known,
  // so the Single Site card (and now HO Address) can be auto-populated from CRM
  // data. GET /md/companies/{id} responds { company: {...}, contacts: [...] } —
  // the company object is nested, not the top-level response body.
  useEffect(() => {
    const companyId = leadData?.companyId;
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/md/companies/${companyId}`);
        if (cancelled) return;
        setCompanyDetail(res.data?.company || null);
        setCompanyContacts(Array.isArray(res.data?.contacts) ? res.data.contacts : []);
        if (!hasExistingProposal && res.data?.company?.defaultCurrencyId) {
          try {
            const currencyRes = await api.get(`/crm/currencies/${res.data.company.defaultCurrencyId}`);
            if (!cancelled) {
              setProposalCurrencyCode(currencyRes.data?.currencyCode || 'IDR');
            }
          } catch (currencyError) {
            console.warn('Could not load company default currency for new proposal:', currencyError);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setCompanyDetail(null);
          setCompanyContacts([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [leadData?.companyId, hasExistingProposal]);

  // ✅ EA CODE: fetch sector list (EA Code source), scoped to per-standard multi-select
  useEffect(() => {
    const fetchEaCodeOptions = async () => {
      try {
        const res = await api.get('/dashboard/sectors');
        if (res.data && res.data.sectors) {
          setEaCodeOptions(
            res.data.sectors
              .filter(sector => sector.eaCode)
              .map(sector => ({ id: sector.id, name: `${sector.eaCode} - ${sector.name}` }))
          );
        }
      } catch (err) {
        console.error('Error fetching EA code options:', err);
        setEaCodeOptions([]);
      }
    };
    fetchEaCodeOptions();
  }, []);

  // ✅ SITES: always keep at least one site card visible (blank — Site Location
  // never auto-fills from the company, see buildEmptySite above).
  useEffect(() => {
    setAuditSites(prev => (prev.length === 0 ? [buildEmptySite(true)] : prev));
    // eslint-disable-next-line
  }, [proposalData.multiSite]);
  
  // Add state for calculation loading
  const [calculating, setCalculating] = useState(false);

  // Add state untuk modal workflow
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [workflowItems, setWorkflowItems] = useState([]);
  const [loadingWorkflow, setLoadingWorkflow] = useState(false);
  // isProcessingCreatePdf state removed - functionality moved to DetailProposal

  // Add state for proposal status
  const [proposalStatus, setProposalStatus] = useState(null);
  const [formatNumber, setFormatNumber] = useState(null);

  // Add state for confirmation modals
  const [showSendToClientModal, setShowSendToClientModal] = useState(false);
  const [showDownloadErrorModal, setShowDownloadErrorModal] = useState(false);
  const [downloadErrorMessage, setDownloadErrorMessage] = useState('');
  const [showRateUndefinedModal, setShowRateUndefinedModal] = useState(false);
  
  // Commercial terms state
  const [commercialTerms, setCommercialTerms] = useState([]);
  const [loadingCommercialTerms, setLoadingCommercialTerms] = useState(false);
  const [predefinedTermsSets, setPredefinedTermsSets] = useState([]);
  const [filteredPredefinedTermsSets, setFilteredPredefinedTermsSets] = useState([]);
  const [showPredefinedModal, setShowPredefinedModal] = useState(false);
  const [selectedPredefinedSet, setSelectedPredefinedSet] = useState(null); // ✅ Changed to single selection
  // ✅ COMMERCIAL TERMS PRESET: Track which preset is currently used
  const [commercialTermsPresetId, setCommercialTermsPresetId] = useState(null);
  // ✅ POINT OPTIONS: Refs to track enrichment and prevent infinite loops
  const hasEnrichedTermsRef = useRef(false);
  const previousPresetIdRef = useRef(null);
  // ✅ COLLAPSIBLE: Track if commercial terms section is expanded
  const [isCommercialTermsExpanded, setIsCommercialTermsExpanded] = useState(true);



  // Tambahkan useEffect untuk fetch unit options
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const res = await api.get('/crm/type-units');
        setUnitOptions(
          (res.data || []).map(unit => ({
            id: unit.id,
            name: unit.name
          }))
        );
      } catch (err) {
        setUnitOptions([]);
      }
    };
    fetchUnits();
  }, []);

  useEffect(() => {
    api.get('/crm/currencies')
      .then(res => {
        const list = Array.isArray(res.data)
          ? res.data
          : (res.data?.data || res.data?.content || []);
        setCurrencies(list.filter(currency => currency?.currencyCode));
      })
      .catch(() => setCurrencies([]));

    api.get('/crm/currency-rates').then(res => {
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.content || []);
      setCurrencyRates(list);
    }).catch(() => setCurrencyRates([]));
  }, []);

  // When proposal data finishes loading, pre-fetch exchange rates for rows that already have a non-IDR currency
  useEffect(() => {
    if (!dataFetched || proposalServices.length === 0) return;
    proposalServices.forEach((section, sIdx) => {
      (section.standards || []).forEach((std, stdIdx) => {
        const code = std.currencyCode || 'IDR';
        if (code !== 'IDR' && !std.exchangeRate) {
          fetchAndStoreExchangeRate(sIdx, stdIdx, code);
        }
        (std.productCategories || []).forEach((pc, pcIdx) => {
          const pcCode = pc.currencyCode || 'IDR';
          if (pcCode !== 'IDR' && !pc.exchangeRate) {
            fetchAndStoreExchangeRate(sIdx, stdIdx, pcCode, true, pcIdx);
          }
        });
      });
    });
  }, [dataFetched]); // intentionally only on dataFetched — runs once when data loads

  // Fetch commercial terms templates from Master Data (new source; predefined file kept as backend fallback)
  useEffect(() => {
    const fetchCommercialTermTemplates = async () => {
      try {
        const res = await api.get('/md/commercial-terms/templates', { params: { page: 0, size: 100 } });
        const content = res.data?.content ?? [];
        setPredefinedTermsSets(Array.isArray(content) ? content : []);
      } catch (err) {
        console.error('Error fetching commercial terms templates (Master Data):', err);
        setPredefinedTermsSets([]);
      }
    };
    fetchCommercialTermTemplates();
  }, []);

  // Filter commercial terms templates by proposal service(s) and optionally by proposal template
  // Show only templates that: have no serviceId (generic) OR have serviceId matching one of the proposal's services
  useEffect(() => {
    if (!predefinedTermsSets || predefinedTermsSets.length === 0) {
      setFilteredPredefinedTermsSets([]);
      return;
    }
    const proposalServiceIds = (proposalServices || [])
      .map((s) => s.service)
      .filter((sid) => sid != null && sid !== '')
      .map((sid) => Number(sid));

    let filtered = predefinedTermsSets;
    if (proposalServiceIds.length > 0) {
      filtered = predefinedTermsSets.filter(
        (t) =>
          t.serviceId == null ||
          t.serviceId === '' ||
          proposalServiceIds.includes(Number(t.serviceId))
      );
      if (filtered.length === 0) filtered = predefinedTermsSets;
    }

    const proposalTemplateId = proposalData.proposalTemplateId;
    if (proposalTemplateId != null && proposalTemplateId !== '') {
      const byTemplate = filtered.filter(
        (t) => t.proposalTemplateId != null && Number(t.proposalTemplateId) === Number(proposalTemplateId)
      );
      if (byTemplate.length > 0) filtered = byTemplate;
    }
    setFilteredPredefinedTermsSets(filtered);
  }, [predefinedTermsSets, proposalData.proposalTemplateId, proposalServices]);

  // ✅ POINT OPTIONS: Ensure options are populated when preset ID exists
  useEffect(() => {
    // Reset enrichment flag if preset ID changed
    if (previousPresetIdRef.current !== commercialTermsPresetId) {
      hasEnrichedTermsRef.current = false;
      previousPresetIdRef.current = commercialTermsPresetId;
    }

    const enrichCommercialTermsWithOptions = async () => {
      if (!commercialTermsPresetId || !commercialTerms || commercialTerms.length === 0) {
        return;
      }

      // Check if any pointsWithOptions is missing options
      const needsEnrichment = commercialTerms.some(term => 
        term.isSection && 
        term.pointsWithOptions && 
        term.pointsWithOptions.some(point => !point.options || point.options.length === 0)
      );

      // Skip if already enriched or doesn't need enrichment
      if (hasEnrichedTermsRef.current || !needsEnrichment) {
        return;
      }

      if (needsEnrichment) {
        try {
          const predefinedRes = await api.get(`/crm/proposals/commercial-terms/predefined/${commercialTermsPresetId}`);
          if (predefinedRes.data && predefinedRes.data.terms) {
            const enrichedTerms = commercialTerms.map(term => {
              if (term.isSection && term.pointsWithOptions) {
                const predefinedSection = predefinedRes.data.terms.find(
                  pt => pt.isSection && pt.sectionOrder === term.sectionOrder
                );
                if (predefinedSection && predefinedSection.pointsWithOptions) {
                  const enrichedPoints = term.pointsWithOptions.map((point, pIdx) => {
                    const predefinedPoint = predefinedSection.pointsWithOptions[pIdx];
                    if (predefinedPoint && predefinedPoint.options && (!point.options || point.options.length === 0)) {
                      return {
                        ...point,
                        options: predefinedPoint.options,
                        pointNumber: predefinedPoint.pointNumber || point.pointNumber
                      };
                    }
                    return point;
                  });
                  // Only update if there are actual changes
                  const hasChanges = enrichedPoints.some((ep, idx) => 
                    ep.options && ep.options.length > 0 && (!term.pointsWithOptions[idx].options || term.pointsWithOptions[idx].options.length === 0)
                  );
                  if (hasChanges) {
                    return {
                      ...term,
                      pointsWithOptions: enrichedPoints
                    };
                  }
                }
              }
              return term;
            });
            
            // Only update state if there are actual changes
            const hasAnyChanges = enrichedTerms.some((et, idx) => 
              JSON.stringify(et.pointsWithOptions) !== JSON.stringify(commercialTerms[idx].pointsWithOptions)
            );
            if (hasAnyChanges) {
              setCommercialTerms(enrichedTerms);
              hasEnrichedTermsRef.current = true; // Mark as enriched
            }
          }
        } catch (error) {
          console.warn('Could not enrich commercial terms with options:', error);
        }
      }
    };

    enrichCommercialTermsWithOptions();
  }, [commercialTermsPresetId, commercialTerms]); // Depend on both to ensure options are populated


  const handleAddService = () => {
    setProposalServices((prev) => [
      ...prev,
      {
        id: Date.now(),
        service: '',
        standards: [
          {
            standard: '',
            standardVersionId: '',
            productCategory: '',
            serviceItem: '',
            yearType: 0,
            selectedYear: new Date().getFullYear(),
            qty: 1,
            unit: '',
            participants: null,
            unitPrice: '',
            currencyCode: 'IDR',
            eaCodeSectorIds: [],
          },
        ],
      },
    ]);
  };

  const handleDeleteSection = (sectionIdx) => {
    setProposalServices(prev => {
      const updated = [...prev];
      updated.splice(sectionIdx, 1);
      return updated;
    });
  };

  // Example options (replace with real data as needed)
  const serviceOptions = productTypes.length > 0
    ? productTypes.map(pt => ({ id: pt.id, name: pt.label || pt.name }))
    : [];

  // ✅ Helper function to check if a service is a training service
  const isTrainingService = (serviceId) => {
    if (!serviceId) return false;
    const service = serviceOptions.find(opt => String(opt.id) === String(serviceId));
    if (!service) return false;
    // Check by name (case-insensitive) - must be exactly "training" or start with "training " (as a whole word)
    // This ensures only the Training service shows the participants column, not certification or other services
    const serviceName = (service.name || '').toLowerCase().trim();
    // Only match if service name is exactly "training" or starts with "training " (with space, indicating it's the first word)
    // This prevents matching services like "certification training program" or other services that might contain "training"
    return serviceName === 'training' || serviceName.startsWith('training ');
  };

  // ✅ EA CODE: only relevant for the Certification service (scoped per standard, not per proposal)
  const isCertificationServiceSection = (serviceId) => {
    if (!serviceId) return false;
    const service = serviceOptions.find(opt => String(opt.id) === String(serviceId));
    const name = (service?.name || '').toLowerCase();
    return name.includes('certification');
  };

  // ✅ AUDIT METHODOLOGY: only relevant for "Certification Program" or "Sustainability" services.
  // The configuration card is hidden unless at least one selected service matches.
  const shouldShowAuditMethodology = useMemo(() => {
    return (proposalServices || []).some(section => {
      const svc = serviceOptions.find(opt => String(opt.id) === String(section.service));
      const name = (svc?.name || '').toLowerCase().trim();
      return name.includes('certification') || name.includes('sustainability');
    });
  }, [proposalServices, serviceOptions]);

  const handleDeleteStandard = (sectionIdx, standardIdx) => {
    setProposalServices(prev => {
      const updated = [...prev];
      const newStandards = updated[sectionIdx].standards.filter((_, i) => i !== standardIdx);
      if (newStandards.length === 0) {
        updated.splice(sectionIdx, 1);
      } else {
        updated[sectionIdx] = {
          ...updated[sectionIdx],
          standards: newStandards
        };
      }
      return updated;
    });
  };

  // Rename handleAddRow to handleAddStandard
  const handleAddStandard = (sectionIdx) => {
    setProposalServices(prev => {
      const updated = [...prev];
      updated[sectionIdx] = {
        ...updated[sectionIdx],
        standards: [
          ...updated[sectionIdx].standards,
          {
            standard: '',
            standardVersionId: '',
            productCategory: '',
            serviceItem: '',
            yearType: 0,
            selectedYear: new Date().getFullYear(),
            qty: 1,
            unit: '',
            participants: null,
            unitPrice: '',
            currencyCode: 'IDR',
            eaCodeSectorIds: [],
          }
        ]
      };
      return updated;
    });
  };

  // Rename handleAddSubRow to handleAddProductCategory
  const handleAddProductCategory = (sectionIdx, standardIdx) => {
    setProposalServices(prev => {
      return prev.map((section, sIdx) => {
        if (sIdx !== sectionIdx) return section;
        return {
          ...section,
          standards: section.standards.map((standard, stIdx) => {
            if (stIdx !== standardIdx) return standard;
            return {
              ...standard,
              productCategories: [
                ...(standard.productCategories || []),
                {
                  standard: standard.standard || '',
                  standardVersionId: standard.standardVersionId || '',
                  productCategory: '',
                  serviceItem: '',
                  yearType: 0,
                  selectedYear: new Date().getFullYear(),
                  qty: 1,
                  unit: '',
                  participants: null,
                  unitPrice: '',
                  currencyCode: 'IDR',
                  eaCodeSectorIds: [],
                }
              ]
            };
          })
        };
      });
    });
  };

  // Delete sub-row (child)
  const handleDeleteProductCategory = (sectionIdx, standardIdx, productCategoryIdx) => {
    setProposalServices(prev => {
      const updated = [...prev];
      const standard = updated[sectionIdx].standards[standardIdx];
      const newProductCategories = standard.productCategories.filter((_, i) => i !== productCategoryIdx);
      updated[sectionIdx].standards[standardIdx] = { ...standard, productCategories: newProductCategories };
      return updated;
    });
  };


  // Modifikasi fungsi handleSave
  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Exclude additionalServices
      const { additionalServices, ...proposalDataWithoutAdditional } = proposalData;

      // ✅ SIGNATURE NAME & POSITION: Auto-fill from assigned user if empty
      let finalSignatureName = proposalData.signatureName;
      let finalSignaturePosition = proposalData.signaturePosition;
      
      const needsSignatureName = !finalSignatureName || finalSignatureName.trim() === '';
      const needsSignaturePosition = !finalSignaturePosition || finalSignaturePosition.trim() === '';
      
      if ((needsSignatureName || needsSignaturePosition) && leadData) {
        try {
          // Get assigned user information from leadData
          if (leadData.assignedTo) {
            // If assignedTo is an object with name/position
            if (needsSignatureName && leadData.assignedTo.name) {
              finalSignatureName = leadData.assignedTo.name;
            } else if (needsSignatureName && (leadData.assignedTo.firstName || leadData.assignedTo.lastName)) {
              finalSignatureName = `${leadData.assignedTo.firstName || ''} ${leadData.assignedTo.lastName || ''}`.trim();
            } else if (needsSignatureName && leadData.assignedTo.fullName) {
              finalSignatureName = leadData.assignedTo.fullName;
            } else if (needsSignatureName && leadData.assignedTo.username) {
              finalSignatureName = leadData.assignedTo.username;
            }
            
            if (needsSignaturePosition && leadData.assignedTo.position) {
              finalSignaturePosition = leadData.assignedTo.position;
            } else if (needsSignaturePosition && leadData.assignedTo.jobTitle) {
              finalSignaturePosition = leadData.assignedTo.jobTitle;
            } else if (needsSignaturePosition && leadData.assignedTo.jobTitleName) {
              finalSignaturePosition = leadData.assignedTo.jobTitleName;
            }
          } else if (leadData.assignedUserName) {
            // Fallback: use assignedUserName if available
            if (needsSignatureName) {
              finalSignatureName = leadData.assignedUserName;
            }
          }
          
          // If still empty, try to fetch from lead API
          if ((needsSignatureName && !finalSignatureName) || (needsSignaturePosition && !finalSignaturePosition)) {
            try {
              const leadResponse = await api.get(`/crm/leads/${id}`);
              const lead = leadResponse.data;
              
              if (lead?.assignedTo) {
                if (needsSignatureName && !finalSignatureName) {
                  if (lead.assignedTo.name) {
                    finalSignatureName = lead.assignedTo.name;
                  } else if (lead.assignedTo.firstName || lead.assignedTo.lastName) {
                    finalSignatureName = `${lead.assignedTo.firstName || ''} ${lead.assignedTo.lastName || ''}`.trim();
                  } else if (lead.assignedTo.fullName) {
                    finalSignatureName = lead.assignedTo.fullName;
                  } else if (lead.assignedTo.username) {
                    finalSignatureName = lead.assignedTo.username;
                  }
                }
                
                if (needsSignaturePosition && !finalSignaturePosition) {
                  if (lead.assignedTo.position) {
                    finalSignaturePosition = lead.assignedTo.position;
                  } else if (lead.assignedTo.jobTitle) {
                    finalSignaturePosition = lead.assignedTo.jobTitle;
                  } else if (lead.assignedTo.jobTitleName) {
                    finalSignaturePosition = lead.assignedTo.jobTitleName;
                  } else if (lead.assignedTo.id) {
                    // ✅ FIX: Fetch user details to get job title if position is not in assignedTo
                    try {
                      const userResponse = await api.get(`/md/internal-users/${lead.assignedTo.id}`);
                      const userData = userResponse.data;
                      if (userData?.jobTitleId) {
                        // Fetch job title - try direct endpoint first, then fallback to list
                        try {
                          // Try direct GET endpoint first
                          try {
                            const jobTitleResponse = await api.get(`/md/job-title/${userData.jobTitleId}`);
                            if (jobTitleResponse.data?.name) {
                              finalSignaturePosition = jobTitleResponse.data.name;
                            }
                          } catch (directError) {
                            // Fallback: fetch all job titles and find the matching one
                            const allJobTitlesResponse = await api.get('/md/job-title');
                            const allJobTitles = Array.isArray(allJobTitlesResponse.data) 
                              ? allJobTitlesResponse.data 
                              : (allJobTitlesResponse.data?.content || []);
                            const matchingJobTitle = allJobTitles.find(jt => jt.id === userData.jobTitleId);
                            if (matchingJobTitle?.name) {
                              finalSignaturePosition = matchingJobTitle.name;
                            }
                          }
                        } catch (jobTitleError) {
                          console.warn('Could not fetch job title:', jobTitleError);
                        }
                      }
                    } catch (userError) {
                      console.warn('Could not fetch user details for position:', userError);
                    }
                  }
                }
              } else if (lead?.assignedUserId && (needsSignatureName || needsSignaturePosition)) {
                // ✅ FIX: If assignedTo is not available but assignedUserId is, fetch user directly
                try {
                  const userResponse = await api.get(`/md/internal-users/${lead.assignedUserId}`);
                  const userData = userResponse.data;
                  
                  if (needsSignatureName && !finalSignatureName) {
                    if (userData?.firstName || userData?.lastName) {
                      finalSignatureName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
                    } else if (userData?.name) {
                      finalSignatureName = userData.name;
                    } else if (userData?.username) {
                      finalSignatureName = userData.username;
                    }
                  }
                  
                  if (needsSignaturePosition && !finalSignaturePosition) {
                    if (userData?.jobTitleId) {
                      // Fetch job title - try direct endpoint first, then fallback to list
                      try {
                        // Try direct GET endpoint first
                        try {
                          const jobTitleResponse = await api.get(`/md/job-title/${userData.jobTitleId}`);
                          if (jobTitleResponse.data?.name) {
                            finalSignaturePosition = jobTitleResponse.data.name;
                          }
                        } catch (directError) {
                          // Fallback: fetch all job titles and find the matching one
                          const allJobTitlesResponse = await api.get('/md/job-title');
                          const allJobTitles = Array.isArray(allJobTitlesResponse.data) 
                            ? allJobTitlesResponse.data 
                            : (allJobTitlesResponse.data?.content || []);
                          const matchingJobTitle = allJobTitles.find(jt => jt.id === userData.jobTitleId);
                          if (matchingJobTitle?.name) {
                            finalSignaturePosition = matchingJobTitle.name;
                          }
                        }
                      } catch (jobTitleError) {
                        console.warn('Could not fetch job title:', jobTitleError);
                      }
                    }
                  }
                } catch (userError) {
                  console.warn('Could not fetch user details:', userError);
                }
              }
            } catch (leadError) {
              console.warn('Could not fetch assigned user for signature:', leadError);
            }
          }
        } catch (error) {
          console.warn('Could not auto-fill signature from assigned user:', error);
        }
      }

      // Hitung grand total (existing code...)
      let grandTotal = 0;
      if (investmentBreakdown && investmentBreakdown.totals) {
        grandTotal = investmentBreakdown.totals.reduce((sum, t) => sum + t, 0);
      } else {
        proposalServices.forEach(section => {
          section.standards.forEach(std => {
            if (std.unitPrice && std.qty) {
              grandTotal += Number(std.unitPrice) * Number(std.qty);
            }
            if (std.productCategories && Array.isArray(std.productCategories)) {
              std.productCategories.forEach(pc => {
                if (pc.unitPrice && pc.qty) {
                  grandTotal += Number(pc.unitPrice) * Number(pc.qty);
                }
              });
            }
          });
        });
      }


      // ✅ WORKFLOW UPDATE: Always update workflow_id_choice on save (without resetting unitPrice)
      // Update workflow IDs for all proposal services to ensure they have the latest rates
      // If unitPrice > fetchMaxPrice, use workFlowIdSystem (max price workflow), otherwise use workflow for current price
      console.log('🔄 Updating workflow IDs for all proposal services before save...');

      // Create a copy of proposalServices to update synchronously
      let updatedProposalServices = [...proposalServices];

      for (let sectionIdx = 0; sectionIdx < proposalServices.length; sectionIdx++) {
        const section = proposalServices[sectionIdx];
        for (let standardIdx = 0; standardIdx < section.standards.length; standardIdx++) {
          const standard = section.standards[standardIdx];

          // Update main standard workflow
          if (standard.serviceItem && standard.unitPrice) {
            try {
              const unitPriceNum = parseFloat(standard.unitPrice);
              const fetchMaxPriceNum = standard.fetchMaxPrice ? parseFloat(standard.fetchMaxPrice) : null;

              // If price is out of range (higher than max price), use workFlowIdSystem
              if (fetchMaxPriceNum !== null && unitPriceNum > fetchMaxPriceNum) {
                updatedProposalServices[sectionIdx].standards[standardIdx].workFlowIdChoice = standard.workFlowIdSystem;
              } else {
                // Price is within range, use workflow for current price
                // ✅ FIX: Use base currency price for workflow determination when company uses different currency
                const workflowPrice = (proposalCurrencyCode === 'IDR') ? standard.unitPrice : (standard.unitPriceBaseCurrency || standard.unitPrice);
                const workflowId = await fetchWorkflowIdByUnitPrice(sectionIdx, standardIdx, workflowPrice, false);
                if (workflowId) {
                  updatedProposalServices[sectionIdx].standards[standardIdx].workFlowIdChoice = workflowId;
                }
              }
            } catch (error) {
              console.warn(`Could not update workflow for standard at section ${sectionIdx}, standard ${standardIdx}:`, error);
            }
          }

          // Update product categories workflow
          if (standard.productCategories && Array.isArray(standard.productCategories)) {
            for (let productCategoryIdx = 0; productCategoryIdx < standard.productCategories.length; productCategoryIdx++) {
              const productCategory = standard.productCategories[productCategoryIdx];
              if (productCategory.serviceItem && productCategory.unitPrice) {
                try {
                  const unitPriceNum = parseFloat(productCategory.unitPrice);
                  const fetchMaxPriceNum = productCategory.fetchMaxPrice ? parseFloat(productCategory.fetchMaxPrice) : null;

                  // If price is out of range (higher than max price), use workFlowIdSystem
                  if (fetchMaxPriceNum !== null && unitPriceNum > fetchMaxPriceNum) {
                    updatedProposalServices[sectionIdx].standards[standardIdx].productCategories[productCategoryIdx].workFlowIdChoice = productCategory.workFlowIdSystem;
                  } else {
                    // Price is within range, use workflow for current price
                    // ✅ FIX: Use base currency price for workflow determination when company uses different currency
                    const workflowPrice = (proposalCurrencyCode === 'IDR') ? productCategory.unitPrice : (productCategory.unitPriceBaseCurrency || productCategory.unitPrice);
                    const workflowId = await fetchWorkflowIdByUnitPrice(sectionIdx, standardIdx, workflowPrice, true, productCategoryIdx);
                    if (workflowId) {
                      updatedProposalServices[sectionIdx].standards[standardIdx].productCategories[productCategoryIdx].workFlowIdChoice = workflowId;
                    }
                  }
                } catch (error) {
                  console.warn(`Could not update workflow for product category at section ${sectionIdx}, standard ${standardIdx}, product category ${productCategoryIdx}:`, error);
                }
              }
            }
          }
        }
      }

      // Update state with all workflow IDs at once
      setProposalServices(updatedProposalServices);
      console.log('✅ Workflow IDs updated for all proposal services');

      // ✅ SCOPE: Scope is stored in lead_services, not in proposal
      // Backend will automatically read from lead_services when proposal.scope is null/empty
      // So we don't need to send scope in the proposal payload

      const payload = {
        ...proposalDataWithoutAdditional,
        // ✅ REMOVED: scope is not stored in proposal - it's stored in lead_services only
        leadId: id,
        grandTotal,
        currencyCode: proposalCurrencyCode, // ✅ kirim currency code ke backend
        displayCurrencyCode: proposalCurrencyCode, // ✅ kirim currency yang digunakan untuk display
        accreditationIds: proposalData.accreditationIds || [], // ✅ ACCREDITATION: Send array
        bankId: proposalData.bankId || null, // ✅ BANK: Explicitly include bankId
        signatureName: finalSignatureName || '', // ✅ SIGNATURE NAME: Include signature name in payload
        signaturePosition: finalSignaturePosition || '', // ✅ SIGNATURE POSITION: Include signature position in payload
        trainingDeliveryMethod: proposalData.trainingDeliveryMethod && proposalData.trainingDeliveryMethod.trim() !== '' ? proposalData.trainingDeliveryMethod : null, // ✅ TRAINING DELIVERY: Include training delivery method in payload (null if empty)
        // ✅ ISCC-SPECIFIC FIELDS: Include in payload
        turnover: proposalData.turnover && proposalData.turnover.trim() !== '' ? proposalData.turnover : null,
        registrationNumber: proposalData.registrationNumber && proposalData.registrationNumber.trim() !== '' ? proposalData.registrationNumber : null,
        facilitiesAndAddress: proposalData.facilitiesAndAddress && proposalData.facilitiesAndAddress.trim() !== '' ? proposalData.facilitiesAndAddress : null,
        materialInputIds: proposalData.materialInputIds || [],
        materialOutputIds: proposalData.materialOutputIds || [],
        ghgCalculation: proposalData.ghgCalculation && proposalData.ghgCalculation.trim() !== '' ? proposalData.ghgCalculation : null,

        // ✅ CARBON PROJECT & GHG FIELDS: Include in payload
        // Shared fields
        objective: proposalData.objective && proposalData.objective.trim() !== '' ? proposalData.objective : null,
        // sector: removed - now fetched from company
        materiality: proposalData.materiality && proposalData.materiality.trim() !== '' ? proposalData.materiality : null,
        multiSite: Boolean(proposalData.multiSite),
        numberOfSites: proposalData.numberOfSites ? parseInt(proposalData.numberOfSites) : null,
        numberOfFacilities: proposalData.numberOfFacilities ? parseInt(proposalData.numberOfFacilities) : null,
        siteAddress: proposalData.siteAddress && proposalData.siteAddress.trim() !== '' ? proposalData.siteAddress : null,
        facilityAddress: proposalData.facilityAddress && proposalData.facilityAddress.trim() !== '' ? proposalData.facilityAddress : null,

        // Audit Methodology (MoM 03 June 2026)
        auditMethodology: proposalData.auditMethodology && proposalData.auditMethodology.trim() !== '' ? proposalData.auditMethodology : null,
        integratedStandardIds: proposalData.integratedStandardIds || [],
        combinedStandardIds: proposalData.combinedStandardIds || [],

        // Carbon Project specific
        methodology: proposalData.methodology && proposalData.methodology.trim() !== '' ? proposalData.methodology : null,
        typeOfEmissionsRemovals: proposalData.typeOfEmissionsRemovals && proposalData.typeOfEmissionsRemovals.trim() !== '' ? proposalData.typeOfEmissionsRemovals : null,
        ghgMonitoringPeriod: proposalData.ghgMonitoringPeriod && proposalData.ghgMonitoringPeriod.trim() !== '' ? proposalData.ghgMonitoringPeriod : null,
        ghgEmissionReductions: proposalData.ghgEmissionReductions && proposalData.ghgEmissionReductions.trim() !== '' ? proposalData.ghgEmissionReductions : null,

        // GHG specific
        organizationActivities: proposalData.organizationActivities && proposalData.organizationActivities.trim() !== '' ? proposalData.organizationActivities : null,
        reportingBoundaries: proposalData.reportingBoundaries && proposalData.reportingBoundaries.trim() !== '' ? proposalData.reportingBoundaries : null,
        claim: proposalData.claim && proposalData.claim.trim() !== '' ? proposalData.claim : null,
        levelOfAssurance: proposalData.levelOfAssurance && proposalData.levelOfAssurance.trim() !== '' ? proposalData.levelOfAssurance : null,
        verificationLocation: proposalData.verificationLocation && proposalData.verificationLocation.trim() !== '' ? proposalData.verificationLocation : null,
        // proposalCode: finalProposalCode, // ❌ HAPUS ini juga
        proposalServices: updatedProposalServices.map((section, sectionIdx) => ({
          id: section.id,
          serviceId: section.service,
            standards: section.standards
              .filter(std => std.standard || std.productCategory || std.serviceItem || std.unitPrice)
              .map((std, standardIdx) => {
                // ✅ VALIDASI PAYLOAD: Jika unitPrice > unitPriceBaseCurrency ATAU > fetchMaxPrice (display), paksa workFlowIdChoice = workFlowIdSystem
                let finalWorkFlowIdChoice = std.workFlowIdChoice;
                const hasSystem = !!std.workFlowIdSystem;
                const unitPriceNum = std.unitPrice ? parseFloat(std.unitPrice) : null;
                const baseCurrencyNum = std.unitPriceBaseCurrency ? parseFloat(std.unitPriceBaseCurrency) : null;
                const fetchMaxPriceNum = std.fetchMaxPrice ? parseFloat(std.fetchMaxPrice) : null;

                if (hasSystem && unitPriceNum !== null) {
                  if (baseCurrencyNum !== null && unitPriceNum > baseCurrencyNum) {
                    finalWorkFlowIdChoice = std.workFlowIdSystem;
                  }
                  if (fetchMaxPriceNum !== null && unitPriceNum > fetchMaxPriceNum) {
                    finalWorkFlowIdChoice = std.workFlowIdSystem;
                  }
                }

                const rowCurrency = std.currencyCode || 'IDR';
                return {
                  standardId: std.standard,
                  standardVersionId: std.standardVersionId || null,
                  productCategoryId: std.productCategory,
                  serviceItemId: std.serviceItem,
                  yearType: std.yearType || 0,
                  selectedYear: std.selectedYear || new Date().getFullYear(),
                  qty: std.qty,
                  unit: std.unit,
                  participants: std.participants || null,
                  unitPrice: std.unitPrice,
                  unitPriceBaseCurrency: (rowCurrency === 'IDR') ? std.unitPrice : std.unitPriceBaseCurrency,
                  currencyCode: rowCurrency,
                  workFlowIdSystem: std.workFlowIdSystem,
                  workFlowIdChoice: finalWorkFlowIdChoice,
                  fetchMaxPrice: std.fetchMaxPrice ?? null,
                  fetchMaxPriceBaseCurrency: std.fetchMaxPriceBaseCurrency ?? null,
                  scope: Array.isArray(std.scope) ? std.scope.join(', ') : (std.scope || null),
                  eaCodeSectorIds: std.eaCodeSectorIds || [],
                  children: std.productCategories
                    ? std.productCategories
                        .filter(pc => pc.productCategory || pc.serviceItem || pc.unitPrice)
                        .map(pc => {
                          let finalChildWorkFlowIdChoice = pc.workFlowIdChoice;
                          const childHasSystem = !!pc.workFlowIdSystem;
                          const childUnitPriceNum = pc.unitPriceDisplayCurrency || pc.unitPrice ? parseFloat(pc.unitPriceDisplayCurrency || pc.unitPrice) : null;
                          const childBaseCurrencyNum = pc.unitPriceBaseCurrency ? parseFloat(pc.unitPriceBaseCurrency) : null;
                          const childFetchMaxPriceNum = pc.fetchMaxPrice ? parseFloat(pc.fetchMaxPrice) : null;

                          if (childHasSystem && childUnitPriceNum !== null) {
                            if (childBaseCurrencyNum !== null && childUnitPriceNum > childBaseCurrencyNum) {
                              finalChildWorkFlowIdChoice = pc.workFlowIdSystem;
                            }
                            if (childFetchMaxPriceNum !== null && childUnitPriceNum > childFetchMaxPriceNum) {
                              finalChildWorkFlowIdChoice = pc.workFlowIdSystem;
                            }
                          }

                          const childRowCurrency = pc.currencyCode || 'IDR';
                          return {
                            standardId: pc.standard,
                            standardVersionId: pc.standardVersionId || std.standardVersionId || null,
                            productCategoryId: pc.productCategory,
                            serviceItemId: pc.serviceItem,
                            yearType: pc.yearType || 0,
                            selectedYear: pc.selectedYear || new Date().getFullYear(),
                            qty: pc.qty,
                            unit: pc.unit,
                            participants: pc.participants || null,
                            unitPrice: pc.unitPriceDisplayCurrency || pc.unitPrice,
                            unitPriceBaseCurrency: (childRowCurrency === 'IDR') ? (pc.unitPriceDisplayCurrency || pc.unitPrice) : pc.unitPriceBaseCurrency,
                            currencyCode: childRowCurrency,
                            workFlowIdSystem: pc.workFlowIdSystem,
                            workFlowIdChoice: finalChildWorkFlowIdChoice,
                            fetchMaxPrice: pc.fetchMaxPrice ?? null,
                            fetchMaxPriceBaseCurrency: pc.fetchMaxPriceBaseCurrency ?? null,
                            scope: Array.isArray(pc.scope) ? pc.scope.join(', ') : (pc.scope || null),
                            // The EA Code input only ever renders once per parent
                            // standard, so a child cycle row (Initial Audit,
                            // Surveillance, Recertification, ...) with no EA Code
                            // of its own inherits the parent's — see
                            // eaCodeInheritance.js for why.
                            eaCodeSectorIds: resolveChildEaCodeSectorIds(pc.eaCodeSectorIds, std.eaCodeSectorIds)
                          };
                        })
                    : []
                };
              })
        }))
      };
      logCurrentWorkflowState();

      let savedProposalId = null;
      
      // 1. Cek apakah proposal sudah ada untuk lead ini
      const existsRes = await api.get(`/crm/proposals/exists/lead/${id}`);
      const exists = existsRes.data === true;
      
      if (exists) {
        // PUT (update) - untuk update tidak perlu generate code baru
        const res = await api.get(`/crm/proposals/id-by-lead/${id}`);
        const proposalId = typeof res.data === 'object' ? res.data.id : res.data;
        await updateProposal(proposalId, payload);
        savedProposalId = proposalId;
      } else {
        // POST (create) - backend akan generate code otomatis
        const createResponse = await createProposal(payload);
        // createProposal returns res.data (already the body), handle both flat and wrapped response
        const createdData = createResponse?.data ?? createResponse;
        savedProposalId = createdData?.id;

        // ✅ Update proposal data dengan code yang di-generate backend
        if (createdData?.proposalCode) {
          setProposalData(prev => ({
            ...prev,
            proposalCode: createdData.proposalCode
          }));
        }
      }

      // ✅ SCOPE: Update scope in lead_services (this is the source of truth)
      // Backend will read from lead_services when generating proposal templates
      if (savedProposalId && proposalData.scope !== undefined) {
        try {
          // Fetch lead services directly from API to ensure we have the latest data
          const leadServicesRes = await api.get(`/lead-services/by-lead/${id}`);
          
          if (leadServicesRes.data && leadServicesRes.data.length > 0) {
            const firstLeadService = leadServicesRes.data[0];
            
            if (firstLeadService && firstLeadService.id) {
              // Extract standardIds from proposalServices (we already have this data)
              let standardIds = [];
              if (proposalServices && proposalServices.length > 0) {
                const firstProposalService = proposalServices[0];
                if (firstProposalService && firstProposalService.standards) {
                  // Extract unique standard IDs from the standards array
                  const standardIdSet = new Set();
                  firstProposalService.standards.forEach(std => {
                    if (std && std.standard) {
                      const stdId = typeof std.standard === 'string' ? parseInt(std.standard) : std.standard;
                      if (stdId && !isNaN(stdId)) {
                        standardIdSet.add(stdId);
                      }
                    }
                  });
                  standardIds = Array.from(standardIdSet);
                }
              }
              
              // If we don't have standardIds from proposalServices, try to get from lead detail
              if (standardIds.length === 0) {
                try {
                  const leadDetailRes = await api.get(`/crm/leads/${id}`);
                  if (leadDetailRes.data && leadDetailRes.data.services && leadDetailRes.data.services.length > 0) {
                    const matchingService = leadDetailRes.data.services.find(s => s.serviceId === firstLeadService.serviceId);
                    if (matchingService && matchingService.standards) {
                      standardIds = Array.isArray(matchingService.standards) 
                        ? matchingService.standards.filter(Boolean)
                        : [];
                    }
                  }
                } catch (leadDetailError) {
                  console.warn('Could not fetch lead detail for standardIds:', leadDetailError);
                }
              }
              
              const formData = new FormData();
              // Format scope based on template type
              let formattedScope = proposalData.scope || '';
              // Check if the proposal template indicates ISCC/sustainability
              const proposalTemplateId = proposalData.proposalTemplateId;
              const proposalTemplate = proposalTemplateOptions.find(opt => String(opt.id) === String(proposalTemplateId));
              const templateName = (proposalTemplate?.name || '').toLowerCase();
              const isIsccOrSustainability = templateName.includes('iscc') || templateName.includes('sustainability');

              if (isIsccOrSustainability) {
                // For ISCC/Sustainability services, ensure scope is an array
                if (Array.isArray(formattedScope)) {
                  formattedScope = formattedScope;
                } else if (formattedScope) {
                  formattedScope = [formattedScope];
                } else {
                  formattedScope = [];
                }
              } else {
                // For non-ISCC/Sustainability services, ensure scope is a string
                if (Array.isArray(formattedScope)) {
                  formattedScope = formattedScope.join(', ');
                }
              }

              const updateData = {
                leadId: id,
                serviceId: firstLeadService.serviceId,
                scope: formattedScope,
                standardIds: standardIds
              };
              formData.append('data', new Blob([JSON.stringify(updateData)], { type: 'application/json' }));
              
              console.log('🔄 Updating scope:', {
                leadServiceId: firstLeadService.id,
                serviceId: firstLeadService.serviceId,
                scope: proposalData.scope,
                standardIds: standardIds
              });
              
              await api.put(`/lead-services/${firstLeadService.id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
              console.log('✅ Scope updated in lead services:', proposalData.scope);
            } else {
              console.warn('⚠️ First lead service does not have an ID');
            }
          } else {
            console.warn('⚠️ No lead services found for lead ID:', id);
          }
        } catch (scopeError) {
          console.error('❌ Error updating scope in lead services:', scopeError);
          console.error('Error details:', scopeError.response?.data);
          console.error('Error status:', scopeError.response?.status);
          // Don't fail the whole save if scope update fails, but log the error
        }
      }
      
      // ✅ TAMBAHAN: Save commercial terms after proposal is saved (always save, even if empty to clear old terms)
      if (savedProposalId) {
        try {
          // ✅ PERBAIKAN: Ensure we have a valid array
          const termsToSave = Array.isArray(commercialTerms) ? commercialTerms : [];
          
          const saveResponse = await api.post(`/crm/proposals/${savedProposalId}/commercial-terms`, {
            proposalId: savedProposalId,
            terms: termsToSave, // Always send, even if empty array to clear old terms
            commercialTermsPresetId: commercialTermsPresetId // ✅ COMMERCIAL TERMS PRESET: Include preset ID
          });
          
          // ✅ PERBAIKAN: Immediately verify by fetching back and reload predefined structure if needed
          try {
            const verifyResponse = await api.get(`/crm/proposals/${savedProposalId}/commercial-terms`);
            const fetchedTerms = verifyResponse.data || [];
            
            // ✅ COMMERCIAL TERMS PRESET: Reload predefined structure and merge if preset ID exists
            if (commercialTermsPresetId) {
              try {
                const predefinedRes = await api.get(`/crm/proposals/commercial-terms/predefined/${commercialTermsPresetId}`);
                
                if (predefinedRes.data && predefinedRes.data.terms) {
                  // ✅ Use predefined structure as base, then match saved selectedOptionId
                  const mergedTerms = predefinedRes.data.terms.map(predefinedTerm => {
                    if (predefinedTerm.isSection) {
                      // Find matching saved section
                      const savedSection = fetchedTerms.find(
                        st => st.isSection && st.sectionOrder === predefinedTerm.sectionOrder
                      );
                      
                      // ✅ ALWAYS use predefined structure when it has pointsWithOptions
                      if (predefinedTerm.pointsWithOptions && predefinedTerm.pointsWithOptions.length > 0) {
                        const mergedPointsWithOptions = predefinedTerm.pointsWithOptions.map((predefinedPoint, pIdx) => {
                          // Try to get saved selectedOptionId from savedTerm
                          let savedSelectedOptionId = null;
                          let savedSelectedCurrency = null;
                          
                          // Method 1: Use selectedOptionIds array from backend (most reliable)
                          if (savedSection?.selectedOptionIds && savedSection.selectedOptionIds[pIdx]) {
                            savedSelectedOptionId = savedSection.selectedOptionIds[pIdx];
                          }
                          
                          // Method 2: Check if savedTerm has pointsWithOptions with selectedOptionId
                          if (!savedSelectedOptionId && savedSection?.pointsWithOptions && savedSection.pointsWithOptions[pIdx]) {
                            savedSelectedOptionId = savedSection.pointsWithOptions[pIdx].selectedOptionId;
                            savedSelectedCurrency = savedSection.pointsWithOptions[pIdx].selectedCurrency;
                          }
                          
                          // Method 3: Extract from saved point text by matching with predefined options (fallback)
                          if (!savedSelectedOptionId && savedSection?.points && savedSection.points[pIdx]) {
                            const savedPointText = savedSection.points[pIdx];

                            // ✅ Data-driven: extract custom value by inputType from master data
                            if (predefinedPoint.inputType === 'NUMBER') {
                              const numberMatch = savedPointText.match(/(\d+)/);
                              if (numberMatch) savedSelectedOptionId = `custom-${numberMatch[1]}`;
                            } else if (predefinedPoint.inputType === 'TEXT') {
                              const suffix = (predefinedPoint.inputSuffix || '').trim();
                              if (suffix && savedPointText.includes(suffix)) {
                                const afterBase = savedPointText.substring((predefinedPoint.pointText || '').length).trim();
                                const value = afterBase.replace(new RegExp(`\\s+${suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}.*$`, 'i'), '').trim();
                                if (value) savedSelectedOptionId = `custom-${value}`;
                              } else {
                                const riskMatch = savedPointText.match(/classified at\s+([^.]+?)\s+risk level/i);
                                if (riskMatch) savedSelectedOptionId = `custom-${riskMatch[1].trim()}`;
                              }
                            } else if (predefinedPoint.inputType === 'CURRENCY') {
                              const parsed = parseCommercialTermCurrencyFromText(savedPointText);
                              if (parsed.amountDigits) savedSelectedOptionId = `custom-${parsed.amountDigits}`;
                              if (parsed.currencyCode) savedSelectedCurrency = parsed.currencyCode;
                            }
                            // ✅ SPECIAL HANDLING: Custom payment days point (2.2)
                            else if (predefinedPoint.pointNumber === "2.2" && predefinedTerm.sectionType === "INVOICE_GENERATION") {
                              const daysMatch = savedPointText.match(/within\s+(\d+)\s+days/i);
                              if (daysMatch && daysMatch[1] !== "15" && daysMatch[1] !== "30") {
                                savedSelectedOptionId = `custom-${daysMatch[1]}`;
                              }
                            } else {
                              // Try to match saved text with predefined options
                              for (const option of predefinedPoint.options || []) {
                                const optionPattern = `[${option.optionText}]`;
                                if (savedPointText.includes(optionPattern)) {
                                  savedSelectedOptionId = option.optionId;
                                  break;
                                }
                                if (savedPointText.includes(option.optionText)) {
                                  const isUniqueMatch = !predefinedPoint.options.some(opt =>
                                    opt.optionId !== option.optionId && opt.optionText.includes(option.optionText)
                                  );
                                  if (isUniqueMatch) {
                                    savedSelectedOptionId = option.optionId;
                                    break;
                                  }
                                }
                              }
                            }
                          }
                          
                          return {
                            ...predefinedPoint,
                            pointText: predefinedPoint.pointText,
                            pointNumber: predefinedPoint.pointNumber,
                            options: predefinedPoint.options || [],
                            selectedOptionId: savedSelectedOptionId || null,
                            ...(predefinedPoint.inputType === 'CURRENCY' && savedSelectedCurrency != null && { selectedCurrency: savedSelectedCurrency })
                          };
                        });
                        
                        return {
                          ...predefinedTerm,
                          sectionTitle: savedSection?.sectionTitle || predefinedTerm.sectionTitle,
                          sectionType: savedSection?.sectionType || predefinedTerm.sectionType,
                          pointsWithOptions: mergedPointsWithOptions,
                          points: savedSection?.points || predefinedTerm.points || []
                        };
                      }
                      
                      return predefinedTerm;
                    }
                    
                    // For standalone points, try to find matching saved point
                    const savedPoint = fetchedTerms.find(
                      st => !st.isSection && st.pointOrder === predefinedTerm.pointOrder
                    );
                    if (savedPoint) {
                      return {
                        ...predefinedTerm,
                        pointText: savedPoint.pointText || predefinedTerm.pointText,
                        selectedOptionId: savedPoint.selectedOptionId || predefinedTerm.selectedOptionId
                      };
                    }
                    
                    return predefinedTerm;
                  });
                  
                  setCommercialTerms(mergedTerms);
                } else {
                  setCommercialTerms(fetchedTerms);
                }
              } catch (predefinedError) {
                console.warn('Could not reload predefined terms after save:', predefinedError);
                setCommercialTerms(fetchedTerms);
              }
            } else {
              // No preset ID, just use fetched terms
              setCommercialTerms(fetchedTerms);
            }
          } catch (verifyError) {
            console.warn('Could not verify saved commercial terms:', verifyError);
          }
        } catch (commercialTermsError) {
          console.error('❌ Error saving commercial terms:', commercialTermsError);
          console.error('Error details:', commercialTermsError.response?.data);
          console.error('Error status:', commercialTermsError.response?.status);
          setAlert({ 
            type: 'warning', 
            message: 'Proposal saved successfully, but failed to save commercial terms. Please try saving commercial terms again.' 
          });
          // Don't fail the whole save if commercial terms fail
        }
      } else {
        console.warn('⚠️ Cannot save commercial terms: savedProposalId is null');
      }

      // ✅ SITES: persist audit sites (seeded to Audit Platform on conversion).
      // Best-effort — never fail the whole save if site persistence has an issue.
      if (savedProposalId) {
        try {
          await saveProposalSites(savedProposalId);
        } catch (sitesError) {
          console.error('❌ Error saving proposal sites:', sitesError);
        }
      }

      // Show success message
      setAlert({
        type: 'success',
        message: 'Proposal saved successfully!'
      });
      
      // navigate('/proposals');
    } catch (err) {
      console.error('Error saving proposal:', err);
      setAlert({ type: 'danger', message: 'Failed to save proposal.' });
    } finally {
      setLoading(false);
    }
  };


  // // Fungsi untuk proses template + kirim email ke client
  const handleConfirmSendToClient = async () => {
    try {
      setLoadingWorkflow(true);
      
      // ✅ Dapatkan proposal ID terlebih dahulu
      const proposalId = await getProposalId(id);
      
      if (!proposalId) {
        throw new Error('Proposal not found for this lead');
      }
      
      // Kirim proposal ke client menggunakan proposal ID
      await api.post(`/crm/proposals/${proposalId}/send-to-client`);
      
      // Tampilkan success message
      const successMessage = 'Proposal sent to client successfully!';
      
      setAlert({
        type: 'success',
        message: successMessage
      });
      
      setShowWorkflowModal(false);
      setWorkflowItems([]);
      
    } catch (error) {
      console.error('Error sending proposal to client:', error);
      
      setAlert({
        type: 'danger',
        message: error.message === 'Proposal not found for this lead'
          ? 'Proposal not found. Please save the proposal first.'
          : 'Failed to send proposal to client.'
      });
    } finally {
      setLoadingWorkflow(false);
    }
  };

  // Modifikasi fungsi handleSetNeedApproval - TAMBAH proses template saja
  const handleSetNeedApproval = async () => {
    try {
      await api.post(`/crm/proposals/${id}/set-need-approval`);
      setAlert({ type: 'success', message: 'Proposal status updated to Need Approval!' });
      setProposalStatus('need_approval');
      setShowWorkflowModal(false);
      setWorkflowItems([]);
      
      setAlert({ 
        type: 'success', 
        message: 'Proposal status updated to Need Approval! Email will be sent after final approval.' 
      });
      
    } catch (error) {
      setAlert({ type: 'danger', message: 'Failed to update proposal status.' });
    }
  };

  useEffect(() => {
    const fetchProductTypes = async () => {
      try {
        const res = await api.get('/crm/services/options');
        setProductTypes(res.data);
      } catch (err) {
        console.error('fetchProductTypes error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProductTypes();
  }, []);

  // ✅ TAMBAHAN: Fetch accreditations
  useEffect(() => {
    const fetchAccreditations = async () => {
      try {
        const res = await api.get('/md/accreditations');
        
        if (!res.data || !Array.isArray(res.data)) {
          console.warn('Accreditations response is not an array:', res.data);
          setAccreditationOptions([]);
          return;
        }
        
        const options = res.data.map(acc => ({
          id: String(acc.id), // Ensure ID is string
          name: acc.name || `Accreditation ${acc.id}` // Fallback name
        }));
        
        setAccreditationOptions(options);
      } catch (err) {
        console.error('Error fetching accreditations:', err);
        console.error('Error details:', err.response?.data || err.message);
        setAccreditationOptions([]);
      }
    };
    fetchAccreditations();
    
    // ✅ BANK: Fetch banks
    const fetchBanks = async () => {
      try {
        const res = await api.get('/md/banks');
        
        if (!res.data || !Array.isArray(res.data)) {
          console.warn('Banks response is not an array:', res.data);
          setBankOptions([]);
          return;
        }
        
        const options = res.data.map(bank => ({
          id: String(bank.id), // Ensure ID is string
          bankName: (bank.bankName || '') + (bank.bankBranch ? ' – ' + bank.bankBranch : '') || `Bank ${bank.id}` // Fallback name
        }));
        
        setBankOptions(options);
      } catch (err) {
        console.error('Error fetching banks:', err);
        console.error('Error details:', err.response?.data || err.message);
        setBankOptions([]);
      }
    };
    fetchBanks();

    // ✅ ISCC SCOPE: Fetch ISCC scope options
    fetchIsccScopeOptions();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      // TAMBAHAN: Cek apakah data sudah di-fetch
      if (dataFetched) {
        return;
      }
      
      // ✅ TAMBAHAN: Ambil data lead untuk mendapatkan company name
      let leadData = null;
      try {
        const leadResponse = await api.get(`/crm/leads/${id}`);
        leadData = leadResponse.data;
        setLeadData(leadData); // ✅ Store leadData in state
      } catch (leadError) {
        console.error('Error fetching lead data:', leadError);
      }
      
      try {
        const res = await api.get(`/crm/proposals/lead/${id}`);
        if (res.data && res.data.length > 0) {
          const proposal = res.data[0];
          setHasExistingProposal(true);

          // TAMBAHAN: Set flag agar tidak fetch lagi
          setDataFetched(true);

          // ✅ SITES: load persisted audit sites for this proposal (empty → prefill effect
          // fills a single site from the company address).
          if (proposal?.id) {
            loadProposalSites(proposal.id);
          }

          // ✅ FIX: Group items by standardId and properly handle parent-child relationships
          const services = (proposal.proposalServices || []).map(section => {
            const allStandards = (section.standards || [])
              .filter(std => typeof std === 'object' && std !== null);
            
            // Separate parent items (parentId === null) from child items (parentId !== null)
            const parentItems = allStandards.filter(std => !std.parentId);
            const childItems = allStandards.filter(std => std.parentId);
            
            // Group child items by their parentId
            const childrenByParentId = {};
            childItems.forEach(child => {
              const parentId = child.parentId;
              if (!childrenByParentId[parentId]) {
                childrenByParentId[parentId] = [];
              }
              childrenByParentId[parentId].push(child);
            });
            
            // Map parent items and attach their children
            const standards = parentItems.map(parent => {
              // Find children for this parent
              const children = childrenByParentId[parent.id] || [];
              
              return {
                ...parent,
                standard: parent.standardId,
                standardVersionId: parent.standardVersionId || '', // ✅ TAMBAHAN: Load standard version ID
                productCategory: parent.productCategoryId,
                serviceItem: parent.serviceItemId,
                yearType: parent.yearType || 0,
                selectedYear: parent.selectedYear || new Date().getFullYear(),
                unitPrice: parent.unitPriceDisplayCurrency || parent.unitPrice,
                participants: parent.participants || null, // ✅ TAMBAHAN: Load participants field
                // ✅ FIX: Include missing fields that are needed for proper persistence
                unitPriceBaseCurrency: parent.unitPriceBaseCurrency,
                unitPriceDisplayCurrency: parent.unitPriceDisplayCurrency,
                workFlowIdSystem: parent.workFlowIdSystem,
                workFlowIdChoice: parent.workFlowIdChoice,
                fetchMaxPrice: parent.fetchMaxPrice,
                fetchMaxPriceBaseCurrency: parent.fetchMaxPriceBaseCurrency,
                currencyCode: parent.currencyCode || 'IDR',
                eaCodeSectorIds: Array.isArray(parent.eaCodeSectorIds)
                  ? parent.eaCodeSectorIds.map(id => String(id))
                  : [],
                // ✅ FIX: Map children to productCategories
                productCategories: children.map(pc => ({
                  ...pc,
                  standard: pc.standardId || parent.standardId || '',
                  standardVersionId: pc.standardVersionId || parent.standardVersionId || '',
                  productCategory: pc.productCategoryId,
                  serviceItem: pc.serviceItemId,
                  yearType: pc.yearType || 0,
                  selectedYear: pc.selectedYear || new Date().getFullYear(),
                  qty: pc.qty,
                  unit: pc.unit,
                  participants: pc.participants || null,
                  unitPrice: pc.unitPriceDisplayCurrency || pc.unitPrice,
                  unitPriceBaseCurrency: pc.unitPriceBaseCurrency,
                  unitPriceDisplayCurrency: pc.unitPriceDisplayCurrency,
                  workFlowIdSystem: pc.workFlowIdSystem,
                  workFlowIdChoice: pc.workFlowIdChoice,
                  fetchMaxPrice: pc.fetchMaxPrice,
                  fetchMaxPriceBaseCurrency: pc.fetchMaxPriceBaseCurrency,
                  currencyCode: pc.currencyCode || 'IDR',
                  eaCodeSectorIds: Array.isArray(pc.eaCodeSectorIds)
                    ? pc.eaCodeSectorIds.map(id => String(id))
                    : [],
                }))
              };
            });
            
            return {
              ...section,
              service: section.serviceId,
              standards
            };
          });
          
          setProposalServices(services);
          // ✅ Version options for these standards are fetched by the dedicated
          // self-healing useEffect (watches proposalServices) below.

          // ✅ TAMBAHAN: Fetch scope from lead services when proposal exists
          let scopeValue = '';
          try {
            const leadServicesRes = await api.get(`/lead-services/by-lead/${id}`);
            if (leadServicesRes.data && leadServicesRes.data.length > 0) {
              const firstService = leadServicesRes.data[0];
              if (firstService.scope) {
                // Check if this is an ISCC/Sustainability service
                const serviceName = firstService.serviceName || '';
                const isIsccOrSustainability = serviceName.toLowerCase().includes('iscc') ||
                                              serviceName.toLowerCase().includes('sustainability');

                // Handle scope based on service type
                if (isIsccOrSustainability) {
                  // For ISCC/Sustainability services, scope should be an array
                  if (Array.isArray(firstService.scope)) {
                    scopeValue = firstService.scope;
                  } else if (typeof firstService.scope === 'string' && firstService.scope.startsWith('[') && firstService.scope.endsWith(']')) {
                    try {
                      const parsed = JSON.parse(firstService.scope);
                      scopeValue = Array.isArray(parsed) ? parsed : [firstService.scope];
                    } catch (e) {
                      scopeValue = [firstService.scope];
                    }
                  } else if (typeof firstService.scope === 'string' && firstService.scope.includes(',')) {
                    // Handle comma-separated string (e.g., "27, 26")
                    scopeValue = firstService.scope.split(',').map(s => s.trim()).filter(s => s);
                  } else {
                    scopeValue = [firstService.scope];
                  }
                } else {
                  // For non-ISCC/Sustainability services, scope should be a string
                  if (typeof firstService.scope === 'string' && firstService.scope.startsWith('[') && firstService.scope.endsWith(']')) {
                    try {
                      const parsed = JSON.parse(firstService.scope);
                      scopeValue = Array.isArray(parsed) ? parsed.join(', ') : firstService.scope;
                    } catch (e) {
                      scopeValue = firstService.scope;
                    }
                  } else {
                    scopeValue = firstService.scope || '';
                  }
                }
              }
            }
          } catch (scopeError) {
            console.warn('Could not fetch scope from lead services:', scopeError);
          }

          // ✅ SCOPE FALLBACK: lead_services can be empty (e.g. never created for this lead)
          // even though real scope data exists on the proposal's own standards
          // (proposal_standard_items.scope, already loaded into `services` above). Only fall
          // back here when lead_services truly had nothing, so it never overrides real data.
          const isScopeValueEmpty = Array.isArray(scopeValue) ? scopeValue.length === 0 : !scopeValue;
          if (isScopeValueEmpty) {
            const standardScopes = services
              .flatMap(section => (section.standards || []).map(std => std?.scope))
              .filter(val => val !== null && val !== undefined && String(val).trim() !== '');
            if (standardScopes.length > 0) {
              scopeValue = [...new Set(standardScopes.map(val => String(val).trim()))].join(', ');
            }
          }

          // ✅ SCOPE: Scope is stored in lead_services, not in proposal
          // We already loaded scope from lead_services above, so no need to check proposal.scope
          // Backend will automatically read from lead_services when proposal.scope is null/empty

          // Tambahkan ini:
          setProposalData(prev => ({
            ...prev,
            companyName: proposal.companyName || '',
            proposalCode: proposal.proposalCode || '',
            proposalDate: proposal.proposalDate || '',
            validUntil: proposal.validUntil || '',
            serviceTemplateId: proposal.serviceTemplateId || '',
            proposalTemplateId: proposal.proposalTemplateId || '',
            scope: scopeValue, // ✅ SCOPE: Set scope from lead_services (not from proposal entity - scope belongs to service, not proposal)
            accreditationIds: proposal.accreditationIds 
                ? proposal.accreditationIds.map(id => String(id))
                : [], // ✅ ACCREDITATION: Load as array
            bankId: proposal.bankId ? String(proposal.bankId) : '', // ✅ BANK: Set bank ID
            projectName: leadData?.projectName || '', // ✅ PROJECT NAME: Set project name from lead
            signatureName: proposal.signatureName || '', // ✅ SIGNATURE NAME: Set signature name
            signaturePosition: proposal.signaturePosition || '', // ✅ SIGNATURE POSITION: Set signature position
            trainingDeliveryMethod: proposal.trainingDeliveryMethod || '', // ✅ TRAINING DELIVERY: Set training delivery method
            // ✅ ISCC-SPECIFIC FIELDS: Load from proposal
            turnover: proposal.turnover || '',
            registrationNumber: proposal.registrationNumber || '',
            facilitiesAndAddress: proposal.facilitiesAndAddress || '',
            materialInputIds: proposal.materialInputIds
                ? (Array.isArray(proposal.materialInputIds) ? proposal.materialInputIds.map(id => String(id)) : [])
                : [],
            materialOutputIds: proposal.materialOutputIds
                ? (Array.isArray(proposal.materialOutputIds) ? proposal.materialOutputIds.map(id => String(id)) : [])
                : [],
            ghgCalculation: proposal.ghgCalculation || '',

            // ✅ CARBON PROJECT & GHG FIELDS: Load from proposal
            // Shared fields
            objective: proposal.objective || '',
            sector: leadData?.sector || '', // ✅ Fetch sector from lead data
            materiality: proposal.materiality || '',
            auditMethodology: proposal.auditMethodology || '',
            multiSite: Boolean(proposal.multiSite),
            numberOfSites: proposal.numberOfSites || '',
            integratedStandardIds: proposal.integratedStandardIds || [],
            combinedStandardIds: proposal.combinedStandardIds || [],
            numberOfFacilities: proposal.numberOfFacilities || '',
            siteAddress: proposal.siteAddress || '',
            facilityAddress: proposal.facilityAddress || '',

            // Carbon Project specific
            methodology: proposal.methodology || '',
            typeOfEmissionsRemovals: proposal.typeOfEmissionsRemovals || '',
            ghgMonitoringPeriod: proposal.ghgMonitoringPeriod || '',
            ghgEmissionReductions: proposal.ghgEmissionReductions || '',

            // GHG specific
            organizationActivities: proposal.organizationActivities || '',
            reportingBoundaries: proposal.reportingBoundaries || '',
            claim: proposal.claim || '',
            levelOfAssurance: proposal.levelOfAssurance || '',
            verificationLocation: proposal.verificationLocation || '',
          }));

          // Set rate date: use proposal's created_at so exchange rate is taken from that point in time
          const rawCreatedAt = proposal.dates?.created_at || proposal.created_at || proposal.createdAt;
          if (rawCreatedAt) {
            setProposalRateDate(rawCreatedAt.toString().split('T')[0]);
          }

          // ✅ MULTI-CURRENCY: Use proposal's saved display currency
          if (proposal.displayCurrencyCode) {
            setProposalCurrencyCode(proposal.displayCurrencyCode);
          } else if (proposal.displayCurrencyId) {
            try {
              const currencyRes = await api.get(`/crm/currencies/${proposal.displayCurrencyId}`);
              setProposalCurrencyCode(currencyRes.data.currencyCode || 'IDR');
            } catch (error) {
              console.warn('Could not fetch currency for displayCurrencyId:', proposal.displayCurrencyId);
              setProposalCurrencyCode('IDR');
            }
          }
          setProposalStatus(proposal.status === 3 ? 'need_approval' : proposal.status); // <-- status dari backend
          
          // ✅ TAMBAHAN: Fetch commercial terms if proposal exists
          try {
            const commercialTermsRes = await api.get(`/crm/proposals/${proposal.id}/commercial-terms`);
            const fetchedTerms = commercialTermsRes.data || [];
            
            // ✅ COMMERCIAL TERMS PRESET: Load preset ID from proposal
            if (proposal.commercialTermsPresetId) {
              setCommercialTermsPresetId(proposal.commercialTermsPresetId);
              
              // ✅ POINT OPTIONS: Always load predefined structure and merge with saved data
              try {
                const predefinedRes = await api.get(`/crm/proposals/commercial-terms/predefined/${proposal.commercialTermsPresetId}`);
                
                if (predefinedRes.data && predefinedRes.data.terms) {
                  // ✅ Use predefined structure as base, then match saved selectedOptionId
                  const mergedTerms = predefinedRes.data.terms.map(predefinedTerm => {
                    if (predefinedTerm.isSection) {
                      // Find matching saved section
                      const savedSection = fetchedTerms.find(
                        st => st.isSection && st.sectionOrder === predefinedTerm.sectionOrder
                      );
                      
                      // ✅ ALWAYS use predefined structure when it has pointsWithOptions (even if savedSection doesn't exist)
                      if (predefinedTerm.pointsWithOptions && predefinedTerm.pointsWithOptions.length > 0) {
                        const mergedPointsWithOptions = predefinedTerm.pointsWithOptions.map((predefinedPoint, pIdx) => {
                          // ✅ DEBUG: Verify predefined point has options
                          if (!predefinedPoint.options || predefinedPoint.options.length === 0) {
                            console.warn('⚠️ Predefined point missing options:', {
                              pointNumber: predefinedPoint.pointNumber,
                              pointText: predefinedPoint.pointText,
                              sectionOrder: predefinedTerm.sectionOrder
                            });
                          }
                          
                          // Try to get saved selectedOptionId from savedTerm
                          let savedSelectedOptionId = null;
                          let savedSelectedCurrency = null;
                          
                          // Method 1: Use selectedOptionIds array from backend (most reliable)
                          if (savedSection?.selectedOptionIds && savedSection.selectedOptionIds[pIdx]) {
                            savedSelectedOptionId = savedSection.selectedOptionIds[pIdx];
                          }
                          
                          // Method 2: Check if savedTerm has pointsWithOptions with selectedOptionId
                          if (!savedSelectedOptionId && savedSection?.pointsWithOptions && savedSection.pointsWithOptions[pIdx]) {
                            savedSelectedOptionId = savedSection.pointsWithOptions[pIdx].selectedOptionId;
                            savedSelectedCurrency = savedSection.pointsWithOptions[pIdx].selectedCurrency;
                          }
                          
                          // Method 3: Extract from saved point text by matching with predefined options (fallback)
                          if (!savedSelectedOptionId && savedSection?.points && savedSection.points[pIdx]) {
                            const savedPointText = savedSection.points[pIdx];

                            // ✅ Data-driven: extract custom value by inputType from master data
                            if (predefinedPoint.inputType === 'NUMBER') {
                              const numberMatch = savedPointText.match(/(\d+)/);
                              if (numberMatch) savedSelectedOptionId = `custom-${numberMatch[1]}`;
                            } else if (predefinedPoint.inputType === 'TEXT') {
                              const suffix = (predefinedPoint.inputSuffix || '').trim();
                              if (suffix && savedPointText.includes(suffix)) {
                                const afterBase = savedPointText.substring((predefinedPoint.pointText || '').length).trim();
                                const value = afterBase.replace(new RegExp(`\\s+${suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}.*$`, 'i'), '').trim();
                                if (value) savedSelectedOptionId = `custom-${value}`;
                              } else {
                                const riskMatch = savedPointText.match(/classified at\s+([^.]+?)\s+risk level/i);
                                if (riskMatch) savedSelectedOptionId = `custom-${riskMatch[1].trim()}`;
                              }
                            } else if (predefinedPoint.inputType === 'CURRENCY') {
                              const parsed = parseCommercialTermCurrencyFromText(savedPointText);
                              if (parsed.amountDigits) savedSelectedOptionId = `custom-${parsed.amountDigits}`;
                              if (parsed.currencyCode) savedSelectedCurrency = parsed.currencyCode;
                            }
                            // ✅ SPECIAL HANDLING: Custom payment days point (2.2) - extract custom days from text
                            else if (predefinedPoint.pointNumber === "2.2" && predefinedTerm.sectionType === "INVOICE_GENERATION") {
                              // Extract custom days from text like "The payment is due within 45 days after the date of the invoice..."
                              const daysMatch = savedPointText.match(/within\s+(\d+)\s+days/i);
                              if (daysMatch) {
                                const days = daysMatch[1];
                                // Check if it's not one of the predefined options (15 or 30)
                                if (days !== "15" && days !== "30") {
                                  // Store as custom option ID so input field can extract it
                                  savedSelectedOptionId = `custom-${days}`;
                                }
                              }
                            }
 else {
                              // Try to match saved text with predefined options
                              // Look for pattern: baseText + " [" + optionText + "]"
                              for (const option of predefinedPoint.options || []) {
                                // Check if saved text contains this option text in brackets
                                const optionPattern = `[${option.optionText}]`;
                                if (savedPointText.includes(optionPattern)) {
                                  savedSelectedOptionId = option.optionId;
                                  break;
                                }
                                // Also try without brackets (for Clause 10 which inserts before comma)
                                if (savedPointText.includes(option.optionText)) {
                                  // Verify it's not part of another option text
                                  const isUniqueMatch = !predefinedPoint.options.some(opt =>
                                    opt.optionId !== option.optionId && opt.optionText.includes(option.optionText)
                                  );
                                  if (isUniqueMatch) {
                                    savedSelectedOptionId = option.optionId;
                                    break;
                                  }
                                }
                              }
                            }
                          }
                          
                          // ✅ CRITICAL: Always include options from predefined structure
                          // Keep pointText as the base text from predefined structure
                          // Preserve inputType and inputSuffix so CURRENCY stays currency and suffix is shown
                          return {
                            ...predefinedPoint,
                            pointText: predefinedPoint.pointText, // ✅ Keep base text, don't merge here
                            pointNumber: predefinedPoint.pointNumber,
                            options: predefinedPoint.options || [], // ✅ Explicitly ensure options array exists
                            selectedOptionId: savedSelectedOptionId || null,
                            ...(predefinedPoint.inputType === 'CURRENCY' && savedSelectedCurrency != null && { selectedCurrency: savedSelectedCurrency })
                          };
                        });
                        
                        // ✅ Always return predefined structure with merged selectedOptionId
                        return {
                          ...predefinedTerm,
                          // ✅ Keep saved section data (title, type) if available, otherwise use predefined
                          sectionTitle: savedSection?.sectionTitle || predefinedTerm.sectionTitle,
                          sectionType: savedSection?.sectionType || predefinedTerm.sectionType,
                          pointsWithOptions: mergedPointsWithOptions, // ✅ This always has options array
                          // ✅ Keep backward compatibility with points array
                          points: savedSection?.points || predefinedTerm.points || []
                        };
                      }
                      
                      // If predefined doesn't have pointsWithOptions, still return it (shouldn't happen for certification)
                      return predefinedTerm;
                    }
                    
                    // For standalone points, try to find matching saved point
                    const savedPoint = fetchedTerms.find(
                      st => !st.isSection && st.pointOrder === predefinedTerm.pointOrder
                    );
                    if (savedPoint) {
                      return {
                        ...predefinedTerm, // Use predefined structure (includes options if available)
                        pointText: savedPoint.pointText || predefinedTerm.pointText,
                        selectedOptionId: savedPoint.selectedOptionId || predefinedTerm.selectedOptionId
                      };
                    }
                    
                    return predefinedTerm;
                  });
                  
                  setCommercialTerms(mergedTerms);
                } else {
                  // Fallback: use fetched terms if predefined not found
                  console.warn('⚠️ Predefined terms not found, using fetched terms');
                  setCommercialTerms(fetchedTerms);
                }
              } catch (predefinedError) {
                console.warn('Could not fetch predefined terms for merging:', predefinedError);
                setCommercialTerms(fetchedTerms);
              }
            } else {
              setCommercialTermsPresetId(null);
              setCommercialTerms(fetchedTerms);
            }
          } catch (err) {
            console.error('Error fetching commercial terms:', err);
            // ✅ If 404 but preset ID exists, still load predefined structure
            if (err.response?.status === 404 && proposal.commercialTermsPresetId) {
              setCommercialTermsPresetId(proposal.commercialTermsPresetId);
              // Load predefined structure even if no saved terms exist
              try {
                const predefinedRes = await api.get(`/crm/proposals/commercial-terms/predefined/${proposal.commercialTermsPresetId}`);
                if (predefinedRes.data && predefinedRes.data.terms) {
                  // Use predefined structure as-is (no saved data to merge)
                  const predefinedTerms = predefinedRes.data.terms.map(predefinedTerm => ({
                    ...predefinedTerm,
                    pointsWithOptions: predefinedTerm.pointsWithOptions?.map(point => ({
                      ...point,
                      selectedOptionId: null // No saved selection
                    })) || []
                  }));
                  setCommercialTerms(predefinedTerms);
                } else {
                  setCommercialTerms([]);
                }
              } catch (predefinedError) {
                console.warn('Could not fetch predefined terms after 404:', predefinedError);
                setCommercialTerms([]);
              }
            } else {
              setCommercialTerms([]);
              setCommercialTermsPresetId(null);
            }
          }
        } else {
          // ✅ TAMBAHAN: Cek localStorage untuk data lead services dari DetailTab
          const leadServicesKey = `leadServices_${id}`;
          const leadServicesData = localStorage.getItem(leadServicesKey);
          
          if (leadServicesData) {
            try {
              const parsedData = JSON.parse(leadServicesData);
              
              // Fallback ke lead-services jika tidak ada proposal
              const leadRes = await api.get(`/lead-services/by-lead/${id}`);
              setLeadProducts(leadRes.data);
              
              // TAMBAHAN: Set flag agar tidak fetch lagi
              setDataFetched(true);

              if (leadRes.data && leadRes.data.length > 0) {
                // ✅ TAMBAHAN: Gunakan data dari localStorage untuk mengisi standards
                const services = leadRes.data.map((item, index) => {
                  const localStorageService = parsedData.find(ls => ls.serviceId === item.serviceId);
                  const rawStandards = localStorageService?.standards || [];
                  // DetailTab stores standards as flat string IDs and scope separately as scopeRows array
                  const scopeRows = Array.isArray(localStorageService?.scope) ? localStorageService.scope : [];

                  return {
                    id: item.id,
                    service: String(item.serviceId),
                    standards: rawStandards.length > 0
                      ? rawStandards.map((s, sIdx) => ({
                          standard: String(s),
                          scope: scopeRows[sIdx] !== undefined ? scopeRows[sIdx] : null,
                          standardVersionId: '',
                          productCategory: '',
                          serviceItem: '',
                          yearType: 0,
                          selectedYear: new Date().getFullYear(),
                          qty: 1,
                          unit: '',
                          participants: null,
                          unitPrice: '',
                        }))
                      : [
                          {
                            standard: '',
                            scope: null,
                            productCategory: '',
                            serviceItem: '',
                            yearType: 0,
                            selectedYear: new Date().getFullYear(),
                            qty: 1,
                            unit: '',
                            unitPrice: '',
                          },
                        ],
                  };
                });
                
                setProposalServices(services);
                
              // ✅ TAMBAHAN: Set company name dan scope dari data lead
                if (leadData) {
                // ✅ TAMBAHAN: Extract scope from first lead service
                let scopeValue = '';
                if (leadRes.data && leadRes.data.length > 0) {
                  const firstService = leadRes.data[0];
                  if (firstService.scope) {
                    // Check if this is an ISCC/Sustainability service
                    const serviceName = firstService.serviceName || '';
                    const isIsccOrSustainability = serviceName.toLowerCase().includes('iscc') ||
                                                  serviceName.toLowerCase().includes('sustainability');

                    // Handle scope based on service type
                    if (isIsccOrSustainability) {
                      // For ISCC/Sustainability services, scope should be an array
                      if (Array.isArray(firstService.scope)) {
                        scopeValue = firstService.scope;
                      } else if (typeof firstService.scope === 'string' && firstService.scope.startsWith('[') && firstService.scope.endsWith(']')) {
                        try {
                          const parsed = JSON.parse(firstService.scope);
                          scopeValue = Array.isArray(parsed) ? parsed : [firstService.scope];
                        } catch (e) {
                          scopeValue = [firstService.scope];
                        }
                      } else if (typeof firstService.scope === 'string' && firstService.scope.includes(',')) {
                        scopeValue = firstService.scope.split(',').map(s => s.trim()).filter(s => s);
                      } else {
                        scopeValue = [firstService.scope];
                      }
                    } else {
                      // For non-ISCC/Sustainability services, scope should be a string
                      if (typeof firstService.scope === 'string' && firstService.scope.startsWith('[') && firstService.scope.endsWith(']')) {
                        try {
                          const parsed = JSON.parse(firstService.scope);
                          scopeValue = Array.isArray(parsed) ? parsed.join(', ') : firstService.scope;
                        } catch (e) {
                          scopeValue = firstService.scope;
                        }
                      } else {
                        scopeValue = firstService.scope || '';
                      }
                    }
                  }
                }

                  setProposalData(prev => ({
                    ...prev,
                    companyName: leadData.companyName || '',
                    scope: scopeValue, // ✅ SCOPE: Set scope from lead_services (not from proposal entity - scope belongs to service, not proposal)
                    projectName: leadData.projectName || '', // ✅ PROJECT NAME: Set project name from lead
                  }));
                  
                  // ✅ FIX: No proposal record exists yet here (404 fallback branch —
                  // fresh "Convert to Proposal" from a Lead). `proposal` is `const`-scoped
                  // to the sibling success branch, so referencing it threw
                  // `ReferenceError: proposal is not defined` -> ErrorBoundary crash on
                  // first navigation (worked after refresh once the proposal existed).
                  // `proposalCurrencyCode` already defaults to 'IDR'.
                }
                
                // ✅ TAMBAHAN: Hapus data dari localStorage setelah digunakan
                localStorage.removeItem(leadServicesKey);
              }
            } catch (parseError) {
              console.error('Error parsing localStorage data:', parseError);
              // Fallback ke logic yang lama
              const leadRes = await api.get(`/lead-services/by-lead/${id}`);
              setLeadProducts(leadRes.data);
              setDataFetched(true);
              
              if (leadRes.data && leadRes.data.length > 0) {
                const services = leadRes.data.map(item => ({
                  id: item.id,
                  service: String(item.serviceId),
                  standards: [
                    {
                      standard: '',
                      productCategory: '',
                      serviceItem: '',
                      yearType: 0,
                      selectedYear: new Date().getFullYear(),
                      qty: 1,
                      unit: '',
                      participants: null, // ✅ TAMBAHAN: Participants field for training services
                      unitPrice: '',
                    },
                  ],
                }));
                setProposalServices(services);
                
                // ✅ TAMBAHAN: Set company name dan scope dari data lead
                if (leadData) {
                  // ✅ TAMBAHAN: Extract scope from first lead service
                  let scopeValue = '';
                  if (leadRes.data && leadRes.data.length > 0) {
                    const firstService = leadRes.data[0];
                    if (firstService.scope) {
                      // Check if this is an ISCC/Sustainability service
                      const serviceName = firstService.serviceName || '';
                      const isIsccOrSustainability = serviceName.toLowerCase().includes('iscc') ||
                                                    serviceName.toLowerCase().includes('sustainability');

                      // Handle scope based on service type
                      if (isIsccOrSustainability) {
                        // For ISCC/Sustainability services, scope should be an array
                        if (Array.isArray(firstService.scope)) {
                          scopeValue = firstService.scope;
                        } else if (typeof firstService.scope === 'string' && firstService.scope.startsWith('[') && firstService.scope.endsWith(']')) {
                          try {
                            const parsed = JSON.parse(firstService.scope);
                            scopeValue = Array.isArray(parsed) ? parsed : [firstService.scope];
                          } catch (e) {
                            scopeValue = [firstService.scope];
                          }
                        } else if (typeof firstService.scope === 'string' && firstService.scope.includes(',')) {
                          scopeValue = firstService.scope.split(',').map(s => s.trim()).filter(s => s);
                        } else {
                          scopeValue = [firstService.scope];
                        }
                      } else {
                        // For non-ISCC/Sustainability services, scope should be a string
                        if (typeof firstService.scope === 'string' && firstService.scope.startsWith('[') && firstService.scope.endsWith(']')) {
                          try {
                            const parsed = JSON.parse(firstService.scope);
                            scopeValue = Array.isArray(parsed) ? parsed.join(', ') : firstService.scope;
                          } catch (e) {
                            scopeValue = firstService.scope;
                          }
                        } else {
                          scopeValue = firstService.scope || '';
                        }
                      }
                    }
                  }

                  setProposalData(prev => ({
                    ...prev,
                    companyName: leadData.companyName || '',
                    scope: scopeValue, // ✅ SCOPE: Set scope from lead_services (not from proposal entity - scope belongs to service, not proposal)
                    projectName: leadData.projectName || '', // ✅ PROJECT NAME: Set project name from lead
                  }));
                }
              }
            }
          } else {
            // Fallback ke lead-services jika tidak ada proposal dan tidak ada data localStorage
            const leadRes = await api.get(`/lead-services/by-lead/${id}`);
            setLeadProducts(leadRes.data);
            
            // TAMBAHAN: Set flag agar tidak fetch lagi
            setDataFetched(true);

            if (leadRes.data && leadRes.data.length > 0) {
              const services = leadRes.data.map(item => ({
                id: item.id,
                service: String(item.serviceId),
                standards: [
                  {
                    standard: '',
                    productCategory: '',
                    serviceItem: '',
                    yearType: 0,
                    selectedYear: new Date().getFullYear(),
                    qty: 1,
                    unit: '',
                    unitPrice: '',
                  },
                ],
              }));
              setProposalServices(services);
              
              // ✅ TAMBAHAN: Set company name dan scope dari data lead
              if (leadData) {
                // ✅ TAMBAHAN: Extract scope from first lead service
                let scopeValue = '';
                if (leadRes.data && leadRes.data.length > 0) {
                  const firstService = leadRes.data[0];
                  if (firstService.scope) {
                    // Handle scope - could be string or JSON array for ISCC
                    if (typeof firstService.scope === 'string' && firstService.scope.startsWith('[') && firstService.scope.endsWith(']')) {
                      try {
                        const parsed = JSON.parse(firstService.scope);
                        scopeValue = Array.isArray(parsed) ? parsed.join(', ') : firstService.scope;
                      } catch (e) {
                        scopeValue = firstService.scope;
                      }
                    } else {
                      scopeValue = firstService.scope || '';
                    }
                  }
                }
                
                setProposalData(prev => ({
                  ...prev,
                  companyName: leadData.companyName || '',
                  scope: scopeValue, // ✅ SCOPE: Set scope from lead_services (not from proposal entity - scope belongs to service, not proposal)
                }));
                // No proposal record in this fallback path — `proposalCurrencyCode` already defaults to 'IDR'.
              }
            }
          }
        }
      } catch (err) {
        // Jika error (misal: 404), fallback ke lead-services
        if (err.response && err.response.status === 404) {
          // ✅ TAMBAHAN: Cek localStorage untuk data lead services dari DetailTab
          const leadServicesKey = `leadServices_${id}`;
          const leadServicesData = localStorage.getItem(leadServicesKey);
          
          if (leadServicesData) {
            try {
              const parsedData = JSON.parse(leadServicesData);
              
              const leadRes = await api.get(`/lead-services/by-lead/${id}`);
              setLeadProducts(leadRes.data);
              
              // TAMBAHAN: Set flag agar tidak fetch lagi
              setDataFetched(true);
              
              // Tambahkan inisialisasi proposalServices:
              if (leadRes.data && leadRes.data.length > 0) {
                // ✅ TAMBAHAN: Gunakan data dari localStorage untuk mengisi standards
                const services = leadRes.data.map((item, index) => {
                  const localStorageService = parsedData.find(ls => ls.serviceId === item.serviceId);
                  
                  return {
                    id: item.id,
                    service: String(item.serviceId),
                    standards: localStorageService && localStorageService.standards && localStorageService.standards.length > 0
                      ? localStorageService.standards.map(standardId => ({
                          standard: String(standardId), // ✅ Standards dari DetailTab
                          standardVersionId: '', // ✅ TAMBAHAN: Standard version ID
                          productCategory: '',
                          serviceItem: '',
                          yearType: 0,
                          unit: '',
                          participants: null, // ✅ TAMBAHAN: Participants field for training services
                          unitPrice: '',
                        }))
                      : [
                          {
                            standard: '',
                            productCategory: '',
                            serviceItem: '',
                            yearType: 0,
                            selectedYear: new Date().getFullYear(),
                            qty: 1,
                            unit: '',
                            participants: null, // ✅ TAMBAHAN: Participants field for training services
                            unitPrice: '',
                          },
                        ],
                  };
                });
                
                setProposalServices(services);
                
                // ✅ TAMBAHAN: Hapus data dari localStorage setelah digunakan
                localStorage.removeItem(leadServicesKey);
              }
            } catch (parseError) {
              console.error('Error parsing localStorage data (404 fallback):', parseError);
              // Fallback ke logic yang lama
              const leadRes = await api.get(`/lead-services/by-lead/${id}`);
              setLeadProducts(leadRes.data);
              setDataFetched(true);
              
              if (leadRes.data && leadRes.data.length > 0) {
                const services = leadRes.data.map(item => ({
                  id: item.id,
                  service: String(item.serviceId),
                  standards: [
                    {
                      standard: '',
                      productCategory: '',
                      serviceItem: '',
                      yearType: 0,
                      selectedYear: new Date().getFullYear(),
                      qty: 1,
                      unit: '',
                      participants: null, // ✅ TAMBAHAN: Participants field for training services
                      unitPrice: '',
                    },
                  ],
                }));
                setProposalServices(services);
                
                // ✅ TAMBAHAN: Set scope from lead services
                if (leadRes.data && leadRes.data.length > 0) {
                  const firstService = leadRes.data[0];
                  let scopeValue = '';
                  if (firstService.scope) {
                    // Check if this is an ISCC/Sustainability service
                    const serviceName = firstService.serviceName || '';
                    const isIsccOrSustainability = serviceName.toLowerCase().includes('iscc') ||
                                                  serviceName.toLowerCase().includes('sustainability');

                    // Handle scope based on service type
                    if (isIsccOrSustainability) {
                      // For ISCC/Sustainability services, scope should be an array
                      if (Array.isArray(firstService.scope)) {
                        scopeValue = firstService.scope;
                      } else if (typeof firstService.scope === 'string' && firstService.scope.startsWith('[') && firstService.scope.endsWith(']')) {
                        try {
                          const parsed = JSON.parse(firstService.scope);
                          scopeValue = Array.isArray(parsed) ? parsed : [firstService.scope];
                        } catch (e) {
                          scopeValue = [firstService.scope];
                        }
                      } else if (typeof firstService.scope === 'string' && firstService.scope.includes(',')) {
                        scopeValue = firstService.scope.split(',').map(s => s.trim()).filter(s => s);
                      } else {
                        scopeValue = [firstService.scope];
                      }
                    } else {
                      // For non-ISCC/Sustainability services, scope should be a string
                      if (typeof firstService.scope === 'string' && firstService.scope.startsWith('[') && firstService.scope.endsWith(']')) {
                        try {
                          const parsed = JSON.parse(firstService.scope);
                          scopeValue = Array.isArray(parsed) ? parsed.join(', ') : firstService.scope;
                        } catch (e) {
                          scopeValue = firstService.scope;
                        }
                      } else {
                        scopeValue = firstService.scope || '';
                      }
                    }
                  }

                  if (leadData) {
                    setProposalData(prev => ({
                      ...prev,
                      companyName: leadData.companyName || '',
                      scope: scopeValue, // ✅ SCOPE: Set scope from lead_services (not from proposal entity - scope belongs to service, not proposal)
                      projectName: leadData.projectName || '', // ✅ PROJECT NAME: Set project name from lead
                    }));
                  }
                }
              }
            }
          } else {
            // Fallback ke logic yang lama
            const leadRes = await api.get(`/lead-services/by-lead/${id}`);
            setLeadProducts(leadRes.data);
            
            // TAMBAHAN: Set flag agar tidak fetch lagi
            setDataFetched(true);
            
            // Tambahkan inisialisasi proposalServices:
            if (leadRes.data && leadRes.data.length > 0) {
              const services = leadRes.data.map(item => ({
                id: item.id,
                service: String(item.serviceId),
                standards: [
                  {
                    standard: '',
                    productCategory: '',
                    serviceItem: '',
                    yearType: 0,
                    selectedYear: new Date().getFullYear(),
                    qty: 1,
                    unit: '',
                    unitPrice: '',
                  },
                ],
              }));
              setProposalServices(services);
              
              // ✅ TAMBAHAN: Set company name dan scope dari data lead
              if (leadData) {
                // ✅ TAMBAHAN: Extract scope from first lead service
                let scopeValue = '';
                if (leadRes.data && leadRes.data.length > 0) {
                  const firstService = leadRes.data[0];
                  if (firstService.scope) {
                    // Handle scope - could be string or JSON array for ISCC
                    if (typeof firstService.scope === 'string' && firstService.scope.startsWith('[') && firstService.scope.endsWith(']')) {
                      try {
                        const parsed = JSON.parse(firstService.scope);
                        scopeValue = Array.isArray(parsed) ? parsed.join(', ') : firstService.scope;
                      } catch (e) {
                        scopeValue = firstService.scope;
                      }
                    } else {
                      scopeValue = firstService.scope || '';
                    }
                  }
                }
                
                setProposalData(prev => ({
                  ...prev,
                  companyName: leadData.companyName || '',
                  scope: scopeValue, // ✅ SCOPE: Set scope from lead_services (not from proposal entity - scope belongs to service, not proposal)
                }));
                
                // ✅ FIX: No proposal record exists yet here (404 fallback branch —
                // fresh "Convert to Proposal" from a Lead, no localStorage data either).
                // `proposal` is `const`-scoped to the sibling success branch, so reading
                // `proposal.displayCurrencyCode` threw `ReferenceError: proposal is not
                // defined` -> ErrorBoundary crash on first navigation (worked after
                // refresh once the proposal existed). `proposalCurrencyCode` defaults to 'IDR'.
              }
            }
          }
        } else {
          // Tangani error lain
          setAlert({ type: 'danger', message: 'Failed to fetch data.' });
        }
      }
    };

    if (id && !dataFetched) fetchData(); // TAMBAHAN: Cek flag
  }, [id, dataFetched]); // TAMBAHAN: Tambahkan dataFetched ke dependency

  // ✅ Self-healing version-options fetch: covers any load path (including the
  // post-conversion redirect) that populates proposalServices with a standard
  // id whose versions haven't been fetched yet, instead of relying on a single
  // fire-and-forget fetch inside fetchData.
  useEffect(() => {
    const missingStandardIds = new Set();
    proposalServices.forEach(section => {
      (section.standards || []).forEach(std => {
        if (std.standard && !requestedVersionStandardIdsRef.current.has(std.standard)) {
          missingStandardIds.add(std.standard);
        }
      });
    });

    if (missingStandardIds.size === 0) return;

    missingStandardIds.forEach(standardId => {
      requestedVersionStandardIdsRef.current.add(standardId);
      api.get(`/md/standards-versions/${standardId}`)
        .then(versionsRes => {
          const versions = (versionsRes.data || []).map(v => ({
            value: v.id,
            label: v.version || v.name || `Version ${v.id}`
          }));
          setVersionOptionsByStandard(prev => ({
            ...prev,
            [standardId]: versions
          }));
        })
        .catch(error => {
          console.warn(`Could not fetch versions for standard ${standardId}:`, error);
        });
    });
  }, [proposalServices]);

  useEffect(() => {
    if (productTypes.length) {
      if (leadProducts.length === 0) {
        setProposalData(prev => ({
          ...prev,
          serviceType: '',
          services: [],
        }));
      } else {
        const mainProduct = leadProducts[0];
        const additional = leadProducts.slice(1);
        setProposalData(prev => ({
          ...prev,
          serviceType: String(mainProduct?.serviceId ?? ''),
          services: [], // or map as needed
        }));
      }
    }
  }, [leadProducts, productTypes]);

  useEffect(() => {
    setProposalData(prev => ({
      ...prev,
      leadId: id
    }));
  }, [id]);

  // Fetch standards and categories for each section when service changes
  useEffect(() => {
    
    proposalServices.forEach((section, idx) => {
      if (section.service) {
        // Fetch standards
        api.get(`/md/standards/by-service/${section.service}`)
          .then(res => {
            setStandardOptionsBySection(prev => ({
              ...prev,
              [idx]: res.data.map(item => ({ id: item.value || item.id, name: item.label || item.name }))
            }));
          })
          .catch(() => {
            setStandardOptionsBySection(prev => ({ ...prev, [idx]: [] }));
          });
        // Fetch product categories
        api.get(`/md/product-category/by-service/${section.service}`)
          .then(res => {
            setCategoryOptionsBySection(prev => ({
              ...prev,
              [idx]: res.data.map(item => ({ id: item.id, name: item.name, activityType: item.activityType, yearOption: item.yearOption }))
            }));
          })
          .catch(() => {
            setCategoryOptionsBySection(prev => ({ ...prev, [idx]: [] }));
          });
      } else {
        setStandardOptionsBySection(prev => ({ ...prev, [idx]: [] }));
        setCategoryOptionsBySection(prev => ({ ...prev, [idx]: [] }));
      }
    });
  }, [proposalServices.map(s => s.service).join(',')]);

  // Fetch items for each row and sub-row when product category changes
  useEffect(() => {
    proposalServices.forEach((section, sectionIdx) => {
      // Main rows
      section.standards.forEach((standard, standardIdx) => {
        if (section.service && standard.productCategory) {
          api.get('/md/audit-type/by-service-and-category', {
            params: {
              serviceId: section.service,
              productCategory: standard.productCategory
            }
          })
            .then(res => {
              setItemOptionsBySection(prev => ({
                ...prev,
                [`${sectionIdx}_${standardIdx}`]: res.data.map(item => ({ id: item.id, name: item.name, activityType: item.activityType, yearOption: item.yearOption }))
              }));
            })
            .catch(() => {
              setItemOptionsBySection(prev => ({ ...prev, [`${sectionIdx}_${standardIdx}`]: [] }));
            });
        } else {
          setItemOptionsBySection(prev => ({ ...prev, [`${sectionIdx}_${standardIdx}`]: [] }));
        }
        // Sub-rows (children)
        if (standard.productCategories && Array.isArray(standard.productCategories)) {
          standard.productCategories.forEach((productCategory, productCategoryIdx) => {
            if (section.service && productCategory.productCategory) {
              api.get('/md/audit-type/by-service-and-category', {
                params: {
                  serviceId: section.service,
                  productCategory: productCategory.productCategory
                }
              })
                .then(res => {
                  setItemOptionsBySection(prev => ({
                    ...prev,
                    [`sub_${sectionIdx}_${standardIdx}_${productCategoryIdx}`]: res.data.map(item => ({ id: item.id, name: item.name, activityType: item.activityType, yearOption: item.yearOption }))
                  }));
                })
                .catch(() => {
                  setItemOptionsBySection(prev => ({ ...prev, [`sub_${sectionIdx}_${standardIdx}_${productCategoryIdx}`]: [] }));
                });
            } else {
              setItemOptionsBySection(prev => ({ ...prev, [`sub_${sectionIdx}_${standardIdx}_${productCategoryIdx}`]: [] }));
            }
          });
        }
      });
    });
  }, [proposalServices.map(section => section.standards.map(standard =>
    `${standard.productCategory || ''}:${standard.serviceItem || ''}:${standard.productCategories
      ? standard.productCategories.map(pc => `${pc.productCategory || ''}:${pc.serviceItem || ''}`).join(',')
      : ''}`
  ).join(',')).join('|'), proposalServices.map(s => s.service).join(',')]);

  // Calculate investment breakdown based on selected year options from the table
  // This function creates a dynamic investment breakdown that uses the yearType and selectedYear
  // from the table instead of fetching from the database
  const handleCalculateBreakdown = () => {
    setCalculating(true);
    
    // Check if necessary options are loaded
    const hasOptions = proposalServices.every((section, sectionIdx) => {
      if (!section.service) return true; // Skip if no service selected
      
      // Check if standards are loaded for this section
      const hasStandards = standardOptionsBySection[sectionIdx] && standardOptionsBySection[sectionIdx].length > 0;
      
      // Check if service items are loaded for each standard
      const hasServiceItems = section.standards.every((standard, standardIdx) => {
        if (!standard.serviceItem) return true; // Skip if no service item selected
        return itemOptionsBySection[`${sectionIdx}_${standardIdx}`] && itemOptionsBySection[`${sectionIdx}_${standardIdx}`].length > 0;
      });
      
      return hasStandards && hasServiceItems;
    });
    
    if (!hasOptions) {
      setCalculating(false);
      return;
    }
    
    let allYears = new Set();
    let breakdown = [];
    let totalsByYear = {};

    // Helper function to get years based on selected year options from table
    const getYearsForSelectedOption = (yearType, selectedYear, dynamicYearRange = null) => {
      if (yearType === 0) {
        // Every year - create a continuous range from min to max year based on Select Year items
        let years = new Set();
        
        if (dynamicYearRange && dynamicYearRange.length > 0) {
          // For Every Year items, only use the years where Select Year items exist
          dynamicYearRange.forEach(year => {
            years.add(year);
          });
        } else {
          // Fallback to 5 years from current year if no Select Year items available
          const currentYear = new Date().getFullYear();
          for (let i = 0; i <= 4; i++) {
            years.add(currentYear + i);
          }
        }
        
        // Convert back to sorted array
        return Array.from(years).sort((a, b) => a - b);
      } else if (yearType === 1) {
        // Select Year - use the selectedYear from the table
        return [selectedYear || new Date().getFullYear()];
      } else {
        // Default fallback - calculate for 4 years from current year
        const currentYear = new Date().getFullYear();
        return [currentYear, currentYear + 1, currentYear + 2, currentYear + 3];
      }
    };

    let totalServiceItemsInCalculator = 0;

    // First pass: collect all years from non-every-year service items to determine dynamic range
    let dynamicYearRange = new Set();
    let hasSelectYearItems = false;
    
    proposalServices.forEach((section, sectionIdx) => {
      (section.standards || []).forEach((standard, standardIdx) => {
        // Main row: service item (if present)
        if (standard.serviceItem) {
          const yearType = standard.yearType || 0;
          const selectedYear = standard.selectedYear || new Date().getFullYear();
          
          if (yearType !== 0) { // Collect years from non-every-year items
            hasSelectYearItems = true;
            const itemYears = getYearsForSelectedOption(yearType, selectedYear);
            itemYears.forEach(y => dynamicYearRange.add(y));
          }
        }

        // Children rows: service items in productCategories
        if (standard.productCategories && Array.isArray(standard.productCategories)) {
          standard.productCategories.forEach((pc, productCategoryIdx) => {
            if (pc.serviceItem) {
              const yearType = pc.yearType || 0;
              const selectedYear = pc.selectedYear || new Date().getFullYear();
              
              if (yearType !== 0) { // Collect years from non-every-year items
                hasSelectYearItems = true;
                const itemYears = getYearsForSelectedOption(yearType, selectedYear);
                itemYears.forEach(y => dynamicYearRange.add(y));
              }
            }
          });
        }
      });
    });

    // If no Select Year items found, use current year as fallback
    if (!hasSelectYearItems) {
      dynamicYearRange.add(new Date().getFullYear());
    }

    // Convert dynamic year range to sorted array
    const dynamicYearArray = Array.from(dynamicYearRange).sort((a, b) => a - b);

    // Second pass: process all service items with the dynamic range
    proposalServices.forEach((section, sectionIdx) => {
      const serviceLabel = serviceOptions.find(opt => String(opt.id) === String(section.service))?.name || `Service ${section.service}`;
      let standardsArr = [];
      
      (section.standards || []).forEach((standard, standardIdx) => {
        // Fix standard name resolution
        const standardOptions = standardOptionsBySection[sectionIdx] || [];
        const standardOption = standardOptions.find(opt => String(opt.id) === String(standard.standard));
        const standardLabel = standard.standard
          ? (standardOption?.name || `Standard ${standard.standard}`)
          : 'Standard';
        
        let serviceItems = [];
        // ✅ PERBAIKAN: Set untuk mencegah duplikasi service items
        const addedServiceItems = new Set();

        // Main row: service item (if present)
        if (standard.serviceItem) {
          // Fix service item name resolution
          const itemOptions = itemOptionsBySection[`${sectionIdx}_${standardIdx}`] || [];
          const itemOption = itemOptions.find(opt => String(opt.id) === String(standard.serviceItem));
          const itemLabel = itemOption?.name || `Service Item ${standard.serviceItem}`;

          // ✅ PERBAIKAN: Cek apakah service item sudah ditambahkan
          const serviceItemKey = `${itemLabel}_${standardLabel}`;
          if (addedServiceItems.has(serviceItemKey)) {
            return; // Skip duplicate
          }
          addedServiceItems.add(serviceItemKey);
          
          const rowCurrencyCode = standard.currencyCode || 'IDR';
          const rawPrice = (standard.unitPrice && standard.qty) ? parseFloat(standard.unitPrice || 0) * parseFloat(standard.qty || 1) : 0;
          const cachedRate = rowCurrencyCode === 'IDR' ? 1 : resolveExchangeRate(rowCurrencyCode);
          const exchangeRate = rowCurrencyCode === 'IDR' ? 1 : (cachedRate > 1 ? cachedRate : (standard.exchangeRate || cachedRate || 1));
          const priceIDR = rowCurrencyCode === 'IDR' ? rawPrice : rawPrice * exchangeRate;

          // Get year option from table selection
          const yearType = standard.yearType || 0;
          const selectedYear = standard.selectedYear || new Date().getFullYear();
          const itemYears = getYearsForSelectedOption(yearType, selectedYear, dynamicYearArray);

          // Add years to allYears set
          itemYears.forEach(y => allYears.add(y));

          // Totals always in IDR
          itemYears.forEach(year => { totalsByYear[year] = (totalsByYear[year] || 0) + priceIDR; });

          serviceItems.push({
            serviceItem: itemLabel,
            prices: [priceIDR],
            priceDisplay: rawPrice,
            unitPrice: parseFloat(standard.unitPrice || 0),
            qty: parseFloat(standard.qty || 1),
            currencyCode: rowCurrencyCode,
            exchangeRate: exchangeRate,
            years: itemYears,
            yearType: yearType,
            selectedYear: selectedYear
          });
          totalServiceItemsInCalculator++;
        }

        // Children rows: service items in productCategories
        if (standard.productCategories && Array.isArray(standard.productCategories)) {
          standard.productCategories.forEach((pc, productCategoryIdx) => {
            if (pc.serviceItem) {
              // Fix service item name resolution for product categories
              const itemOptions = itemOptionsBySection[`sub_${sectionIdx}_${standardIdx}_${productCategoryIdx}`] || [];
              const itemOption = itemOptions.find(opt => String(opt.id) === String(pc.serviceItem));
              const itemLabel = itemOption?.name || `Service Item ${pc.serviceItem}`;

              // ✅ PERBAIKAN: Cek apakah service item sudah ditambahkan
              const serviceItemKey = `${itemLabel}_${standardLabel}`;
              if (addedServiceItems.has(serviceItemKey)) {
                return; // Skip duplicate
              }
              addedServiceItems.add(serviceItemKey);
              
              const childCurrencyCode = pc.currencyCode || 'IDR';
              const childRawPrice = (pc.unitPrice && pc.qty) ? parseFloat(pc.unitPrice || 0) * parseFloat(pc.qty || 1) : 0;
              const childCachedRate = childCurrencyCode === 'IDR' ? 1 : resolveExchangeRate(childCurrencyCode);
              const childExchangeRate = childCurrencyCode === 'IDR' ? 1 : (childCachedRate > 1 ? childCachedRate : (pc.exchangeRate || childCachedRate || 1));
              const childPriceIDR = childCurrencyCode === 'IDR' ? childRawPrice : childRawPrice * childExchangeRate;

              // Get year option from table selection
              const yearType = pc.yearType || 0;
              const selectedYear = pc.selectedYear || new Date().getFullYear();
              const itemYears = getYearsForSelectedOption(yearType, selectedYear, dynamicYearArray);

              // Add years to allYears set
              itemYears.forEach(y => allYears.add(y));

              // Totals always in IDR
              itemYears.forEach(year => { totalsByYear[year] = (totalsByYear[year] || 0) + childPriceIDR; });

              serviceItems.push({
                serviceItem: itemLabel,
                prices: [childPriceIDR],
                priceDisplay: childRawPrice,
                unitPrice: parseFloat(pc.unitPrice || 0),
                qty: parseFloat(pc.qty || 1),
                currencyCode: childCurrencyCode,
                exchangeRate: childExchangeRate,
                years: itemYears,
                yearType: yearType,
                selectedYear: selectedYear
              });
              totalServiceItemsInCalculator++;
            }
          });
        }
        standardsArr.push({ standard: standardLabel, serviceItems });
      });
      breakdown.push({ service: serviceLabel, standards: standardsArr });
    });
    
    // Sort years in ascending order and only include years that have actual data
    const sortedYears = Array.from(allYears).sort((a, b) => a - b);
    
    // Always show breakdown section, but only set data if we have years
    if (sortedYears.length > 0) {
      setInvestmentBreakdown({ 
        years: sortedYears, 
        breakdown, 
        totals: sortedYears.map(y => totalsByYear[y] || 0) 
      });
    } else {
      // If no years found, clear the breakdown data but keep section visible
      setInvestmentBreakdown(null);
    }
    
    setCalculating(false);
  };



  // Signal that only changes when breakdown-relevant fields change.
  // Excludes unitPriceBaseCurrency / workflowId updates so their async callbacks
  // don't keep resetting the debounce timer.
  const breakdownPriceSignal = React.useMemo(() =>
    proposalServices.map(s =>
      (s.standards || []).map(std =>
        [std.serviceItem, std.unitPrice, std.qty, std.currencyCode, std.exchangeRate, std.yearType, std.selectedYear,
          (std.productCategories || []).map(pc =>
            [pc.serviceItem, pc.unitPrice, pc.qty, pc.currencyCode, pc.exchangeRate, pc.yearType, pc.selectedYear].join(':')
          ).join('|')
        ].join(':')
      ).join(';')
    ).join('###'),
  [proposalServices]);

  // ✅ PERBAIKAN: Hanya satu useEffect untuk menghitung breakdown
  React.useEffect(() => {
    if (dataFetched && proposalServices.length > 0 && !loading &&
        Object.keys(standardOptionsBySection).length > 0 &&
        Object.keys(itemOptionsBySection).length > 0) {
      const timer = setTimeout(() => {
        handleCalculateBreakdown();
      }, 400);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line
  }, [dataFetched, breakdownPriceSignal, loading, standardOptionsBySection, itemOptionsBySection, currencyRates]);

  const handleServiceTemplateChange = (value) => {
    setProposalData(prev => ({ ...prev, serviceTemplateId: value }));
    
    // Reset proposal template when service template changes
    setProposalData(prev => ({ ...prev, proposalTemplateId: '' }));
    
    // Fetch proposal templates for the selected service
    fetchProposalTemplatesByService(value);
  };

  const handleProposalTemplateChange = (value) => {
    setProposalData(prev => ({ ...prev, proposalTemplateId: value }));
  };

  // Fetch proposal templates when service template changes
  useEffect(() => {
    if (proposalData.serviceTemplateId) {
      fetchProposalTemplatesByService(proposalData.serviceTemplateId);
    } else {
      setProposalTemplateOptions([]);
    }
  }, [proposalData.serviceTemplateId]);

  const fetchMaxPrice = async (sectionIdx, standardIdx, isSubRow = false, productCategoryIdx = null) => {
    let section = proposalServices[sectionIdx];
    let standard = section.standards[standardIdx];
    let params = {
      serviceId: section.service,
      productCategoryId: isSubRow
        ? standard.productCategories?.[productCategoryIdx]?.productCategory
        : standard.productCategory,
      standardId: standard.standard,
      serviceItemId: isSubRow
        ? standard.productCategories?.[productCategoryIdx]?.serviceItem
        : standard.serviceItem,
      unitTypeId: isSubRow
        ? standard.productCategories?.[productCategoryIdx]?.unit
        : standard.unit,
    };

    // ✅ MULTI-CURRENCY: Tambahkan companyId untuk konversi currency
    // Ambil companyId dari leadData jika tersedia
    if (leadData && leadData.companyId) {
      params.companyId = leadData.companyId;
    } else if (proposalData.leadId) {
      try {
        const leadResponse = await api.get(`/crm/leads/${proposalData.leadId}`);
        if (leadResponse.data?.companyId) {
          params.companyId = leadResponse.data.companyId;
        }
      } catch (error) {
        console.warn('Could not fetch lead data for companyId:', error);
      }
    }

    // ✅ FIX: Tambahkan assigned user ID untuk filter rate management berdasarkan workflow assigned user
    // Get assigned user ID from leadData (assignedTo.id or assignedUserId)
    if (leadData) {
      const assignedUserId = leadData.assignedTo?.id || leadData.assignedUserId;
      if (assignedUserId) {
        params.assignedUserId = assignedUserId;
      }
    } else if (proposalData.leadId) {
      try {
        const leadResponse = await api.get(`/crm/leads/${proposalData.leadId}`);
        const assignedUserId = leadResponse.data?.assignedTo?.id || leadResponse.data?.assignedUserId;
        if (assignedUserId) {
          params.assignedUserId = assignedUserId;
        }
      } catch (error) {
        console.warn('Could not fetch lead data for assignedUserId:', error);
      }
    }

    // Hapus undefined/null agar query string rapi
    Object.keys(params).forEach(key => {
      if (!params[key]) delete params[key];
    });
    
    try {
      const res = await api.get('/crm/rates/max-price', { params });
      const { rateTo, workFlowId, unitTypeId, originalRate } = res.data || {};
      
      // ✅ INFORMASI KE USER JIKA RATE TIDAK ADA / 0
      if (!rateTo || Number(rateTo) <= 0) {
        // Set unit price ke 0 pada baris terkait
        setProposalServices(prev => {
          const updated = [...prev];
          if (isSubRow) {
            if (updated[sectionIdx]?.standards?.[standardIdx]?.productCategories?.[productCategoryIdx]) {
              updated[sectionIdx].standards[standardIdx].productCategories[productCategoryIdx].unitPrice = 0;
            }
          } else {
            if (updated[sectionIdx]?.standards?.[standardIdx]) {
              updated[sectionIdx].standards[standardIdx].unitPrice = 0;
            }
          }
          return updated;
        });
        
        setShowRateUndefinedModal(true);
        return;
      }
      
      
      // Update langsung ke unitPrice, workFlowId, dan unit
      setProposalServices(prev => {
        const updated = [...prev];
        if (isSubRow) {
          updated[sectionIdx].standards[standardIdx].productCategories[productCategoryIdx].unitPrice = rateTo;
          updated[sectionIdx].standards[standardIdx].productCategories[productCategoryIdx].workFlowIdSystem = workFlowId;
          // ✅ Simpan original suggested price (display & base currency) agar tidak terpengaruh edit user
          updated[sectionIdx].standards[standardIdx].productCategories[productCategoryIdx].fetchMaxPrice = rateTo;
          
          // ✅ PERBAIKAN: Simpan nilai asli dalam base currency untuk menghindari precision loss
          if (originalRate) {
            updated[sectionIdx].standards[standardIdx].productCategories[productCategoryIdx].unitPriceBaseCurrency = originalRate;
            updated[sectionIdx].standards[standardIdx].productCategories[productCategoryIdx].fetchMaxPriceBaseCurrency = originalRate;
          }
          
          // TAMBAHAN: Set unit secara otomatis jika unitTypeId tersedia
          if (unitTypeId) {
            updated[sectionIdx].standards[standardIdx].productCategories[productCategoryIdx].unit = unitTypeId;
          }
          
          // Jika workFlowId ada datanya dan bukan null, set workFlowIdChoice sama dengan workFlowId
          if (workFlowId && workFlowId !== null) {
            updated[sectionIdx].standards[standardIdx].productCategories[productCategoryIdx].workFlowIdChoice = workFlowId;
          }
        } else {
          updated[sectionIdx].standards[standardIdx].unitPrice = rateTo;
          updated[sectionIdx].standards[standardIdx].workFlowIdSystem = workFlowId;
          // ✅ Simpan original suggested price (display & base currency) agar tidak terpengaruh edit user
          updated[sectionIdx].standards[standardIdx].fetchMaxPrice = rateTo;
          
          // ✅ PERBAIKAN: Simpan nilai asli dalam base currency untuk menghindari precision loss
          if (originalRate) {
            updated[sectionIdx].standards[standardIdx].unitPriceBaseCurrency = originalRate;
            updated[sectionIdx].standards[standardIdx].fetchMaxPriceBaseCurrency = originalRate;
          }
          
          // TAMBAHAN: Set unit secara otomatis jika unitTypeId tersedia
          if (unitTypeId) {
            updated[sectionIdx].standards[standardIdx].unit = unitTypeId;
          }
          
          // Jika workFlowId ada datanya dan bukan null, set workFlowIdChoice sama dengan workFlowId
          if (workFlowId && workFlowId !== null) {
            updated[sectionIdx].standards[standardIdx].workFlowIdChoice = workFlowId;
          }
        }
        return updated;
      });
    } catch (err) {
      // Optional: handle error
    }
  };

  // ✅ TAMBAHAN: Fungsi untuk update unitPriceBaseCurrency ketika user mengubah unitPrice secara manual
  const updateUnitPriceBaseCurrency = async (sectionIdx, standardIdx, unitPrice, isSubRow = false, productCategoryIdx = null) => {
    try {
      const row = isSubRow
        ? proposalServices[sectionIdx]?.standards?.[standardIdx]?.productCategories?.[productCategoryIdx]
        : proposalServices[sectionIdx]?.standards?.[standardIdx];
      const rowCurrencyCode = row?.currencyCode || proposalCurrencyCode || 'IDR';
      const rowRate = await getRowExchangeRate(rowCurrencyCode);
      const baseCurrencyAmount = Number(unitPrice || 0) * rowRate;
      
      // Update state dengan unitPriceBaseCurrency
      setProposalServices(prev => {
        const updated = [...prev];
        if (isSubRow) {
          updated[sectionIdx].standards[standardIdx].productCategories[productCategoryIdx].unitPriceBaseCurrency = baseCurrencyAmount;
        } else {
          updated[sectionIdx].standards[standardIdx].unitPriceBaseCurrency = baseCurrencyAmount;
        }
        return updated;
      });
    } catch (error) {
      console.error('Error updating unitPriceBaseCurrency:', error);
    }
  };

  const fetchWorkflowIdByUnitPrice = async (sectionIdx, standardIdx, unitPrice, isSubRow = false, productCategoryIdx = null) => {
    let section = proposalServices[sectionIdx];
    let standard = section.standards[standardIdx];
    let params = {
      serviceId: section.service,
      productCategoryId: isSubRow
        ? standard.productCategories?.[productCategoryIdx]?.productCategory
        : standard.productCategory,
      standardId: standard.standard,
      serviceItemId: isSubRow
        ? standard.productCategories?.[productCategoryIdx]?.serviceItem
        : standard.serviceItem,
      unitTypeId: isSubRow
        ? standard.productCategories?.[productCategoryIdx]?.unit
        : standard.unit,
      unitPrice: unitPrice
    };

    // Tambahkan companyId jika tersedia
    if (leadData && leadData.companyId) {
      params.companyId = leadData.companyId;
    }

    // ✅ FIX: Tambahkan assigned user ID untuk workflow recommendation
    // Get assigned user ID from leadData (assignedTo.id or assignedUserId)
    if (leadData) {
      const assignedUserId = leadData.assignedTo?.id || leadData.assignedUserId;
      if (assignedUserId) {
        params.cuserId = assignedUserId; // Backend expects cuserId but we send assigned user ID
      }
    }

    // Hapus params kosong
    Object.keys(params).forEach(key => {
      if (!params[key] && params[key] !== 0) delete params[key];
    });

    try {
      const res = await api.get('/crm/rates/workflow-by-unit-price', { params });

      // ✅ FIX: Use recommendedWorkflowId if available (for assigned user), otherwise fall back to workFlowId
      const { workFlowId, recommendedWorkflowId } = res.data || {};
      const finalWorkflowId = recommendedWorkflowId || workFlowId;

      return finalWorkflowId;
    } catch (err) {
      console.error('❌ ERROR: Gagal mendapatkan workFlowId berdasarkan unit price:', err);
      return null;
    }
  };


  // PDF conversion functions moved to DetailProposal component

  // ✅ TAMBAHKAN fungsi handleWorkflowModalClose yang hilang
  const handleWorkflowModalClose = () => {
    setShowWorkflowModal(false);
    setWorkflowItems([]);
  };

  // ✅ TAMBAHKAN fungsi handleSendToClient yang hilang
  const handleSendToClient = () => {
    setShowSendToClientModal(true);
  };

  // ✅ TAMBAHKAN fungsi confirmSendToClient yang akan dipanggil dari modal
  const confirmSendToClient = async () => {
    setLoadingWorkflow(true);
    try {
      // Fetch workflow items yang perlu review
      const response = await api.get(`/crm/proposals/${id}/workflow-review`);
      
      // ✅ TAMBAHAN: Cek apakah ada workflow items yang perlu review
      if (!response.data || response.data.length === 0) {
        // Jika tidak ada workflow items, langsung kirim ke client
        await handleConfirmSendToClient();
        return;
      }
      
      // Jika ada workflow items, tampilkan modal
      setWorkflowItems(response.data);
      setShowWorkflowModal(true);
      
    } catch (error) {
      console.error('Error fetching workflow items:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to fetch workflow items for review.'
      });
    } finally {
      setLoadingWorkflow(false);
    }
  };

  // ✅ TAMBAHKAN fungsi getProposalId yang hilang
  const getProposalId = async (leadId) => {
    try {
      const res = await api.get(`/crm/proposals/id-by-lead/${leadId}`);
      const proposalId = typeof res.data === 'object' ? res.data.id : res.data;
      return proposalId;
    } catch (error) {
      console.error('Error getting proposal ID:', error);
      throw new Error('Proposal not found for this lead');
    }
  };

  // PDF creation functionality moved to DetailProposal component

  if (loading) {
    return <Loading />;
  }

  // ✅ HELPER: Log seluruh workflow state sebelum save
  function logCurrentWorkflowState() {
    // Function kept for potential future debugging but no logging
  }

  const getQuotedInvestmentTotalForYear = (year) => (investmentBreakdown?.breakdown || []).reduce((serviceTotal, serviceRow) => (
    serviceTotal + (serviceRow.standards || []).reduce((standardTotal, stdRow) => (
      standardTotal + (stdRow.serviceItems || []).reduce((itemTotal, item) => {
        if (!Array.isArray(item.years) || !item.years.includes(year)) return itemTotal;
        return itemTotal + (Number(item.unitPrice ?? item.priceDisplay ?? 0) * Number(item.qty ?? 1));
      }, 0)
    ), 0)
  ), 0);
  const showConversionColumns = String(proposalCurrencyCode || 'IDR').toUpperCase() !== 'IDR';
  const breakdownBaseColumnCount = showConversionColumns ? 4 : 3;



  return (
    <>
      {loading && <Loading />}
      {alert && (
        <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
      )}
      
      {/* Workflow Review Modal */}
      <Modal
        isOpen={showWorkflowModal}
        onClose={handleWorkflowModalClose}
        title="Workflow Review Required"
        size="xl"
      >
        <div className="modal-body">
          {loadingWorkflow ? (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading workflow items...</p>
            </div>
          ) : (
            <>
              <div className="alert alert-warning">
                <h6 className="alert-heading">⚠️ Workflow Review Required</h6>
                <p className="mb-0">
                  The following service items require workflow review before sending to client:
                </p>
              </div>
              
              {workflowItems.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-vcenter">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th>Standard</th>
                        <th>Product Category</th>
                        <th>Service Item</th>
                        <th>Qty</th>
                        <th>Unit</th>
                        <th>Unit Price</th>
                        <th>Workflow Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workflowItems.map((item, index) => {
                        return (
                          <tr key={index}>
                            <td>{item.serviceName}</td>
                            <td>{item.standardName}</td>
                            <td>{item.productCategoryName}</td>
                            <td>{item.serviceItemName}</td>
                            <td>{item.qty}</td>
                            <td>{item.unit}</td>
                            <td>
                              {item.unitPrice ? 
                                getCurrencyPrefix(proposalCurrencyCode) + item.unitPrice.toLocaleString('id-ID') : 
                                (getCurrencyPrefix(proposalCurrencyCode) + '0')
                              }
                            </td>
                            <td>
                              <span className={`badge badge-${item.workflowStatus === 'pending' ? 'warning' : 'info'}`}>
                                {item.workflowStatus === 'pending' ? 'Pending Review' : 'Needs Approval'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">No workflow items require review.</p>
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={handleWorkflowModalClose}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={handleSetNeedApproval}
            disabled={loadingWorkflow}
          >
            Ok
          </button>
        </div>
      </Modal>

      {/* Rate Undefined Modal */}
      <Modal
        isOpen={showRateUndefinedModal}
        onClose={() => setShowRateUndefinedModal(false)}
        title="Rate Not Defined"
        size="md"
      >
        <div className="modal-body">
          <div className="alert alert-warning d-flex align-items-center" role="alert">
            <div className="alert-icon me-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
                <path d="M12 8v4"></path>
                <path d="M12 16h.01"></path>
              </svg>
            </div>
            <div>
              Audit Type rate is not defined; the recommended unit price cannot be displayed.
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-outline-info"
            onClick={() => setShowRateUndefinedModal(false)}
          >
            OK
          </button>
        </div>
      </Modal>

      {/* Send to Client Confirmation Modal */}
      <ConfirmationModal
        isOpen={showSendToClientModal}
        onClose={() => setShowSendToClientModal(false)}
        onConfirm={confirmSendToClient}
        title="Send to Client"
        message="Are you sure you want to send this proposal to the client?"
        confirmText="Send"
        cancelText="Cancel"
        confirmVariant="primary"
      />

      {/* Download Error Modal */}
      <ConfirmationModal
        isOpen={showDownloadErrorModal}
        onClose={() => setShowDownloadErrorModal(false)}
        onConfirm={() => setShowDownloadErrorModal(false)}
        title="Download Error"
        message={downloadErrorMessage}
        confirmText="OK"
        cancelText=""
        confirmVariant="secondary"
        showCancel={false}
      />

      {/* Commercial Terms Template Modal (Master Data) */}
      <Modal
        isOpen={showPredefinedModal}
        onClose={() => {
          setShowPredefinedModal(false);
          setSelectedPredefinedSet(null);
        }}
        title="Select Commercial Terms Template"
        size="xl"
      >
        <div className="modal-body">
          <div className="alert alert-info">
            <p className="mb-0">
              Select one commercial terms template (from Master Data) to use. You can edit them after adding.
            </p>
          </div>
          <div className="list-group">
            {filteredPredefinedTermsSets.length === 0 ? (
              <div className="list-group-item text-center text-muted">
                No commercial terms templates available for the selected service(s). Add a service to the proposal above, or add templates linked to this service in Master Data → Commercial Terms.
              </div>
            ) : (
              filteredPredefinedTermsSets.map((set) => (
              <div key={set.id ?? set.code} className="list-group-item">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="predefined-set"
                    id={`predefined-set-${set.id ?? set.code}`}
                    checked={selectedPredefinedSet === set.id || selectedPredefinedSet === set.code}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPredefinedSet(set.id ?? set.code);
                      }
                    }}
                  />
                  <label className="form-check-label" htmlFor={`predefined-set-${set.id ?? set.code}`}>
                    <div>
                      <h6 className="mb-1">{set.name}</h6>
                      {set.serviceName && (
                        <small className="d-block text-primary mb-1">Service: {set.serviceName}</small>
                      )}
                      <small className="text-muted">{set.description || '—'}</small>
                      {(set.terms?.length ?? 0) > 0 && (
                        <div className="mt-1">
                          <small className="text-muted">
                            {set.terms.reduce((total, term) => {
                              if (term?.isSection) {
                                return total + (term.pointsWithOptions?.length ?? term.points?.length ?? 0);
                              }
                              return total + 1;
                            }, 0)} point(s) across {set.terms.length} section(s)
                          </small>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </div>
            ))
          )}
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setShowPredefinedModal(false);
              setSelectedPredefinedSet(null);
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!selectedPredefinedSet}
            onClick={async () => {
              try {
                if (!selectedPredefinedSet) {
                  return;
                }
                // Fetch full template with terms from Master Data (numeric id) or CRM fallback (code string)
                const isNumericId = /^\d+$/.test(String(selectedPredefinedSet));
                const url = isNumericId
                  ? `/md/commercial-terms/templates/${selectedPredefinedSet}`
                  : `/crm/proposals/commercial-terms/predefined/${selectedPredefinedSet}`;
                const res = await api.get(url);

                if (!res.data || !res.data.terms || res.data.terms.length === 0) {
                  setAlert({
                    type: 'warning',
                    message: 'No terms found in selected template.'
                  });
                  return;
                }

                setCommercialTerms(res.data.terms);
                setCommercialTermsPresetId(selectedPredefinedSet);
                setShowPredefinedModal(false);
                setSelectedPredefinedSet(null);
                setAlert({
                  type: 'success',
                  message: `Loaded ${res.data.terms.length} terms from selected template. You can now edit them as needed.`
                });
              } catch (error) {
                console.error('Error loading commercial terms template:', error);
                setAlert({
                  type: 'danger',
                  message: 'Failed to load commercial terms template.'
                });
              }
            }}
          >
            Use Selected Template
          </button>
        </div>
      </Modal>

      <div className="container-xl" style={{ overflowX: 'hidden', maxWidth: '100%', overflowAnchor: 'none', scrollBehavior: 'auto' }}>
        <ProposalHeader
          readOnly={readOnly}
          companyName={proposalData.companyName}
          proposalCode={proposalData.proposalCode}
          handleBack={() => window.history.length > 1 ? navigate(-1) : navigate('/proposals')}
          proposalDate={proposalData.proposalDate}
          validUntil={proposalData.validUntil}
          handleDateChange={(e) =>
            setProposalData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
          }
          onServiceTemplateChange={handleServiceTemplateChange}
          serviceTemplateId={proposalData.serviceTemplateId}
          proposalTemplateId={proposalData.proposalTemplateId}
          onProposalTemplateChange={handleProposalTemplateChange}
          proposalTemplateOptions={proposalTemplateOptions}
          scope={displayScope} // ✅ ISCC SCOPE: Pass converted scope to ProposalHeader
          onScopeChange={(value) => // ✅ ISCC SCOPE: Handle scope change
            setProposalData((prev) => ({ ...prev, scope: value }))
          }
          isccScopeOptions={isccScopeOptions} // ✅ ISCC SCOPE: Pass ISCC scope options
          forceIsccMode={isTemplateIsccOrSustainability} // ✅ ISCC SCOPE: Force ISCC mode only when the template is actually ISCC/Sustainability
          accreditationIds={proposalData.accreditationIds} // ✅ ACCREDITATION: Pass array
          onAccreditationChange={(values) => // ✅ ACCREDITATION: Handle accreditation change (values is array)
            setProposalData((prev) => ({ ...prev, accreditationIds: values || [] }))
          }
          accreditationOptions={accreditationOptions} // ✅ TAMBAHAN: Pass accreditation options
          bankId={proposalData.bankId} // ✅ BANK: Pass bank ID
          onBankChange={(value) => // ✅ BANK: Handle bank change
            setProposalData((prev) => ({ ...prev, bankId: value }))
          }
          bankOptions={bankOptions} // ✅ BANK: Pass bank options
          projectName={proposalData.projectName} // ✅ PROJECT NAME: Pass project name
          onProjectNameChange={(value) => // ✅ PROJECT NAME: Handle project name change
            setProposalData((prev) => ({ ...prev, projectName: value }))
          }
          signatureName={proposalData.signatureName} // ✅ SIGNATURE NAME: Pass signature name
          onSignatureNameChange={(value) => // ✅ SIGNATURE NAME: Handle signature name change
            setProposalData((prev) => ({ ...prev, signatureName: value }))
          }
          signaturePosition={proposalData.signaturePosition} // ✅ SIGNATURE POSITION: Pass signature position
          onSignaturePositionChange={(value) => // ✅ SIGNATURE POSITION: Handle signature position change
            setProposalData((prev) => ({ ...prev, signaturePosition: value }))
          }
          trainingDeliveryMethod={proposalData.trainingDeliveryMethod} // ✅ TRAINING DELIVERY: Pass training delivery method
          onTrainingDeliveryMethodChange={(value) => // ✅ TRAINING DELIVERY: Handle training delivery method change
            setProposalData((prev) => ({ ...prev, trainingDeliveryMethod: value }))
          }
          // ✅ ISCC-SPECIFIC FIELDS
          turnover={proposalData.turnover}
          onTurnoverChange={(value) =>
            setProposalData((prev) => ({ ...prev, turnover: value }))
          }
          registrationNumber={proposalData.registrationNumber}
          onRegistrationNumberChange={(value) =>
            setProposalData((prev) => ({ ...prev, registrationNumber: value }))
          }
          facilitiesAndAddress={proposalData.facilitiesAndAddress}
          onFacilitiesAndAddressChange={(value) =>
            setProposalData((prev) => ({ ...prev, facilitiesAndAddress: value }))
          }
          materialInputIds={proposalData.materialInputIds}
          onMaterialInputsChange={(values) =>
            setProposalData((prev) => ({ ...prev, materialInputIds: values || [] }))
          }
          materialOutputIds={proposalData.materialOutputIds}
          onMaterialOutputsChange={(values) =>
            setProposalData((prev) => ({ ...prev, materialOutputIds: values || [] }))
          }
          ghgCalculation={proposalData.ghgCalculation}
          onGhgCalculationChange={(value) =>
            setProposalData((prev) => ({ ...prev, ghgCalculation: value }))
          }

          // ✅ CARBON PROJECT & GHG FIELDS - Shared fields
          objective={proposalData.objective}
          onObjectiveChange={(value) => setProposalData(prev => ({ ...prev, objective: value }))}
          sector={proposalData.sector}
          onSectorChange={(value) => setProposalData(prev => ({ ...prev, sector: value }))}
          companyId={leadData?.companyId} // ✅ Pass company ID for sector updates
          materiality={proposalData.materiality}
          onMaterialityChange={(value) => setProposalData(prev => ({ ...prev, materiality: value }))}
          numberOfSites={proposalData.numberOfSites}
          onNumberOfSitesChange={(value) => setProposalData(prev => ({ ...prev, numberOfSites: value }))}
          numberOfFacilities={proposalData.numberOfFacilities}
          onNumberOfFacilitiesChange={(value) => setProposalData(prev => ({ ...prev, numberOfFacilities: value }))}
          siteAddress={proposalData.siteAddress}
          onSiteAddressChange={(value) => setProposalData(prev => ({ ...prev, siteAddress: value }))}
          facilityAddress={proposalData.facilityAddress}
          onFacilityAddressChange={(value) => setProposalData(prev => ({ ...prev, facilityAddress: value }))}

          // Carbon Project specific
          methodology={proposalData.methodology}
          onMethodologyChange={(value) => setProposalData(prev => ({ ...prev, methodology: value }))}
          typeOfEmissionsRemovals={proposalData.typeOfEmissionsRemovals}
          onTypeOfEmissionsRemovalsChange={(value) => setProposalData(prev => ({ ...prev, typeOfEmissionsRemovals: value }))}
          ghgMonitoringPeriod={proposalData.ghgMonitoringPeriod}
          onGhgMonitoringPeriodChange={(value) => setProposalData(prev => ({ ...prev, ghgMonitoringPeriod: value }))}
          ghgEmissionReductions={proposalData.ghgEmissionReductions}
          onGhgEmissionReductionsChange={(value) => setProposalData(prev => ({ ...prev, ghgEmissionReductions: value }))}

          // GHG specific
          organizationActivities={proposalData.organizationActivities}
          onOrganizationActivitiesChange={(value) => setProposalData(prev => ({ ...prev, organizationActivities: value }))}
          reportingBoundaries={proposalData.reportingBoundaries}
          onReportingBoundariesChange={(value) => setProposalData(prev => ({ ...prev, reportingBoundaries: value }))}
          claim={proposalData.claim}
          onClaimChange={(value) => setProposalData(prev => ({ ...prev, claim: value }))}
          levelOfAssurance={proposalData.levelOfAssurance}
          onLevelOfAssuranceChange={(value) => setProposalData(prev => ({ ...prev, levelOfAssurance: value }))}
          verificationLocation={proposalData.verificationLocation}
          onVerificationLocationChange={(value) => setProposalData(prev => ({ ...prev, verificationLocation: value }))}
        />
        {/* Render service sections */}
        <div style={readOnly ? { pointerEvents: 'none', opacity: 0.85 } : undefined}>
        {proposalServices.map((section, idx) => (
          <div key={section.id} className="card mb-4 position-relative" style={{ overflow: 'hidden' }}>
            {!readOnly && (
              <button
                type="button"
                className="btn btn-icon btn-danger position-absolute end-0 top-0 m-2"
                aria-label="Delete Section"
                onClick={() => handleDeleteSection(idx)}
                style={{ zIndex: 2 }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-x"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M18 6l-12 12" /><path d="M6 6l12 12" /></svg>
              </button>
            )}
            <div className="card-body position-relative" style={{ overflow: 'hidden', padding: '1.5rem', overflowAnchor: 'none' }}>
              <div className="mb-3">
                <label className="form-label">Service</label>
                <div style={{ maxWidth: '300px' }}>
                  <AppSelect
                    name={`service-${section.id}`}
                    value={section.service}
                    onChange={(name, value) => {
                      const updatedSections = [...proposalServices];
                      updatedSections[idx].service = value;
                      setProposalServices(updatedSections);
                    }}
                    options={serviceOptions}
                    placeholder="Select Service"
                    className="mb-2"
                    dropdownParent="body"
                  />
                </div>
              </div>
              <div className="table-responsive" style={{ overflowX: 'auto', overflowY: 'visible', width: '100%', overflowAnchor: 'none', scrollBehavior: 'auto' }}>
                <table className="table" style={{ width: '100%', tableLayout: 'auto', marginBottom: 0 }}>
                  <thead>
                    <tr>
                      <th className="text-nowrap" style={{ minWidth: 180 }}>Standard</th>
                      <th className="text-nowrap" style={{ minWidth: 100 }}>Version</th>
                      <th className="text-nowrap" style={{ minWidth: 180 }}>Product Category</th>
                      <th className="text-nowrap" style={{ minWidth: 180 }}>Service Item</th>
                      <th className="text-nowrap" style={{ minWidth: 140 }}>Year Type</th>
                      <th className="text-nowrap" style={{ minWidth: 100 }}>Year</th>
                      <th className="text-nowrap">Qty</th>
                      <th className="text-nowrap">Unit</th>
                      <th className="text-nowrap">Currency</th>
                      {isTrainingService(section.service) && <th className="text-nowrap">NoP</th>}
                      <th className="text-nowrap">Unit Price</th>
                      <th className="text-nowrap text-center" style={{ width: 72, minWidth: 72 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.standards.map((standard, standardIdx) => (
                      <React.Fragment key={standardIdx}>
                        <tr>
                          <td style={{ minWidth: 150 }}>
                            <AppSelect
                              name={`standard-${section.id}-${standardIdx}`}
                              value={standard.standard}
                              onChange={async (name, value) => {
                                const updatedSections = [...proposalServices];
                                updatedSections[idx].standards[standardIdx].standard = value;
                                // ✅ TAMBAHAN: Reset version when standard changes
                                updatedSections[idx].standards[standardIdx].standardVersionId = '';
                                setProposalServices(updatedSections);
                                
                                // ✅ TAMBAHAN: Fetch versions for selected standard
                                if (value) {
                                  try {
                                    const versionsRes = await api.get(`/md/standards-versions/${value}`);
                                    const versions = (versionsRes.data || []).map(v => ({
                                      value: v.id,
                                      label: v.version || v.name || `Version ${v.id}`
                                    }));
                                    setVersionOptionsByStandard(prev => ({
                                      ...prev,
                                      [value]: versions
                                    }));
                                  } catch (error) {
                                    console.warn('Could not fetch standard versions:', error);
                                    setVersionOptionsByStandard(prev => ({
                                      ...prev,
                                      [value]: []
                                    }));
                                  }
                                }
                              }}
                              options={standardOptionsBySection[idx] || []}
                              placeholder="Select Standard"
                              dropdownParent="body"
                            />
                          </td>
                          {/* ✅ TAMBAHAN: Version select field */}
                          <td style={{ minWidth: 120 }}>
                            <AppSelect
                              key={`version-${section.id}-${standardIdx}-${standard.standard ? (versionOptionsByStandard[standard.standard]?.length || 0) : 0}`}
                              name={`version-${section.id}-${standardIdx}`}
                              value={standard.standardVersionId || ''}
                              onChange={(name, value) => {
                                const updatedSections = [...proposalServices];
                                updatedSections[idx].standards[standardIdx].standardVersionId = value;
                                setProposalServices(updatedSections);
                              }}
                              options={standard.standard ? (versionOptionsByStandard[standard.standard] || []) : []}
                              placeholder="Select Version"
                              dropdownParent="body"
                              disabled={!standard.standard}
                              valueField="value"
                              labelField="label"
                            />
                          </td>
                          <td style={{ minWidth: 180 }}>
                            <AppSelect
                              name={`product-category-${section.id}-${standardIdx}`}
                              value={standard.productCategory}
                              onChange={(name, value) => {
                                const updatedSections = [...proposalServices];
                                updatedSections[idx].standards[standardIdx].productCategory = value;
                                const forcedYearType = getForcedProductCategoryYearType(
                                  idx, standardIdx, null, value, standard.serviceItem
                                );
                                if (forcedYearType !== null) {
                                  updatedSections[idx].standards[standardIdx].yearType = forcedYearType;
                                }
                                setProposalServices(updatedSections);
                              }}
                              options={categoryOptionsBySection[idx] || []}
                              placeholder="Select Product Category"
                              dropdownParent="body"
                            />
                          </td>
                          <td style={{ minWidth: 180 }}>
                            <AppSelect
                              name={`item-${section.id}-${standardIdx}`}
                              value={standard.serviceItem}
                              onChange={(name, value) => {
                                const updatedSections = [...proposalServices];
                                updatedSections[idx].standards[standardIdx].serviceItem = value;
                                const forcedYearType = getForcedProductCategoryYearType(
                                  idx, standardIdx, null, standard.productCategory, value
                                );
                                if (forcedYearType !== null) {
                                  updatedSections[idx].standards[standardIdx].yearType = forcedYearType;
                                }
                                setProposalServices(updatedSections);
                                
                                // TAMBAHAN: Panggil fetchMaxPrice ketika service item dipilih untuk mengisi unit price otomatis
                                if (value) {
                                  fetchMaxPrice(idx, standardIdx);
                                }
                              }}
                              options={itemOptionsBySection[`${idx}_${standardIdx}`] || []}
                              placeholder="Select Service Item"
                              dropdownParent="body"
                            />
                          </td>
                          <td style={{ minWidth: 120 }}>
                            <FormFieldSelect
                              value={standard.yearType || 0}
                              onChange={(e) => {
                                const updatedSections = [...proposalServices];
                                updatedSections[idx].standards[standardIdx].yearType = Number(e.target.value);
                                setProposalServices(updatedSections);
                              }}
                            >
                              <option value={0}>Every Year</option>
                              <option value={1}>Select Year</option>
                            </FormFieldSelect>
                          </td>
                          <td style={{ minWidth: 120 }}>
                            {standard.yearType === 1 ? (
                              <FormFieldSelect
                                value={standard.selectedYear || new Date().getFullYear()}
                                onChange={(e) => {
                                  const updatedSections = [...proposalServices];
                                  updatedSections[idx].standards[standardIdx].selectedYear = Number(e.target.value);
                                  setProposalServices(updatedSections);
                                }}
                              >
                                {Array.from({ length: 11 }, (_, i) => 2020 + i).map(year => (
                                  <option key={year} value={year}>{year}</option>
                                ))}
                              </FormFieldSelect>
                            ) : (
                              <span className="text-muted">Auto</span>
                            )}
                          </td>
                          <td style={{ minWidth: 80 }}>
                            <input 
                              type="number" 
                              className="form-control" 
                              value={standard.qty} 
                              onChange={e => {
                              const updatedSections = [...proposalServices];
                              updatedSections[idx].standards[standardIdx].qty = e.target.value;
                              setProposalServices(updatedSections);
                              }}
                            />
                          </td>
                          <td style={{ minWidth: 100 }}>
                            <AppSelect
                              name={`unit-${section.id}-${standardIdx}`}
                              value={standard.unit}
                              onChange={(name, value) => {
                                const updatedSections = [...proposalServices];
                                updatedSections[idx].standards[standardIdx].unit = value;
                                updatedSections[idx].standards[standardIdx].workFlowIdChoice = null; // Reset workFlowIdChoice
                                updatedSections[idx].standards[standardIdx].workFlowIdSystem = null; // Reset workFlowIdSystem
                                setProposalServices(updatedSections);
                                // HAPUS: fetchMaxPrice(idx, standardIdx); // Sekarang dipanggil saat service item dipilih
                              }}
                              options={unitOptions}
                              placeholder="Select Unit"
                              dropdownParent="body"
                              disabled={true}
                            />
                          </td>
                          <td style={{ minWidth: 120 }}>
                            <FormFieldSelect
                              value={standard.currencyCode || 'IDR'}
                              onChange={async (e) => {
                                const nextCurrencyCode = e.target.value;
                                await handleRowCurrencyChange(idx, standardIdx, nextCurrencyCode);
                              }}
                            >
                              {currencyOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </FormFieldSelect>
                          </td>
                          {isTrainingService(section.service) && (
                            <td style={{ minWidth: 120 }}>
                              <input
                                type="text"
                                className="form-control"
                                name={`participants-${section.id}-${standardIdx}`}
                                value={standard.participants || ''}
                                onChange={(e) => {
                                  // ✅ Only allow numeric input
                                  const value = e.target.value.replace(/[^0-9]/g, '');
                                  const updatedSections = [...proposalServices];
                                  updatedSections[idx].standards[standardIdx].participants = value ? parseInt(value) : null;
                                  setProposalServices(updatedSections);
                                }}
                                onKeyDown={(e) => {
                                  // ✅ Block non-numeric keys (except backspace, delete, tab, arrow keys, etc.)
                                  const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                                  const isNumber = /^[0-9]$/.test(e.key);
                                  const isAllowedKey = allowedKeys.includes(e.key) || 
                                                       (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) ||
                                                       (e.metaKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()));
                                  
                                  if (!isNumber && !isAllowedKey) {
                                    e.preventDefault();
                                  }
                                }}
                                onPaste={(e) => {
                                  // ✅ Only allow pasting numbers
                                  e.preventDefault();
                                  const pastedText = e.clipboardData.getData('text');
                                  const numbersOnly = pastedText.replace(/[^0-9]/g, '');
                                  if (numbersOnly) {
                                    const updatedSections = [...proposalServices];
                                    updatedSections[idx].standards[standardIdx].participants = parseInt(numbersOnly) || null;
                                    setProposalServices(updatedSections);
                                  }
                                }}
                                placeholder="0"
                                inputMode="numeric"
                                pattern="[0-9]*"
                              />
                            </td>
                          )}
                          <td style={{ minWidth: 180 }}>
                            <CurrencyInput
                              className="form-control"
                              name={`unitPrice-${section.id}-${standardIdx}`}
                              value={standard.unitPrice}
                              decimalsLimit={2}
                              onValueChange={async (value) => {
                                const updatedSections = [...proposalServices];
                                updatedSections[idx].standards[standardIdx].unitPrice = value;
                                // ✅ FIX: Also update unitPriceDisplayCurrency to ensure consistency
                                updatedSections[idx].standards[standardIdx].unitPriceDisplayCurrency = value;
                                setProposalServices(updatedSections);

                                // Update unitPriceBaseCurrency ketika user mengubah unitPrice secara manual
                                if (value && (standard.currencyCode || 'IDR') !== 'IDR') {
                                  await updateUnitPriceBaseCurrency(idx, standardIdx, value);
                                }

                                fetchWorkflowIdByUnitPrice(idx, standardIdx, value);
                              }}
                              prefix={getCurrencyPrefix(standard.currencyCode || 'IDR')}
                              placeholder="0"
                              allowNegativeValue={false}
                            />
                          </td>
                          <td className="text-center align-middle">
                            {!readOnly && (
                              <button className="btn btn-icon btn-sm btn-outline-danger" type="button" title="Delete standard" onClick={() => handleDeleteStandard(idx, standardIdx)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icon-tabler-trash"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M4 7l16 0" /><path d="M10 11l0 6" /><path d="M14 11l0 6" /><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" /><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" /></svg>
                              </button>
                            )}
                          </td>
                        </tr>
                        {standard.productCategories && standard.productCategories.map((productCategory, productCategoryIdx) => (
                          <React.Fragment key={`product-category-fragment-${standardIdx}-${productCategoryIdx}`}>
                          <tr key={`product-category-${standardIdx}-${productCategoryIdx}`} className="table-light">
                            {/* ✅ TAMBAHAN: Empty Version column for alignment */}
                            <td></td>
                            <td className="text-end pe-2">
                              <small className="text-muted">↳</small>
                            </td>
                            <td style={{ minWidth: 180 }}>
                              <AppSelect
                                name={`product-category-${section.id}-${standardIdx}-${productCategoryIdx}`}
                                value={productCategory.productCategory}
                                onChange={(name, value) => {
                                  const updatedSections = [...proposalServices];
                                  updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].productCategory = value;
                                  const forcedYearType = getForcedProductCategoryYearType(
                                    idx,
                                    standardIdx,
                                    productCategoryIdx,
                                    value,
                                    productCategory.serviceItem
                                  );
                                  if (forcedYearType !== null) {
                                    updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].yearType = forcedYearType;
                                  }
                                  setProposalServices(updatedSections);
                                }}
                                options={categoryOptionsBySection[idx] || []}
                                placeholder="Select Product Category"
                                dropdownParent="body"
                              />
                            </td>
                            <td style={{ minWidth: 180 }}>
                              <AppSelect
                                name={`item-${section.id}-${standardIdx}-${productCategoryIdx}`}
                                value={productCategory.serviceItem}
                                onChange={(name, value) => {
                                  const updatedSections = [...proposalServices];
                                  updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].serviceItem = value;
                                  const forcedYearType = getForcedProductCategoryYearType(
                                    idx,
                                    standardIdx,
                                    productCategoryIdx,
                                    productCategory.productCategory,
                                    value
                                  );
                                  if (forcedYearType !== null) {
                                    updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].yearType = forcedYearType;
                                  }
                                  setProposalServices(updatedSections);
                                  
                                  // TAMBAHAN: Panggil fetchMaxPrice ketika service item dipilih untuk mengisi unit price otomatis
                                  if (value) {
                                    fetchMaxPrice(idx, standardIdx, true, productCategoryIdx);
                                  }
                                }}
                                options={itemOptionsBySection[`sub_${idx}_${standardIdx}_${productCategoryIdx}`] || []}
                                placeholder="Select Service Item"
                                dropdownParent="body"
                              />
                            </td>
                            <td style={{ minWidth: 120 }}>
                              <FormFieldSelect
                                value={productCategory.yearType || 0}
                                onChange={(e) => {
                                  const updatedSections = [...proposalServices];
                                  updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].yearType = Number(e.target.value);
                                  setProposalServices(updatedSections);
                                }}
                              >
                                <option value={0}>Every Year</option>
                                <option value={1}>Select Year</option>
                              </FormFieldSelect>
                            </td>
                            <td style={{ minWidth: 120 }}>
                              {productCategory.yearType === 1 ? (
                                <FormFieldSelect
                                  value={productCategory.selectedYear || new Date().getFullYear()}
                                  onChange={(e) => {
                                    const updatedSections = [...proposalServices];
                                    updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].selectedYear = Number(e.target.value);
                                    setProposalServices(updatedSections);
                                  }}
                                >
                                  {Array.from({ length: 11 }, (_, i) => 2020 + i).map(year => (
                                    <option key={year} value={year}>{year}</option>
                                  ))}
                                </FormFieldSelect>
                              ) : (
                                <span className="text-muted">Auto</span>
                              )}
                            </td>
                            <td style={{ minWidth: 80 }}>
                              <input 
                                type="number" 
                                className="form-control" 
                                value={productCategory.qty} 
                                onChange={e => {
                                const updatedSections = [...proposalServices];
                                updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].qty = e.target.value;
                                setProposalServices(updatedSections);
                                }}
                              />
                            </td>
                            <td style={{ minWidth: 100 }}>
                              <AppSelect
                                name={`unit-${section.id}-${standardIdx}-${productCategoryIdx}`}
                                value={productCategory.unit}
                                onChange={(name, value) => {
                                  const updatedSections = [...proposalServices];
                                  updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].unit = value;
                                  updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].workFlowIdChoice = null; // Reset workFlowIdChoice
                                  updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].workFlowIdSystem = null; // Reset workFlowIdSystem
                                  setProposalServices(updatedSections);
                                  // HAPUS: fetchMaxPrice(idx, standardIdx, true, productCategoryIdx); // Sekarang dipanggil saat service item dipilih
                                }}
                                options={unitOptions}
                                placeholder="Select Unit"
                                dropdownParent="body"
                                disabled={true}
                              />
                            </td>
                            <td style={{ minWidth: 120 }}>
                              <FormFieldSelect
                                value={productCategory.currencyCode || 'IDR'}
                                onChange={async (e) => {
                                  const nextCurrencyCode = e.target.value;
                                  await handleRowCurrencyChange(idx, standardIdx, nextCurrencyCode, true, productCategoryIdx);
                                }}
                              >
                                {currencyOptions.map(option => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </FormFieldSelect>
                            </td>
                            {isTrainingService(section.service) && (
                              <td style={{ minWidth: 120 }}>
                                <input
                                  type="text"
                                  className="form-control"
                                  name={`participants-${section.id}-${standardIdx}-${productCategoryIdx}`}
                                  value={productCategory.participants || ''}
                                  onChange={(e) => {
                                    // ✅ Only allow numeric input
                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                    const updatedSections = [...proposalServices];
                                    updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].participants = value ? parseInt(value) : null;
                                    setProposalServices(updatedSections);
                                  }}
                                  onKeyDown={(e) => {
                                    // ✅ Block non-numeric keys (except backspace, delete, tab, arrow keys, etc.)
                                    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                                    const isNumber = /^[0-9]$/.test(e.key);
                                    const isAllowedKey = allowedKeys.includes(e.key) || 
                                                         (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) ||
                                                         (e.metaKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()));
                                    
                                    if (!isNumber && !isAllowedKey) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onPaste={(e) => {
                                    // ✅ Only allow pasting numbers
                                    e.preventDefault();
                                    const pastedText = e.clipboardData.getData('text');
                                    const numbersOnly = pastedText.replace(/[^0-9]/g, '');
                                    if (numbersOnly) {
                                      const updatedSections = [...proposalServices];
                                      updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].participants = parseInt(numbersOnly) || null;
                                      setProposalServices(updatedSections);
                                    }
                                  }}
                                  placeholder="0"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                />
                              </td>
                            )}
                            <td style={{ minWidth: 180 }}>
                              <CurrencyInput
                                className="form-control"
                                name={`unitPrice-${section.id}-${standardIdx}-${productCategoryIdx}`}
                                value={productCategory.unitPrice}
                                decimalsLimit={2}
                                onValueChange={async (value) => {
                                  const updatedSections = [...proposalServices];
                                  updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].unitPrice = value;
                                  // ✅ FIX: Also update unitPriceDisplayCurrency to ensure consistency
                                  updatedSections[idx].standards[standardIdx].productCategories[productCategoryIdx].unitPriceDisplayCurrency = value;
                                  setProposalServices(updatedSections);
                                  
                                  // Update unitPriceBaseCurrency ketika user mengubah unitPrice secara manual
                                  if (value && (productCategory.currencyCode || 'IDR') !== 'IDR') {
                                    await updateUnitPriceBaseCurrency(idx, standardIdx, value, true, productCategoryIdx);
                                  }

                                  fetchWorkflowIdByUnitPrice(idx, standardIdx, value, true, productCategoryIdx);
                                }}
                                prefix={getCurrencyPrefix(productCategory.currencyCode || 'IDR')}
                                placeholder="0"
                                allowNegativeValue={false}
                              />
                            </td>
                            <td className="text-center align-middle">
                              {!readOnly && (
                                <button className="btn btn-icon btn-sm btn-outline-danger" type="button" title="Delete product category" onClick={() => handleDeleteProductCategory(idx, standardIdx, productCategoryIdx)}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icon-tabler-trash"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M4 7l16 0" /><path d="M10 11l0 6" /><path d="M14 11l0 6" /><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" /><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" /></svg>
                                </button>
                              )}
                            </td>
                          </tr>
                          </React.Fragment>
                        ))}
                        {/* Button: Add Product Category — left-aligned, indented under the standard so it
                            never collides with the right-hand Action column */}
                        {!readOnly && (
                          <tr>
                            <td colSpan={isTrainingService(section.service) ? 11 : 10} className="py-1">
                              <button
                                className="btn btn-outline-primary"
                                type="button"
                                onClick={() => handleAddProductCategory(idx, standardIdx)}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 5l0 14" /><path d="M5 12l14 0" /></svg>
                                Add product category
                              </button>
                            </td>
                          </tr>
                        )}
                        {isCertificationServiceSection(section.service) && (
                          <tr className="table-secondary">
                            <td colSpan={isTrainingService(section.service) ? 12 : 11} className="py-2 px-3">
                              <label className="form-label mb-1 fw-semibold small">EA Code</label>
                              <AppSelect
                                name={`eaCode-${section.id}-${standardIdx}`}
                                value={standard.eaCodeSectorIds || []}
                                onChange={(name, values) => {
                                  const updatedSections = [...proposalServices];
                                  updatedSections[idx].standards[standardIdx].eaCodeSectorIds = values || [];
                                  setProposalServices(updatedSections);
                                }}
                                options={eaCodeOptions}
                                placeholder="Select EA code(s)"
                                dropdownParent="body"
                                valueField="id"
                                labelField="name"
                                multiple={true}
                              />
                            </td>
                          </tr>
                        )}
                        <tr className="table-secondary">
                          <td colSpan={isTrainingService(section.service) ? 12 : 11} className="py-2 px-3">
                            <label className="form-label mb-1 fw-semibold small">Scope</label>
                            {isIsccOrSustainabilityService(section.service) ? (
                              <AppSelect
                                name={`standard-scope-${section.id}-${standardIdx}`}
                                value={
                                  Array.isArray(standard.scope)
                                    ? standard.scope.map(v => String(v))
                                    : (standard.scope
                                        ? String(standard.scope).split(',').map(v => v.trim()).filter(Boolean)
                                        : [])
                                }
                                onChange={(name, value) => {
                                  const updatedSections = [...proposalServices];
                                  updatedSections[idx].standards[standardIdx].scope = value;
                                  setProposalServices(updatedSections);
                                }}
                                options={isccScopeOptions}
                                placeholder="Select scope items"
                                multiple={true}
                                valueField="value"
                                labelField="label"
                                searchField="label"
                                dropdownParent="body"
                              />
                            ) : (
                              <textarea
                                className="form-control"
                                value={standard.scope || ''}
                                onChange={(e) => {
                                  const updatedSections = [...proposalServices];
                                  updatedSections[idx].standards[standardIdx].scope = e.target.value;
                                  setProposalServices(updatedSections);
                                }}
                                placeholder="Enter scope for this standard"
                                rows="2"
                              />
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                {/* Button: Add Standard (was Add Row) */}
                {!readOnly && (
                  <button
                    className="btn btn-primary mt-2"
                    type="button"
                    onClick={() => handleAddStandard(idx)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-plus"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 5l0 14" /><path d="M5 12l14 0" /></svg>
                    Add Standard
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {/* Add Service button - moved to right side below service sections */}
        {!readOnly && (
          <div className="d-flex justify-content-end my-3">
            <button className="btn btn-primary" onClick={handleAddService}>
              + Add Service
            </button>
          </div>
        )}

        {/* Audit Methodology — shown only for Certification Program / Sustainability services.
            Positioned after Services and before Commercial Terms. */}
        {shouldShowAuditMethodology && (
          <div className="card mb-4 border-0 shadow-sm audit-methodology-section" style={{ overflow: 'hidden', borderRadius: '0.75rem' }}>
            <div
              className="card-header border-0 d-flex align-items-center"
              style={{ background: 'linear-gradient(135deg, #0d3d42 0%, #1f656c 55%, #2a8a93 100%)', color: '#fff', padding: '1rem 1.25rem' }}
            >
              <span
                className="d-inline-flex align-items-center justify-content-center me-3 flex-shrink-0"
                style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.15)' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2"/><path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z"/><path d="M9 14l2 2l4 -4"/></svg>
              </span>
              <div>
                <h3 className="card-title mb-0 text-white" style={{ fontWeight: 600 }}>Audit Methodology</h3>
                <div className="small text-white" style={{ opacity: 0.85 }}>Audit planning for Certification Program &amp; Sustainability services</div>
              </div>
            </div>
            <div className="card-body" style={{ padding: '1.5rem' }}>
              <div className="alert alert-info d-flex align-items-start gap-2 mb-4" style={{ borderRadius: '0.5rem' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-1"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 9h.01"/><path d="M11 12h1v4h1"/><path d="M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0 -18z"/></svg>
                <span className="small mb-0">Proposal-level audit planning information. Site details below are seeded to the Audit Platform. Projects with more than one standard must use Integrated or Combined audit before Audit Platform tasks can be created.</span>
              </div>

              <div className="row g-3">
                <div className="col-12 col-lg-6">
                  <label className="form-label fw-semibold">Methodology</label>
                  <FormFieldSelect
                    value={proposalData.auditMethodology || ''}
                    onChange={(e) => setProposalData(prev => ({ ...prev, auditMethodology: e.target.value }))}
                  >
                    <option value="">-- Select Methodology --</option>
                    <option value="Single Audit">Single Audit</option>
                    <option value="Integrated Audit">Integrated Audit</option>
                    <option value="Combined Audit">Combined Audit</option>
                  </FormFieldSelect>
                </div>
                <div className="col-7 col-lg-3">
                  <label className="form-label fw-semibold">Sites</label>
                  <FormFieldSelect
                    value={proposalData.multiSite ? 'multi' : 'single'}
                    onChange={(e) => handleSitesModeChange(e.target.value)}
                  >
                    <option value="single">Single Site</option>
                    <option value="multi">Multi-sites</option>
                  </FormFieldSelect>
                </div>
                <div className="col-5 col-lg-3">
                  <label className="form-label fw-semibold">Number of Sites</label>
                  <input
                    type="number"
                    className="form-control"
                    value={proposalData.multiSite ? (proposalData.numberOfSites || '') : 1}
                    onChange={(e) => setProposalData(prev => ({ ...prev, numberOfSites: e.target.value }))}
                    min="1"
                    placeholder="e.g. 2"
                    disabled={!proposalData.multiSite}
                  />
                </div>
              </div>

              <div className="row g-3 mt-1">
                <div className="col-12">
                  <label className="form-label fw-semibold d-flex align-items-center gap-2">
                    HO Address
                    {hoAddressSaveState === 'saving' && <span className="text-muted small fw-normal">Saving to Company...</span>}
                    {hoAddressSaveState === 'saved' && <span className="text-success small fw-normal">Saved to Company</span>}
                    {hoAddressSaveState === 'error' && <span className="text-danger small fw-normal">Failed to save to Company — try again</span>}
                  </label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={proposalData.siteAddress || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setProposalData(prev => ({ ...prev, siteAddress: value }));
                      if (hoAddressSaveState !== 'idle') setHoAddressSaveState('idle');
                    }}
                    onBlur={(e) => syncHoAddressToCompany(e.target.value)}
                    placeholder="Enter the client's Head Office address"
                  />
                  <div className="form-text">Auto-filled from the client Company's address. Editing this also updates the Company record.</div>
                </div>
              </div>

              {/* ===== Sites — directly below HO Address. Never auto-filled from the
                   company: this is the record seeded to Audit Platform for scheduling
                   and certificate issuance/draft, so it must be entered deliberately. ===== */}
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                  <div>
                    <label className="form-label fw-semibold mb-0">
                      {proposalData.multiSite ? 'Project Sites' : 'Site Location'}
                    </label>
                    <div className="text-muted small">
                      This site information is shared with Audit Platform Scheduling and will be used later in FTRD-02 Certificate Issuance. Keep it accurate before confirming the audit schedule.
                    </div>
                  </div>
                  {proposalData.multiSite && (
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={addAuditSite}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 5l0 14"/><path d="M5 12l14 0"/></svg>
                      Add Site
                    </button>
                  )}
                </div>

                {auditSites.map((site, index) => (
                  <div
                    key={site.id || index}
                    className="rounded mb-3"
                    style={{ border: '1px solid #e9ecef', background: '#fff', overflow: 'hidden', transition: 'box-shadow 0.2s ease, border-color 0.2s ease' }}
                  >
                    <div
                      className="d-flex justify-content-between align-items-center px-3 py-2"
                      style={{ background: '#f8fafa', borderBottom: '1px solid #eef1f1' }}
                    >
                      <span className="fw-semibold d-flex align-items-center gap-2" style={{ color: '#1f656c' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"/><path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z"/></svg>
                        {proposalData.multiSite ? `Site ${index + 1}` : 'Primary Site'}
                        {site.isPrimary && (
                          <span className="badge ms-1" style={{ background: 'rgba(42,138,147,0.12)', color: '#1f656c', fontWeight: 600 }}>Primary</span>
                        )}
                      </span>
                      {proposalData.multiSite && auditSites.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-icon btn-sm btn-outline-danger"
                          title="Remove site"
                          onClick={() => removeAuditSite(index)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M18 6l-12 12"/><path d="M6 6l12 12"/></svg>
                        </button>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="row g-3">
                        <div className="col-12 col-md-4">
                          <label className="form-label small fw-semibold text-muted">Site Name</label>
                          <input className="form-control" value={site.siteName || ''} placeholder="e.g. Head Office" onChange={(e) => updateAuditSite(index, 'siteName', e.target.value)} />
                        </div>
                        <div className="col-6 col-md-4">
                          <label className="form-label small fw-semibold text-muted">Country</label>
                          <input className="form-control" value={site.country || ''} placeholder="Country" onChange={(e) => updateAuditSite(index, 'country', e.target.value)} />
                        </div>
                        <div className="col-6 col-md-4">
                          <label className="form-label small fw-semibold text-muted">City</label>
                          <input className="form-control" value={site.city || ''} placeholder="City" onChange={(e) => updateAuditSite(index, 'city', e.target.value)} />
                        </div>
                        <div className="col-12">
                          <label className="form-label small fw-semibold text-muted">Site Address</label>
                          <textarea className="form-control" rows="2" value={site.address || ''} placeholder="Full site address" onChange={(e) => updateAuditSite(index, 'address', e.target.value)} />
                        </div>
                        {proposalData.multiSite && (
                          <div className="col-6 col-md-3 d-flex align-items-end">
                            <label className="form-check d-flex align-items-center gap-2 mb-2" style={{ cursor: 'pointer' }}>
                              <input
                                className="form-check-input mt-0"
                                type="radio"
                                name="primarySite"
                                checked={Boolean(site.isPrimary)}
                                onChange={() => setPrimarySite(index)}
                              />
                              <span className="form-check-label small">Primary site</span>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {savingSites && <div className="text-muted small">Saving sites...</div>}
              </div>

              {(proposalData.auditMethodology === 'Integrated Audit' || proposalData.auditMethodology === 'Combined Audit') && (
                <div className="mt-4 p-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}>
                  <label className="form-label fw-semibold mb-1">Standards for {proposalData.auditMethodology}</label>
                  <div className="text-muted small mb-3">
                    Integrated audit uses the same auditor for multiple standards on the same audit date. Combined audit uses different auditors in the same audit window.
                  </div>
                  {getAuditStandardOptions().length > 1 ? (
                    <div className="row g-2">
                      {getAuditStandardOptions().map(option => {
                        const targetField = proposalData.auditMethodology === 'Integrated Audit' ? 'integratedStandardIds' : 'combinedStandardIds';
                        const selected = (proposalData[targetField] || []).map(Number).includes(Number(option.id));
                        return (
                          <div className="col-12 col-md-6" key={`${option.id}-${option.label}`}>
                            <label
                              className="form-check border rounded p-2 h-100 mb-0 d-flex align-items-center"
                              style={{ cursor: 'pointer', transition: 'all 0.15s ease', background: selected ? 'rgba(42,138,147,0.08)' : '#fff', borderColor: selected ? '#2a8a93' : '' }}
                            >
                              <input
                                className="form-check-input me-2 mt-0"
                                type="checkbox"
                                checked={selected}
                                onChange={(e) => updateAuditStandardSelection(targetField, option.id, e.target.checked)}
                              />
                              <span className="form-check-label">
                                {option.label}
                                <span className="text-muted ms-1 small">({option.serviceName})</span>
                              </span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="alert alert-warning mb-0">
                      Integrated/combined audit can only be configured after the project has more than one standard.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Commercial Terms Section */}
        <div className="card mb-4 commercial-terms-section" style={{ overflowAnchor: 'none' }}>
          <div 
            className="card-header" 
            style={{ cursor: 'pointer' }}
            onClick={() => setIsCommercialTermsExpanded(!isCommercialTermsExpanded)}
          >
            <div className="d-flex justify-content-between align-items-center w-100">
              <h5 className="card-title mb-0">Commercial Terms</h5>
              <ChevronDown 
                size={20}
                style={{ 
                  transform: isCommercialTermsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  marginLeft: 'auto'
                }}
              />
            </div>
          </div>
          {isCommercialTermsExpanded && (
          <div className="card-body" style={{ overflowAnchor: 'none' }}>
            {loadingCommercialTerms ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading commercial terms...</p>
              </div>
            ) : (
              <>
                {commercialTerms.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted">No commercial terms added yet.</p>
                  </div>
                ) : (
                  commercialTerms.map((term, idx) => (
                    <div key={`term-${term.sectionType || 'standalone'}-${term.sectionOrder || idx}`} className="mb-4 p-3 border rounded" style={{ overflowAnchor: 'none' }}>
                      {term.isSection ? (
                        <div>
                          <div className="mb-2">
                            <label className="form-label fw-bold">Section Title</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="e.g., Payment Terms, Delivery Terms"
                              value={term.sectionTitle || ''}
                              disabled
                              readOnly
                            />
                          </div>
                          <div className="mb-2">
                            <label className="form-label">Points</label>
                            {/* ✅ POINT OPTIONS: Display points with options if available */}
                            {(term.pointsWithOptions || []).map((pointWithOptions, pIdx) => (
                              <div key={`point-${term.sectionType || 'standalone'}-${term.sectionOrder || 'no-order'}-${pointWithOptions.pointNumber || pIdx}`} className="mb-4 p-3 border rounded">
                                <div className="mb-2">
                                  <label className="form-label fw-bold">
                                    Point {pointWithOptions.pointNumber || (pIdx + 1)}
                                  </label>
                                  {/* Base Text: only show for RADIO points (avoid duplicate with input label) */}
                                  {pointWithOptions.pointText && pointWithOptions.pointText.trim() !== '' && !((pointWithOptions.inputType === 'NUMBER') || (pointWithOptions.inputType === 'TEXT') || (pointWithOptions.inputType === 'CURRENCY') || (pointWithOptions.inputType == null && (!pointWithOptions.options || pointWithOptions.options.length === 0))) && (
                                    <div className="mb-2">
                                      <strong>Base Text:</strong>
                                      <p className="text-muted">{pointWithOptions.pointText}</p>
                                    </div>
                                  )}
                                  {/* ✅ POINT OPTIONS: Data-driven by inputType from master data (NUMBER, TEXT, CURRENCY, RADIO). Fallback: no options + no inputType → NUMBER for backward compat. */}
                                  {((pointWithOptions.inputType === 'NUMBER') || (pointWithOptions.inputType == null && (!pointWithOptions.options || pointWithOptions.options.length === 0))) ? (
                                    (() => {
                                      const isFallbackNumber = pointWithOptions.inputType == null && (!pointWithOptions.options || pointWithOptions.options.length === 0);
                                      const effectiveSuffix = (pointWithOptions.inputSuffix || '').trim() || (isFallbackNumber && term.sectionType === 'PARTICIPANT_CAPACITY' ? 'participants' : '');
                                      return (
                                    <div className="mb-2">
                                      <label className="form-label">
                                        {pointWithOptions.pointText || 'Value'}:
                                      </label>
                                      <div className="input-group" style={{ maxWidth: '300px' }}>
                                        <input
                                          type="number"
                                          className="form-control"
                                          min="0"
                                          step="1"
                                          placeholder="Enter number"
                                          value={(() => {
                                            if (pointWithOptions.selectedOptionId && pointWithOptions.selectedOptionId.startsWith('custom-')) {
                                              const number = pointWithOptions.selectedOptionId.replace('custom-', '');
                                              if (number && !isNaN(number)) return number;
                                            }
                                            if (term.points && term.points[pIdx]) {
                                              const match = term.points[pIdx].match(/(\d+)/);
                                              if (match) return match[1];
                                            }
                                            return '';
                                          })()}
                                          onChange={(e) => {
                                            const inputValue = e.target.value;
                                            const updated = [...commercialTerms];
                                            if (!updated[idx].pointsWithOptions) updated[idx].pointsWithOptions = [];
                                            if (!updated[idx].points) updated[idx].points = [];
                                            const suffix = (pointWithOptions.inputSuffix || '').trim() || (pointWithOptions.inputType == null && term.sectionType === 'PARTICIPANT_CAPACITY' ? 'participants' : '');
                                            if (inputValue && inputValue.trim() !== '') {
                                              const fullText = suffix
                                                ? `${pointWithOptions.pointText} ${inputValue} ${suffix}.`
                                                : `${pointWithOptions.pointText} ${inputValue}.`;
                                              updated[idx].points[pIdx] = fullText;
                                              updated[idx].pointsWithOptions[pIdx].selectedOptionId = `custom-${inputValue}`;
                                            } else {
                                              updated[idx].pointsWithOptions[pIdx].selectedOptionId = null;
                                              updated[idx].points[pIdx] = pointWithOptions.pointText;
                                            }
                                            setCommercialTerms(updated);
                                          }}
                                        />
                                        {(pointWithOptions.inputSuffix || effectiveSuffix) && (
                                          <span className="input-group-text">{pointWithOptions.inputSuffix || effectiveSuffix}</span>
                                        )}
                                      </div>
                                    </div>
                                    );
                                    })()
                                  ) : (pointWithOptions.inputType === 'TEXT') ? (
                                    <div className="mb-2">
                                      <label className="form-label">
                                        {pointWithOptions.pointText || 'Value'}:
                                      </label>
                                      <div className="input-group" style={{ maxWidth: '600px' }}>
                                        <input
                                          type="text"
                                          className="form-control"
                                          placeholder="Enter value"
                                          value={(() => {
                                            if (pointWithOptions.selectedOptionId && pointWithOptions.selectedOptionId.startsWith('custom-')) {
                                              return pointWithOptions.selectedOptionId.replace('custom-', '');
                                            }
                                            if (term.points && term.points[pIdx] && pointWithOptions.pointText) {
                                              const after = pointWithOptions.pointText.trim().length;
                                              const rest = term.points[pIdx].substring(after).trim();
                                              return rest.replace(/\s+risk level\.?.*$/i, '').trim() || rest.split('.')[0].trim() || '';
                                            }
                                            return '';
                                          })()}
                                          onChange={(e) => {
                                            const inputValue = e.target.value;
                                            const updated = [...commercialTerms];
                                            if (!updated[idx].pointsWithOptions) updated[idx].pointsWithOptions = [];
                                            if (!updated[idx].points) updated[idx].points = [];
                                            const suffix = (pointWithOptions.inputSuffix || '').trim();
                                            if (inputValue && inputValue.trim() !== '') {
                                              const fullText = suffix
                                                ? `${pointWithOptions.pointText} ${inputValue} ${suffix}.`
                                                : `${pointWithOptions.pointText} ${inputValue}.`;
                                              updated[idx].points[pIdx] = fullText;
                                              updated[idx].pointsWithOptions[pIdx].selectedOptionId = `custom-${inputValue}`;
                                            } else {
                                              updated[idx].pointsWithOptions[pIdx].selectedOptionId = null;
                                              updated[idx].points[pIdx] = pointWithOptions.pointText;
                                            }
                                            setCommercialTerms(updated);
                                          }}
                                        />
                                        {pointWithOptions.inputSuffix && (
                                          <span className="input-group-text">{pointWithOptions.inputSuffix}</span>
                                        )}
                                      </div>
                                    </div>
                                  ) : (pointWithOptions.inputType === 'CURRENCY') ? (
                                    <div className="mb-2">
                                      <label className="form-label">
                                        {pointWithOptions.pointText?.includes('IDR') || pointWithOptions.inputSuffix ? `${pointWithOptions.inputSuffix || 'IDR'} Amount` : 'Amount'}:
                                      </label>
                                      <div className="input-group" style={{ maxWidth: '250px', width: '100%' }}>
                                        <FormFieldSelect
                                          style={{ width: '120px', flex: '0 0 120px' }}
                                          value={pointWithOptions.selectedCurrency || 'IDR'}
                                          onChange={(e) => {
                                            const newCode = e.target.value;
                                            const updated = [...commercialTerms];
                                            if (!updated[idx].pointsWithOptions) updated[idx].pointsWithOptions = [];
                                            if (!updated[idx].points) updated[idx].points = [];
                                            updated[idx].pointsWithOptions[pIdx] = { ...updated[idx].pointsWithOptions[pIdx], selectedCurrency: newCode };
                                            const rawNum = pointWithOptions.selectedOptionId && pointWithOptions.selectedOptionId.startsWith('custom-')
                                              ? pointWithOptions.selectedOptionId.replace('custom-', '')
                                              : (() => {
                                                const text = updated[idx].points[pIdx];
                                                if (!text) return '';
                                                const parsed = parseCommercialTermCurrencyFromText(text);
                                                return parsed.amountDigits || '';
                                              })();
                                            if (rawNum !== '') {
                                              const num = parseInt(rawNum, 10);
                                              if (!Number.isNaN(num)) {
                                                const formattedValue = num.toLocaleString('id-ID');
                                                const prefix = getCommercialTermAmountPrefix(newCode);
                                                const suffixPart = (pointWithOptions.inputSuffix || '').trim();
                                                const amountCore = prefix + formattedValue;
                                                const amountWithPrefix = suffixPart ? `${amountCore} ${suffixPart}` : amountCore;
                                                const baseText = pointWithOptions.pointText || '';
                                                let fullText;
                                                if (/\bnet amount\s+.*?\s+to\b/i.test(baseText)) {
                                                  fullText = baseText.replace(/\b(net amount)\s+.*?(\s+to\b)/i, `$1 ${amountWithPrefix}$2`);
                                                } else if (baseText.trim()) {
                                                  fullText = `${baseText} ${amountWithPrefix}`;
                                                } else {
                                                  fullText = amountWithPrefix;
                                                }
                                                updated[idx].points[pIdx] = fullText;
                                              }
                                            }
                                            setCommercialTerms(updated);
                                          }}
                                        >
                                          {currencyOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                              {option.label}{option.symbol ? ` (${option.symbol})` : ''}
                                            </option>
                                          ))}
                                        </FormFieldSelect>
                                        <CurrencyInput
                                          className="form-control"
                                          style={{ minWidth: '180px', flex: '1 1 auto' }}
                                          name={`currency-${idx}-${pIdx}`}
                                          value={(() => {
                                            if (pointWithOptions.selectedOptionId && pointWithOptions.selectedOptionId.startsWith('custom-')) {
                                              const raw = pointWithOptions.selectedOptionId.replace('custom-', '');
                                              return raw.replace(/\D/g, '');
                                            }
                                            if (term.points && term.points[pIdx]) {
                                              const parsed = parseCommercialTermCurrencyFromText(term.points[pIdx]);
                                              return parsed.amountDigits || '';
                                            }
                                            return '';
                                          })()}
                                          decimalsLimit={0}
                                          onValueChange={(value) => {
                                            const updated = [...commercialTerms];
                                            if (!updated[idx].pointsWithOptions) updated[idx].pointsWithOptions = [];
                                            if (!updated[idx].points) updated[idx].points = [];
                                            const selectedCurrency = pointWithOptions.selectedCurrency || 'IDR';
                                            const rawNum = value != null ? String(value).replace(/[^\d]/g, '') : '';
                                            if (rawNum !== '') {
                                              const num = parseInt(rawNum, 10);
                                              if (!Number.isNaN(num)) {
                                                const formattedValue = num.toLocaleString('id-ID');
                                                const prefix = getCommercialTermAmountPrefix(selectedCurrency);
                                                const suffixPart = (pointWithOptions.inputSuffix || '').trim();
                                                const amountCore = prefix + formattedValue;
                                                const amountWithPrefix = suffixPart ? `${amountCore} ${suffixPart}` : amountCore;
                                                const baseText = pointWithOptions.pointText || '';
                                                let fullText;
                                                if (/\bnet amount\s+.*?\s+to\b/i.test(baseText)) {
                                                  fullText = baseText.replace(/\b(net amount)\s+.*?(\s+to\b)/i, `$1 ${amountWithPrefix}$2`);
                                                } else if (baseText.trim()) {
                                                  fullText = `${baseText} ${amountWithPrefix}`;
                                                } else {
                                                  fullText = amountWithPrefix;
                                                }
                                                updated[idx].points[pIdx] = fullText;
                                                updated[idx].pointsWithOptions[pIdx].selectedOptionId = `custom-${rawNum}`;
                                                if (!updated[idx].pointsWithOptions[pIdx].selectedCurrency) {
                                                  updated[idx].pointsWithOptions[pIdx].selectedCurrency = 'IDR';
                                                }
                                              }
                                            } else {
                                              updated[idx].pointsWithOptions[pIdx].selectedOptionId = null;
                                              updated[idx].points[pIdx] = pointWithOptions.pointText || '';
                                            }
                                            setCommercialTerms(updated);
                                          }}
                                          placeholder="Enter amount"
                                          allowNegativeValue={false}
                                        />
                                        {pointWithOptions.inputSuffix && (
                                          <span className="input-group-text">{pointWithOptions.inputSuffix}</span>
                                        )}
                                      </div>
                                    </div>
                                  ) : pointWithOptions.options && Array.isArray(pointWithOptions.options) && pointWithOptions.options.length > 0 ? (
                                    <div className="mb-2">
                                      <label className="form-label">Select Option:</label>
                                      {pointWithOptions.options.map((option) => (
                                        <div key={option.optionId} className="form-check mb-2">
                                          <input
                                            className="form-check-input"
                                            type="radio"
                                            name={`point-option-${idx}-${pIdx}`}
                                            id={`option-${idx}-${pIdx}-${option.optionId}`}
                                            checked={pointWithOptions.selectedOptionId === option.optionId ||
                                                     (option.optionId === "2.2-option-3" && pointWithOptions.selectedOptionId &&
                                                      pointWithOptions.selectedOptionId.startsWith('custom-'))}
                                            onChange={(e) => {
                                              const updated = [...commercialTerms];
                                              if (!updated[idx].pointsWithOptions) updated[idx].pointsWithOptions = [];
                                              updated[idx].pointsWithOptions[pIdx].selectedOptionId = option.optionId;
                                              // ✅ Also update the points array for backward compatibility
                                              if (!updated[idx].points) updated[idx].points = [];

                                              // ✅ Properly merge base text with selected option (same logic as preview and backend)
                                              let fullText;
                                              const baseText = pointWithOptions.pointText;
                                              if (!baseText || baseText.trim() === '') {
                                                // If base text is empty, use option text as full text
                                                fullText = option.optionText;
                                              } else if (baseText.includes('[15 days/ 30 days/ custom days]')) {
                                                // Replace payment terms placeholder
                                                fullText = baseText.replace('[15 days/ 30 days/ custom days]', option.optionText);
                                              } else if (baseText.includes('[including/ excluding VAT]')) {
                                                // Replace VAT placeholder
                                                fullText = baseText.replace('[including/ excluding VAT]', option.optionText);
                                              } else if (baseText.includes('[including VAT (following government regulation)./excluding VAT (following government regulation)]')) {
                                                // Replace GHG VAT placeholder
                                                fullText = baseText.replace('[including VAT (following government regulation)./excluding VAT (following government regulation)]', option.optionText);
                                              } else if (baseText.includes('[15 days after the date of the invoice or as specified otherwise in the agreement./30 days after the date of the invoice or as specified otherwise in the agreement./custom days after the date of the invoice or as specified otherwise in the agreement]')) {
                                                // Replace GHG payment placeholder
                                                fullText = baseText.replace('[15 days after the date of the invoice or as specified otherwise in the agreement./30 days after the date of the invoice or as specified otherwise in the agreement./custom days after the date of the invoice or as specified otherwise in the agreement]', option.optionText);
                                              } else if (baseText.trim().endsWith(' ,')) {
                                                // Insert option before the comma (like Clause 10 old format)
                                                const commaIndex = baseText.lastIndexOf(' ,');
                                                fullText = baseText.substring(0, commaIndex) + ' ' + option.optionText + baseText.substring(commaIndex);
                                              } else {
                                                // ✅ GHG terms: For terms without placeholders, append option text to base text
                                                // Check if option already starts with the base text to avoid duplication
                                                if (option.optionText.startsWith(baseText.trim())) {
                                                  fullText = option.optionText;
                                                } else {
                                                  fullText = baseText + ' ' + option.optionText;
                                                }
                                              }
                                              // ✅ Update points array with merged text for backward compatibility
                                              updated[idx].points[pIdx] = fullText;
                                              setCommercialTerms(updated);
                                            }}
                                          />
                                          <label className="form-check-label" htmlFor={`option-${idx}-${pIdx}-${option.optionId}`}>
                                            {option.optionText}
                                          </label>
                                        </div>
                                      ))}

                                      {/* ✅ Show input field for custom payment days when selected */}
                                      {((pointWithOptions.selectedOptionId === "2.2-option-3") ||
                                        (pointWithOptions.selectedOptionId && pointWithOptions.selectedOptionId.startsWith('custom-'))) &&
                                       pointWithOptions.pointNumber === "2.2" && term.sectionType === "INVOICE_GENERATION" && (
                                        <div className="mt-2">
                                          <label className="form-label">Custom Days:</label>
                                          <div className="input-group" style={{ maxWidth: '200px' }}>
                                            <input
                                              type="number"
                                              className="form-control"
                                              min="1"
                                              step="1"
                                              placeholder="Enter days"
                                              value={(() => {
                                                // Extract custom days from selectedOptionId (format: custom-{days})
                                                if (pointWithOptions.selectedOptionId && pointWithOptions.selectedOptionId.startsWith('custom-')) {
                                                  const days = pointWithOptions.selectedOptionId.replace('custom-', '');
                                                  return days && !isNaN(days) ? days : '';
                                                }
                                                return '';
                                              })()}
                                              onChange={(e) => {
                                                const inputValue = e.target.value;
                                                const updated = [...commercialTerms];
                                                if (!updated[idx].pointsWithOptions) updated[idx].pointsWithOptions = [];
                                                if (!updated[idx].points) updated[idx].points = [];

                                                if (inputValue && inputValue.trim() !== '') {
                                                  // Store as custom value
                                                  updated[idx].pointsWithOptions[pIdx].selectedOptionId = `custom-${inputValue}`;

                                                  // Create full text with custom days
                                                  const fullText = pointWithOptions.pointText + ' ' +
                                                    pointWithOptions.options.find(opt => opt.optionId === "2.2-option-3").optionText
                                                      .replace('custom days', inputValue + ' days');
                                                  updated[idx].points[pIdx] = fullText;
                                                } else {
                                                  // Clear selection if input is empty
                                                  updated[idx].pointsWithOptions[pIdx].selectedOptionId = null;
                                                  updated[idx].points[pIdx] = pointWithOptions.pointText;
                                                }

                                                setCommercialTerms(updated);
                                              }}
                                            />
                                            <span className="input-group-text">days</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ) : commercialTermsPresetId ? (
                                    // ✅ If preset ID exists but options are missing, show loading/error message
                                    <div className="alert alert-warning">
                                      <small>Loading options... If this persists, please refresh the page.</small>
                                    </div>
                                  ) : null}
                                  {/* ✅ Show selected value preview (skip for input-type points and custom payment days sub-input) */}
                                  {pointWithOptions.selectedOptionId && pointWithOptions.options && pointWithOptions.options.length > 0 &&
                                   !['NUMBER', 'TEXT', 'CURRENCY'].includes(pointWithOptions.inputType) &&
                                   !(pointWithOptions.pointNumber === "2.2" && term.sectionType === "INVOICE_GENERATION" &&
                                     (pointWithOptions.selectedOptionId === "2.2-option-3" ||
                                      (pointWithOptions.selectedOptionId && pointWithOptions.selectedOptionId.startsWith('custom-')))) && (
                                    <div className="mt-2 p-2 bg-light rounded">
                                      <strong>Selected:</strong> {
                                        (() => {
                                          const selectedOption = pointWithOptions.options.find(opt => opt.optionId === pointWithOptions.selectedOptionId);
                                          if (!selectedOption) return '';

                                          // ✅ Properly merge base text with selected option (enhanced logic)
                                          let finalText;
                                          const baseText = pointWithOptions.pointText;
                                          if (!baseText || baseText.trim() === '') {
                                            // If base text is empty, use option text as full text
                                            finalText = selectedOption.optionText;
                                          } else if (baseText.includes('[15 days/ 30 days/ custom days]')) {
                                            // Replace payment terms placeholder
                                            finalText = baseText.replace('[15 days/ 30 days/ custom days]', selectedOption.optionText);
                                          } else if (baseText.includes('[including/ excluding VAT]')) {
                                            // Replace VAT placeholder
                                            finalText = baseText.replace('[including/ excluding VAT]', selectedOption.optionText);
                                          } else if (baseText.includes('[including VAT (following government regulation)./excluding VAT (following government regulation)]')) {
                                            // Replace GHG VAT placeholder
                                            finalText = baseText.replace('[including VAT (following government regulation)./excluding VAT (following government regulation)]', selectedOption.optionText);
                                          } else if (baseText.includes('[15 days after the date of the invoice or as specified otherwise in the agreement./30 days after the date of the invoice or as specified otherwise in the agreement./custom days after the date of the invoice or as specified otherwise in the agreement]')) {
                                            // Replace GHG payment placeholder
                                            finalText = baseText.replace('[15 days after the date of the invoice or as specified otherwise in the agreement./30 days after the date of the invoice or as specified otherwise in the agreement./custom days after the date of the invoice or as specified otherwise in the agreement]', selectedOption.optionText);
                                          } else if (baseText.trim().endsWith(" ,")) {
                                            // Insert option before the comma (like Clause 10 old format)
                                            const commaIndex = baseText.lastIndexOf(" ,");
                                            finalText = baseText.substring(0, commaIndex) + " " + selectedOption.optionText + baseText.substring(commaIndex);
                                          } else {
                                            // ✅ GHG terms: For terms without placeholders, append option text to base text
                                            // Check if option already starts with the base text to avoid duplication
                                            if (selectedOption.optionText.startsWith(baseText.trim())) {
                                              finalText = selectedOption.optionText;
                                            } else {
                                              finalText = baseText + " " + selectedOption.optionText;
                                            }
                                          }
                                          return finalText;
                                        })()
                                      }
                                    </div>
                                  )}
                                  {/* ✅ Show preview for NUMBER/TEXT/CURRENCY (and fallback number) input-type points */}
                                  {(['NUMBER', 'TEXT', 'CURRENCY'].includes(pointWithOptions.inputType) || (pointWithOptions.inputType == null && (!pointWithOptions.options || pointWithOptions.options.length === 0))) && term.points && term.points[pIdx] && (
                                    <div className="mt-2 p-2 bg-light rounded">
                                      <strong>Preview:</strong> {term.points[pIdx]}
                                    </div>
                                  )}
                                  {/* ✅ Show preview for custom payment days */}
                                  {(pointWithOptions.pointNumber === "2.2" && term.sectionType === "INVOICE_GENERATION" && pointWithOptions.selectedOptionId && pointWithOptions.selectedOptionId.startsWith('custom-')) && term.points && term.points[pIdx] && (
                                    <div className="mt-2 p-2 bg-light rounded">
                                      <strong>Preview:</strong> {term.points[pIdx]}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                            {/* ✅ No backward compatibility textarea - always use predefined structure when preset ID exists */}
                            {(!term.pointsWithOptions || term.pointsWithOptions.length === 0) && commercialTermsPresetId && (
                              <div className="alert alert-info">
                                <small>Loading predefined commercial terms structure...</small>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <textarea
                            className="form-control"
                            placeholder="Standalone point text"
                            rows={4}
                            value={term.pointText || ''}
                                  onChange={(e) => {
                              const updated = [...commercialTerms];
                              updated[idx].pointText = e.target.value;
                              setCommercialTerms(updated);
                              // ✅ COMMERCIAL TERMS PRESET: Clear preset ID when manually editing
                              if (commercialTermsPresetId) {
                                setCommercialTermsPresetId(null);
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ))
                )}
                
                <div className="d-flex gap-2 mt-3 align-items-center">
                  {!readOnly && (
                    <button
                      className="btn btn-success"
                      onClick={() => setShowPredefinedModal(true)}
                    >
                      Select Commercial Terms
                    </button>
                  )}
                  {/* ✅ COMMERCIAL TERMS PRESET: Show current preset indicator */}
                  {commercialTermsPresetId && (
                    <div className="badge bg-info text-dark ms-2">
                      Using template: {predefinedTermsSets.find(s => s.id === commercialTermsPresetId || s.code === commercialTermsPresetId || String(s.id) === String(commercialTermsPresetId))?.name || commercialTermsPresetId}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          )}
        </div>
        
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="card-title mb-0">
              Investment Breakdown 
              {investmentBreakdown && ` (${investmentBreakdown.years.length} Years)`}
            </h5>
          </div>
            <div className="card-body">
              {calculating && !investmentBreakdown ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 text-muted">Calculating investment breakdown...</p>
                </div>
              ) : investmentBreakdown ? (
                <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Details</th>
                      <th style={{ width: '80px', minWidth: '80px' }}>Unit</th>
                      <th style={{ width: '60px', minWidth: '60px' }}>Qty</th>
                      {showConversionColumns && <th style={{ width: '110px', minWidth: '110px' }}>Exchange Rate</th>}
                      {investmentBreakdown.years.map(year => (
                        <th key={year}>
                          {year}
                          <div className="text-muted fw-normal small">Quoted / IDR</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {investmentBreakdown.breakdown.map((serviceRow, sIdx) => (
                      <React.Fragment key={sIdx}>
                        <tr>
                          <td className="fw-bold" colSpan={breakdownBaseColumnCount + investmentBreakdown.years.length}>{serviceRow.service}</td>
                        </tr>
                        {serviceRow.standards.map((stdRow, stdIdx) => (
                          <React.Fragment key={stdIdx}>
                            <tr>
                              <td style={{ paddingLeft: 24, fontWeight: 'bold' }} colSpan={breakdownBaseColumnCount + investmentBreakdown.years.length}>{stdRow.standard}</td>
                            </tr>
                            {stdRow.serviceItems.map((item, cidx) => (
                          <tr key={cidx}>
                                <td style={{ paddingLeft: 48 }}>
                                  {item.serviceItem}
                                </td>
                                <td style={{ width: '80px', minWidth: '80px' }}>{item.unit || '-'}</td>
                                <td style={{ width: '60px', minWidth: '60px' }}>{item.qty ?? '-'}</td>
                                {showConversionColumns && (
                                  <td style={{ width: '110px', minWidth: '110px' }}>
                                    {Number(item.exchangeRate || 1).toLocaleString('id-ID')}
                                  </td>
                                )}
                                {investmentBreakdown.years.map((year, i) => {
                                  const yearIdx = item.years.indexOf(year);
                                  if (yearIdx === -1) return <td key={year}>-</td>;
                                  const itemCurrency = String(item.currencyCode || proposalCurrencyCode || 'IDR').toUpperCase();
                                  const displayUnitPrice = Number(item.unitPrice ?? item.priceDisplay ?? 0);
                                  const displayQty = Number(item.qty ?? 1);
                                  const sourceAmount = displayUnitPrice * displayQty;
                                  const convertedAmount = Number(item.prices?.[yearIdx] ?? item.prices?.[0] ?? 0);
                                  return (
                                    <td key={year} className="align-middle">
                                      {showConversionColumns ? <div className="rounded border bg-light px-2 py-1">
                                        <div className="d-flex justify-content-between align-items-center gap-2">
                                          <span className="text-muted small">{itemCurrency}</span>
                                          <span className="fw-semibold">{getCurrencyPrefix(itemCurrency)}{sourceAmount.toLocaleString('id-ID')}</span>
                                        </div>
                                        <div className="border-top mt-1 pt-1 d-flex justify-content-between align-items-center gap-2">
                                          <span className="text-muted small">IDR</span>
                                          <span className="text-success fw-semibold">Rp {convertedAmount.toLocaleString('id-ID')}</span>
                                        </div>
                                      </div> : <span className="fw-semibold">Rp {convertedAmount.toLocaleString('id-ID')}</span>}
                                    </td>
                                  );
                                })}
                          </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th>Total</th>
                      <th></th>
                      <th></th>
                      {showConversionColumns && <th></th>}
                      {investmentBreakdown.totals.map((total, i) => (
                        <th key={i} className="text-end">
                          {showConversionColumns ? <div className="rounded border bg-light px-2 py-1">
                            <div><span className="text-muted small">{proposalCurrencyCode}</span> {getCurrencyPrefix(proposalCurrencyCode)}{getQuotedInvestmentTotalForYear(investmentBreakdown.years[i]).toLocaleString('id-ID')}</div>
                            <div className="border-top mt-1 pt-1 text-success"><span className="text-muted small">IDR</span> Rp {total.toLocaleString('id-ID')}</div>
                          </div> : <span className="fw-semibold">Rp {total.toLocaleString('id-ID')}</span>}
                        </th>
                      ))}
                    </tr>
                     <tr>
                       <th>Grand Total</th>
                       <th colSpan={investmentBreakdown.totals.length + (showConversionColumns ? 3 : 2)} className="text-end">
                         {showConversionColumns ? <div className="rounded border bg-light px-2 py-1 d-inline-block">
                           <div><span className="text-muted small">{proposalCurrencyCode}</span> {getCurrencyPrefix(proposalCurrencyCode)}{investmentBreakdown.years.reduce((sum, year) => sum + getQuotedInvestmentTotalForYear(year), 0).toLocaleString('id-ID')}</div>
                           <div className="border-top mt-1 pt-1 text-success"><span className="text-muted small">IDR</span> Rp {investmentBreakdown.totals.reduce((sum, t) => sum + t, 0).toLocaleString('id-ID')}</div>
                         </div> : <span className="fw-semibold">Rp {investmentBreakdown.totals.reduce((sum, t) => sum + t, 0).toLocaleString('id-ID')}</span>}
                       </th>
                     </tr>
                  </tfoot>
                </table>
              </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">No investment breakdown available. Add services and items to see the calculation.</p>
                </div>
              )}
            </div>
          </div>
        </div>
        {!readOnly && (
          <div className="d-flex justify-content-end mt-3 mb-3 me-3">
            <button className="btn btn-outline-primary me-2">Cancel</button>
            {/* Create PDF button moved to DetailProposal component */}
            <button className="btn btn-primary" onClick={handleSave}>Save</button>
          </div>
        )}
      </div>
    </>
  );
};

export default EditProposal;
